import {
	type CSSProperties,
	type ReactNode,
	type TouchEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { TEAM_REVEAL_PLAYERS } from "./snacksTeamRevealPlayers.ts";

type Player = {
	id: string;
	name: string;
	pos: string;
	price: number;
	team: string;
	mpg: number;
	per: number;
	vorp: number;
	pts: number;
	trb: number;
	ast: number;
	off: number;
	def: number;
	space: number;
	create: number;
	usage: number;
	reb: number;
};

type PlayerAdvancedStats = {
	dbpm: number;
	drtg: number;
	dws: number;
	obpm: number;
	ows: number;
	pm100: number;
};

type PlayerDefenseShape = {
	blkp: number;
	min: number;
	stlp: number;
};

const CAP = 30;
const ROSTER_SIZE = 5;
const DEFAULT_LEAGUE_YEAR = 2025;
const PLAYER_HANDLE_STORAGE_KEY = "cap-lineup-player-handle";
const LEAGUE_YEARS = Array.from(
	{ length: 11 },
	(_, i) => DEFAULT_LEAGUE_YEAR - i,
);

const RAW_PLAYERS: Player[] = [
	{
		id: "jokicni01",
		name: "Nikola Jokic",
		team: "DEN",
		pos: "C",
		price: 10,
		mpg: 36.7,
		per: 32,
		vorp: 9.8,
		pts: 29.6,
		trb: 12.7,
		ast: 10.2,
		off: 99,
		def: 99,
		space: 81,
		create: 99,
		usage: 86,
		reb: 99,
	},
	{
		id: "gilgesh01",
		name: "Shai Gilgeous-Alexander",
		team: "OKC",
		pos: "G",
		price: 10,
		mpg: 34.2,
		per: 30.7,
		vorp: 8.9,
		pts: 32.7,
		trb: 5,
		ast: 6.4,
		off: 99,
		def: 99,
		space: 83,
		create: 99,
		usage: 99,
		reb: 70,
	},
	{
		id: "antetgi01",
		name: "Giannis Antetokounmpo",
		team: "MIL",
		pos: "GF",
		price: 10,
		mpg: 34.2,
		per: 30.5,
		vorp: 6.6,
		pts: 30.4,
		trb: 11.9,
		ast: 6.5,
		off: 99,
		def: 99,
		space: 35,
		create: 99,
		usage: 99,
		reb: 99,
	},
	{
		id: "jamesle01",
		name: "LeBron James",
		team: "LAL",
		pos: "GF",
		price: 10,
		mpg: 34.9,
		per: 22.7,
		vorp: 4.7,
		pts: 24.4,
		trb: 7.8,
		ast: 8.2,
		off: 88,
		def: 85,
		space: 82,
		create: 99,
		usage: 87,
		reb: 84,
	},
	{
		id: "halibty01",
		name: "Tyrese Haliburton",
		team: "IND",
		pos: "G",
		price: 10,
		mpg: 33.6,
		per: 21.8,
		vorp: 4.9,
		pts: 18.6,
		trb: 3.5,
		ast: 9.2,
		off: 90,
		def: 81,
		space: 96,
		create: 99,
		usage: 63,
		reb: 63,
	},
	{
		id: "tatumja01",
		name: "Jayson Tatum",
		team: "BOS",
		pos: "F",
		price: 8,
		mpg: 36.4,
		per: 21.7,
		vorp: 4.8,
		pts: 26.8,
		trb: 8.7,
		ast: 6,
		off: 85,
		def: 92,
		space: 99,
		create: 99,
		usage: 90,
		reb: 87,
	},
	{
		id: "curryst01",
		name: "Stephen Curry",
		team: "GS",
		pos: "PG",
		price: 8,
		mpg: 32.2,
		per: 21.5,
		vorp: 4.8,
		pts: 24.5,
		trb: 4.4,
		ast: 6,
		off: 91,
		def: 80,
		space: 99,
		create: 99,
		usage: 86,
		reb: 68,
	},
	{
		id: "sabondo01",
		name: "Domantas Sabonis",
		team: "SAC",
		pos: "FC",
		price: 8,
		mpg: 34.7,
		per: 22.9,
		vorp: 4.4,
		pts: 19.1,
		trb: 13.9,
		ast: 6,
		off: 85,
		def: 87,
		space: 66,
		create: 90,
		usage: 63,
		reb: 99,
	},
	{
		id: "doncilu01",
		name: "Luka Doncic",
		team: "DAL/LAL",
		pos: "G",
		price: 8,
		mpg: 35.4,
		per: 24,
		vorp: 3.9,
		pts: 28.2,
		trb: 8.2,
		ast: 7.7,
		off: 90,
		def: 90,
		space: 99,
		create: 99,
		usage: 98,
		reb: 85,
	},
	{
		id: "davisan02",
		name: "Anthony Davis",
		team: "LAL/DAL",
		pos: "FC",
		price: 8,
		mpg: 33.5,
		per: 26.3,
		vorp: 3.2,
		pts: 24.7,
		trb: 11.6,
		ast: 3.5,
		off: 87,
		def: 99,
		space: 52,
		create: 86,
		usage: 88,
		reb: 99,
	},
	{
		id: "edwaran01",
		name: "Anthony Edwards",
		team: "MIN",
		pos: "SG",
		price: 6,
		mpg: 36.3,
		per: 20.1,
		vorp: 4.6,
		pts: 27.6,
		trb: 5.7,
		ast: 4.5,
		off: 83,
		def: 84,
		space: 99,
		create: 93,
		usage: 91,
		reb: 73,
	},
	{
		id: "hardeja01",
		name: "James Harden",
		team: "LAC",
		pos: "G",
		price: 6,
		mpg: 35.3,
		per: 20,
		vorp: 4.4,
		pts: 22.8,
		trb: 5.8,
		ast: 8.7,
		off: 79,
		def: 94,
		space: 97,
		create: 99,
		usage: 86,
		reb: 74,
	},
	{
		id: "townska01",
		name: "Karl-Anthony Towns",
		team: "NYC",
		pos: "FC",
		price: 6,
		mpg: 35,
		per: 23.4,
		vorp: 3.5,
		pts: 24.4,
		trb: 12.8,
		ast: 3.1,
		off: 82,
		def: 85,
		space: 81,
		create: 79,
		usage: 79,
		reb: 99,
	},
	{
		id: "wembavi01",
		name: "Victor Wembanyama",
		team: "SA",
		pos: "FC",
		price: 6,
		mpg: 33.2,
		per: 24.2,
		vorp: 3.3,
		pts: 24.3,
		trb: 11,
		ast: 3.7,
		off: 83,
		def: 99,
		space: 99,
		create: 86,
		usage: 88,
		reb: 99,
	},
	{
		id: "sengual01",
		name: "Alperen Sengun",
		team: "HOU",
		pos: "C",
		price: 6,
		mpg: 31.5,
		per: 21.4,
		vorp: 3.9,
		pts: 19.1,
		trb: 10.3,
		ast: 4.9,
		off: 77,
		def: 99,
		space: 38,
		create: 85,
		usage: 76,
		reb: 97,
	},
	{
		id: "brunsja01",
		name: "Jalen Brunson",
		team: "NYC",
		pos: "G",
		price: 8,
		mpg: 35.4,
		per: 21.6,
		vorp: 3.1,
		pts: 26,
		trb: 2.9,
		ast: 7.3,
		off: 82,
		def: 62,
		space: 86,
		create: 99,
		usage: 86,
		reb: 60,
	},
	{
		id: "willija06",
		name: "Jalen Williams",
		team: "OKC",
		pos: "GF",
		price: 4,
		mpg: 32.4,
		per: 20.3,
		vorp: 3.4,
		pts: 21.6,
		trb: 5.3,
		ast: 5.1,
		off: 75,
		def: 99,
		space: 77,
		create: 88,
		usage: 80,
		reb: 72,
	},
	{
		id: "duranke01",
		name: "Kevin Durant",
		team: "PHO",
		pos: "GF",
		price: 4,
		mpg: 36.5,
		per: 21.2,
		vorp: 3,
		pts: 26.6,
		trb: 6,
		ast: 4.2,
		off: 79,
		def: 70,
		space: 91,
		create: 87,
		usage: 83,
		reb: 75,
	},
	{
		id: "whitede01",
		name: "Derrick White",
		team: "BOS",
		pos: "G",
		price: 4,
		mpg: 33.9,
		per: 16.6,
		vorp: 3.6,
		pts: 16.4,
		trb: 4.5,
		ast: 4.8,
		off: 70,
		def: 91,
		space: 99,
		create: 78,
		usage: 57,
		reb: 68,
	},
	{
		id: "hartjo01",
		name: "Josh Hart",
		team: "NYC",
		pos: "GF",
		price: 4,
		mpg: 37.6,
		per: 16.5,
		vorp: 3.6,
		pts: 13.6,
		trb: 9.6,
		ast: 5.9,
		off: 65,
		def: 98,
		space: 63,
		create: 75,
		usage: 44,
		reb: 92,
	},
	{
		id: "holidjr01",
		name: "Jrue Holiday",
		team: "BOS",
		pos: "G",
		price: 2,
		mpg: 30.6,
		per: 13.5,
		vorp: 1.3,
		pts: 11.1,
		trb: 4.3,
		ast: 3.9,
		off: 52,
		def: 86,
		space: 75,
		create: 62,
		usage: 46,
		reb: 68,
	},
	{
		id: "bridgmi01",
		name: "Mikal Bridges",
		team: "NYC",
		pos: "GF",
		price: 2,
		mpg: 37,
		per: 14,
		vorp: 1.2,
		pts: 17.6,
		trb: 3.2,
		ast: 3.7,
		off: 54,
		def: 69,
		space: 80,
		create: 67,
		usage: 57,
		reb: 61,
	},
	{
		id: "dortlu01",
		name: "Luguentz Dort",
		team: "OKC",
		pos: "GF",
		price: 2,
		mpg: 29.2,
		per: 11.3,
		vorp: 1.2,
		pts: 10.1,
		trb: 4.1,
		ast: 1.6,
		off: 48,
		def: 89,
		space: 87,
		create: 47,
		usage: 39,
		reb: 67,
	},
	{
		id: "murrake02",
		name: "Keegan Murray",
		team: "SAC",
		pos: "F",
		price: 2,
		mpg: 34.3,
		per: 12,
		vorp: 0.2,
		pts: 12.4,
		trb: 6.7,
		ast: 1.4,
		off: 46,
		def: 72,
		space: 80,
		create: 47,
		usage: 44,
		reb: 79,
	},
	{
		id: "caldwke01",
		name: "Kentavious Caldwell-Pope",
		team: "ORL",
		pos: "G",
		price: 2,
		mpg: 29.6,
		per: 9.9,
		vorp: 0.3,
		pts: 8.7,
		trb: 2.2,
		ast: 1.8,
		off: 45,
		def: 95,
		space: 70,
		create: 41,
		usage: 36,
		reb: 58,
	},
	{
		id: "embiijo01",
		name: "Joel Embiid",
		team: "PHI",
		pos: "C",
		price: 10,
		mpg: 30.2,
		per: 23.4,
		vorp: 0.8,
		pts: 23.8,
		trb: 8.2,
		ast: 4.5,
		off: 84,
		def: 80,
		space: 66,
		create: 87,
		usage: 98,
		reb: 88,
	},
	{
		id: "lillada01",
		name: "Damian Lillard",
		team: "MIL",
		pos: "PG",
		price: 8,
		mpg: 36.1,
		per: 21.3,
		vorp: 3.2,
		pts: 24.9,
		trb: 4.7,
		ast: 7.1,
		off: 86,
		def: 62,
		space: 95,
		create: 96,
		usage: 84,
		reb: 69,
	},
	{
		id: "mitchdo01",
		name: "Donovan Mitchell",
		team: "CLE",
		pos: "G",
		price: 8,
		mpg: 31.4,
		per: 20.9,
		vorp: 3.2,
		pts: 24,
		trb: 4.5,
		ast: 5,
		off: 82,
		def: 76,
		space: 90,
		create: 88,
		usage: 90,
		reb: 68,
	},
	{
		id: "irvinky01",
		name: "Kyrie Irving",
		team: "DAL",
		pos: "G",
		price: 8,
		mpg: 36.1,
		per: 19.9,
		vorp: 2.5,
		pts: 24.7,
		trb: 4.8,
		ast: 4.6,
		off: 82,
		def: 65,
		space: 96,
		create: 90,
		usage: 83,
		reb: 69,
	},
	{
		id: "bookede01",
		name: "Devin Booker",
		team: "PHO",
		pos: "G",
		price: 8,
		mpg: 37.3,
		per: 19.3,
		vorp: 1.7,
		pts: 25.6,
		trb: 4.1,
		ast: 7.1,
		off: 80,
		def: 54,
		space: 78,
		create: 94,
		usage: 87,
		reb: 66,
	},
	{
		id: "adebaba01",
		name: "Bam Adebayo",
		team: "MIA",
		pos: "FC",
		price: 8,
		mpg: 34.3,
		per: 18.9,
		vorp: 2.7,
		pts: 18.1,
		trb: 9.6,
		ast: 4.3,
		off: 74,
		def: 88,
		space: 77,
		create: 76,
		usage: 70,
		reb: 91,
	},
	{
		id: "banchpa01",
		name: "Paolo Banchero",
		team: "ORL",
		pos: "PF",
		price: 8,
		mpg: 34.4,
		per: 20,
		vorp: 2.2,
		pts: 25.9,
		trb: 7.5,
		ast: 4.8,
		off: 80,
		def: 73,
		space: 76,
		create: 89,
		usage: 96,
		reb: 83,
	},
	{
		id: "holmgch01",
		name: "Chet Holmgren",
		team: "OKC",
		pos: "FC",
		price: 8,
		mpg: 27.4,
		per: 19.2,
		vorp: 1,
		pts: 15,
		trb: 8,
		ast: 2,
		off: 72,
		def: 94,
		space: 90,
		create: 60,
		usage: 64,
		reb: 86,
	},
	{
		id: "leonaka01",
		name: "Kawhi Leonard",
		team: "LAC",
		pos: "SF",
		price: 8,
		mpg: 31.9,
		per: 20.5,
		vorp: 1.5,
		pts: 21.5,
		trb: 5.9,
		ast: 3.1,
		off: 79,
		def: 82,
		space: 93,
		create: 76,
		usage: 82,
		reb: 76,
	},
	{
		id: "cunnica01",
		name: "Cade Cunningham",
		team: "DET",
		pos: "G",
		price: 6,
		mpg: 35,
		per: 20.6,
		vorp: 3.7,
		pts: 26.1,
		trb: 6.1,
		ast: 9.1,
		off: 81,
		def: 73,
		space: 84,
		create: 99,
		usage: 96,
		reb: 77,
	},
	{
		id: "garlada01",
		name: "Darius Garland",
		team: "CLE",
		pos: "G",
		price: 6,
		mpg: 30.7,
		per: 19.9,
		vorp: 3,
		pts: 20.6,
		trb: 2.9,
		ast: 6.7,
		off: 82,
		def: 61,
		space: 96,
		create: 91,
		usage: 81,
		reb: 60,
	},
	{
		id: "herroty01",
		name: "Tyler Herro",
		team: "MIA",
		pos: "SG",
		price: 6,
		mpg: 35.4,
		per: 19.7,
		vorp: 3.5,
		pts: 23.9,
		trb: 5.2,
		ast: 5.5,
		off: 82,
		def: 64,
		space: 94,
		create: 87,
		usage: 83,
		reb: 72,
	},
	{
		id: "mobleev01",
		name: "Evan Mobley",
		team: "CLE",
		pos: "PF",
		price: 6,
		mpg: 30.5,
		per: 22.3,
		vorp: 3.6,
		pts: 18.5,
		trb: 9.3,
		ast: 3.2,
		off: 80,
		def: 94,
		space: 88,
		create: 70,
		usage: 69,
		reb: 91,
	},
	{
		id: "moranja01",
		name: "Ja Morant",
		team: "MEM",
		pos: "PG",
		price: 4,
		mpg: 30.4,
		per: 19.3,
		vorp: 1.7,
		pts: 23.2,
		trb: 4.1,
		ast: 7.3,
		off: 77,
		def: 62,
		space: 64,
		create: 96,
		usage: 93,
		reb: 66,
	},
	{
		id: "wagnefr01",
		name: "Franz Wagner",
		team: "ORL",
		pos: "SF",
		price: 6,
		mpg: 33.7,
		per: 20.1,
		vorp: 2.6,
		pts: 24.1,
		trb: 5.7,
		ast: 4.7,
		off: 78,
		def: 77,
		space: 68,
		create: 83,
		usage: 91,
		reb: 74,
	},
	{
		id: "reaveau01",
		name: "Austin Reaves",
		team: "LAL",
		pos: "SG",
		price: 4,
		mpg: 34.9,
		per: 18.1,
		vorp: 2.6,
		pts: 20.2,
		trb: 4.5,
		ast: 5.8,
		off: 75,
		def: 65,
		space: 91,
		create: 86,
		usage: 70,
		reb: 69,
	},
	{
		id: "anunoog01",
		name: "OG Anunoby",
		team: "NYC",
		pos: "F",
		price: 4,
		mpg: 36.6,
		per: 15.4,
		vorp: 1.9,
		pts: 18,
		trb: 4.8,
		ast: 2.2,
		off: 63,
		def: 82,
		space: 91,
		create: 58,
		usage: 55,
		reb: 70,
	},
	{
		id: "powelno01",
		name: "Norman Powell",
		team: "LAC",
		pos: "GF",
		price: 4,
		mpg: 32.6,
		per: 17.5,
		vorp: 1.8,
		pts: 21.8,
		trb: 3.2,
		ast: 2.1,
		off: 70,
		def: 65,
		space: 99,
		create: 63,
		usage: 77,
		reb: 61,
	},
	{
		id: "banede01",
		name: "Desmond Bane",
		team: "MEM",
		pos: "SG",
		price: 4,
		mpg: 32,
		per: 17.9,
		vorp: 2.9,
		pts: 19.2,
		trb: 6.1,
		ast: 5.3,
		off: 73,
		def: 76,
		space: 94,
		create: 80,
		usage: 69,
		reb: 76,
	},
	{
		id: "allenja01",
		name: "Jarrett Allen",
		team: "CLE",
		pos: "C",
		price: 4,
		mpg: 28,
		per: 22.1,
		vorp: 3.6,
		pts: 13.5,
		trb: 9.7,
		ast: 1.9,
		off: 74,
		def: 90,
		space: 25,
		create: 52,
		usage: 45,
		reb: 93,
	},
	{
		id: "zubaciv01",
		name: "Ivica Zubac",
		team: "LAC",
		pos: "C",
		price: 4,
		mpg: 32.8,
		per: 22.3,
		vorp: 3.4,
		pts: 16.8,
		trb: 12.6,
		ast: 2.7,
		off: 76,
		def: 86,
		space: 25,
		create: 60,
		usage: 56,
		reb: 99,
	},
	{
		id: "thompam01",
		name: "Amen Thompson",
		team: "HOU",
		pos: "GF",
		price: 4,
		mpg: 32.2,
		per: 18.7,
		vorp: 3.4,
		pts: 14.1,
		trb: 8.2,
		ast: 3.8,
		off: 67,
		def: 99,
		space: 52,
		create: 72,
		usage: 50,
		reb: 86,
	},
	{
		id: "greenja05",
		name: "Jalen Green",
		team: "HOU",
		pos: "SG",
		price: 2,
		mpg: 32.9,
		per: 15.1,
		vorp: 1.7,
		pts: 21,
		trb: 4.6,
		ast: 3.4,
		off: 63,
		def: 65,
		space: 84,
		create: 72,
		usage: 80,
		reb: 69,
	},
	{
		id: "murraja01",
		name: "Jamal Murray",
		team: "DEN",
		pos: "G",
		price: 2,
		mpg: 36.1,
		per: 17.8,
		vorp: 1.7,
		pts: 21.4,
		trb: 3.9,
		ast: 6,
		off: 68,
		def: 58,
		space: 94,
		create: 85,
		usage: 70,
		reb: 65,
	},
];

const PLAYER_ADVANCED_STATS = {
	jokicni01: {
		ows: 12.7,
		dws: 3.8,
		obpm: 9.9,
		dbpm: 3.3,
		pm100: 10.5,
		drtg: 111,
	},
	gilgesh01: {
		ows: 11.9,
		dws: 4.8,
		obpm: 8.9,
		dbpm: 2.6,
		pm100: 16.9,
		drtg: 107,
	},
	antetgi01: {
		ows: 7.8,
		dws: 3.7,
		obpm: 6.9,
		dbpm: 2.5,
		pm100: 6.9,
		drtg: 109,
	},
	jamesle01: {
		ows: 4.9,
		dws: 2.8,
		obpm: 4.9,
		dbpm: 0.7,
		pm100: -0.3,
		drtg: 114,
	},
	halibty01: {
		ows: 8.1,
		dws: 2.3,
		obpm: 5.7,
		dbpm: 0.2,
		pm100: 3.3,
		drtg: 115,
	},
	tatumja01: {
		ows: 5.3,
		dws: 4.2,
		obpm: 4.4,
		dbpm: 0.8,
		pm100: 9.2,
		drtg: 110,
	},
	curryst01: { ows: 5.2, dws: 2.7, obpm: 6.4, dbpm: 0, pm100: 5.3, drtg: 113 },
	sabondo01: {
		ows: 7.4,
		dws: 2.7,
		obpm: 4.2,
		dbpm: 1.1,
		pm100: 2.7,
		drtg: 114,
	},
	doncilu01: {
		ows: 3.5,
		dws: 2.5,
		obpm: 5.5,
		dbpm: 1.2,
		pm100: 8.3,
		drtg: 112,
	},
	davisan02: { ows: 3.7, dws: 3, obpm: 4, dbpm: 1.5, pm100: 1.2, drtg: 108.3 },
	edwaran01: { ows: 4.6, dws: 3.8, obpm: 4.4, dbpm: 0, pm100: 4.7, drtg: 112 },
	hardeja01: { ows: 4, dws: 4.3, obpm: 3.5, dbpm: 0.8, pm100: 5.8, drtg: 110 },
	townska01: {
		ows: 6.3,
		dws: 3.8,
		obpm: 3.5,
		dbpm: 0.1,
		pm100: 6.2,
		drtg: 110,
	},
	wembavi01: { ows: 1, dws: 2.8, obpm: 3.6, dbpm: 2.9, pm100: 2.9, drtg: 108 },
	sengual01: { ows: 4.1, dws: 4.2, obpm: 2.6, dbpm: 1.8, pm100: 7, drtg: 108 },
	brunsja01: {
		ows: 6.8,
		dws: 1.4,
		obpm: 4.7,
		dbpm: -1.3,
		pm100: 2.1,
		drtg: 118,
	},
	willija06: {
		ows: 3.7,
		dws: 4.1,
		obpm: 2.7,
		dbpm: 1.4,
		pm100: 9.7,
		drtg: 107,
	},
	duranke01: { ows: 4, dws: 1.2, obpm: 3.9, dbpm: -0.7, pm100: -1, drtg: 119 },
	whitede01: {
		ows: 5.1,
		dws: 3.4,
		obpm: 2.7,
		dbpm: 0.8,
		pm100: 9.7,
		drtg: 112,
	},
	hartjo01: { ows: 5.4, dws: 3.8, obpm: 1.1, dbpm: 1.8, pm100: 3.6, drtg: 112 },
	holidjr01: { ows: 2.8, dws: 2.4, obpm: -0.3, dbpm: 1, pm100: 8.7, drtg: 113 },
	bridgmi01: { ows: 3.7, dws: 2, obpm: 0.4, dbpm: -0.9, pm100: 5.6, drtg: 118 },
	dortlu01: {
		ows: 2.4,
		dws: 3.2,
		obpm: -0.6,
		dbpm: 0.8,
		pm100: 13.5,
		drtg: 110,
	},
	murrake02: {
		ows: 2.4,
		dws: 2.1,
		obpm: -0.8,
		dbpm: -0.8,
		pm100: 1.2,
		drtg: 117,
	},
	caldwke01: { ows: 1.5, dws: 3.1, obpm: -3, dbpm: 1.6, pm100: 0.4, drtg: 112 },
	embiijo01: {
		ows: 0.9,
		dws: 0.5,
		obpm: 3.7,
		dbpm: -0.3,
		pm100: -5.4,
		drtg: 116,
	},
	lillada01: { ows: 5.6, dws: 2, obpm: 4.7, dbpm: -0.6, pm100: 0.4, drtg: 115 },
	mitchdo01: {
		ows: 4.8,
		dws: 2.8,
		obpm: 3.8,
		dbpm: -0.2,
		pm100: 11.5,
		drtg: 113,
	},
	irvinky01: {
		ows: 3.4,
		dws: 1.3,
		obpm: 3.9,
		dbpm: -0.5,
		pm100: 2.2,
		drtg: 118,
	},
	bookede01: {
		ows: 6.1,
		dws: 0.3,
		obpm: 2.8,
		dbpm: -2.4,
		pm100: -3,
		drtg: 123,
	},
	adebaba01: { ows: 3.5, dws: 4, obpm: 1, dbpm: 1, pm100: 0.2, drtg: 110 },
	banchpa01: { ows: 1.1, dws: 2.4, obpm: 3.4, dbpm: 0.2, pm100: -1, drtg: 110 },
	holmgch01: { ows: 1.1, dws: 2, obpm: 0.5, dbpm: 1.9, pm100: 16.3, drtg: 103 },
	leonaka01: {
		ows: 1.5,
		dws: 1.9,
		obpm: 2.5,
		dbpm: 0.6,
		pm100: 12.7,
		drtg: 109,
	},
	cunnica01: { ows: 3, dws: 2.9, obpm: 3.8, dbpm: 0.1, pm100: 3.4, drtg: 113 },
	garlada01: {
		ows: 5.8,
		dws: 2.3,
		obpm: 3.9,
		dbpm: -0.8,
		pm100: 10.3,
		drtg: 115,
	},
	herroty01: {
		ows: 4.7,
		dws: 2.7,
		obpm: 3.8,
		dbpm: -0.7,
		pm100: 1.3,
		drtg: 115,
	},
	mobleev01: {
		ows: 5.3,
		dws: 3.8,
		obpm: 3.1,
		dbpm: 1.5,
		pm100: 12.2,
		drtg: 108,
	},
	moranja01: { ows: 2, dws: 1.5, obpm: 3.1, dbpm: -0.7, pm100: 6.2, drtg: 115 },
	wagnefr01: {
		ows: 2.6,
		dws: 3.1,
		obpm: 2.6,
		dbpm: 0.5,
		pm100: 5.8,
		drtg: 110,
	},
	reaveau01: {
		ows: 5.6,
		dws: 2.1,
		obpm: 2.4,
		dbpm: -0.5,
		pm100: 4.4,
		drtg: 117,
	},
	anunoog01: {
		ows: 3.7,
		dws: 2.8,
		obpm: 0.7,
		dbpm: 0.1,
		pm100: 5.2,
		drtg: 114,
	},
	powelno01: {
		ows: 2.9,
		dws: 2.3,
		obpm: 1.7,
		dbpm: -0.2,
		pm100: 8.3,
		drtg: 113,
	},
	banede01: { ows: 4, dws: 2.6, obpm: 2.6, dbpm: 0.6, pm100: 2.4, drtg: 113 },
	allenja01: { ows: 7.6, dws: 4, obpm: 3, dbpm: 1.2, pm100: 11.2, drtg: 108 },
	zubaciv01: { ows: 7, dws: 4.7, obpm: 2.3, dbpm: 0.8, pm100: 9.3, drtg: 108 },
	thompam01: { ows: 4.1, dws: 3.9, obpm: 1.4, dbpm: 2.6, pm100: 6, drtg: 108 },
	greenja05: { ows: 1.9, dws: 3.2, obpm: 1.1, dbpm: -0.5, pm100: 2, drtg: 113 },
	murraja01: { ows: 4.6, dws: 1.6, obpm: 1.8, dbpm: -1, pm100: 6.5, drtg: 118 },
} satisfies Record<string, PlayerAdvancedStats>;

const PLAYER_DEFENSE_SHAPE = {
	jokicni01: { min: 2571, stlp: 2.4, blkp: 1.6 },
	gilgesh01: { min: 2598, stlp: 2.4, blkp: 3 },
	antetgi01: { min: 2289, stlp: 1.2, blkp: 3.2 },
	jamesle01: { min: 2444, stlp: 1.4, blkp: 1.5 },
	halibty01: { min: 2451, stlp: 2.1, blkp: 1.8 },
	tatumja01: { min: 2624, stlp: 1.5, blkp: 1.3 },
	curryst01: { min: 2252, stlp: 1.7, blkp: 1.3 },
	sabondo01: { min: 2429, stlp: 1, blkp: 1 },
	doncilu01: { min: 1769, stlp: 2.4, blkp: 1.1 },
	davisan02: { min: 1706, stlp: 1.7, blkp: 6 },
	edwaran01: { min: 2871, stlp: 1.6, blkp: 1.7 },
	hardeja01: { min: 2789, stlp: 2.1, blkp: 2 },
	townska01: { min: 2517, stlp: 1.4, blkp: 1.9 },
	wembavi01: { min: 1527, stlp: 1.6, blkp: 10.4 },
	sengual01: { min: 2395, stlp: 1.7, blkp: 2.3 },
	brunsja01: { min: 2301, stlp: 1.3, blkp: 0.3 },
	willija06: { min: 2237, stlp: 2.4, blkp: 2.1 },
	duranke01: { min: 2265, stlp: 1.1, blkp: 3.2 },
	whitede01: { min: 2574, stlp: 1.4, blkp: 2.9 },
	hartjo01: { min: 2897, stlp: 2, blkp: 0.9 },
	holidjr01: { min: 1896, stlp: 1.7, blkp: 1.3 },
	bridgmi01: { min: 3036, stlp: 1.2, blkp: 1.3 },
	dortlu01: { min: 2073, stlp: 1.8, blkp: 1.9 },
	murrake02: { min: 2610, stlp: 1.1, blkp: 2.6 },
	caldwke01: { min: 2279, stlp: 2.2, blkp: 1.4 },
	embiijo01: { min: 574, stlp: 1.2, blkp: 3.1 },
	lillada01: { min: 2093, stlp: 1.6, blkp: 0.4 },
	mitchdo01: { min: 2232, stlp: 2, blkp: 0.7 },
	irvinky01: { min: 1804, stlp: 1.8, blkp: 1.1 },
	bookede01: { min: 2795, stlp: 1.2, blkp: 0.5 },
	adebaba01: { min: 2674, stlp: 1.8, blkp: 1.9 },
	banchpa01: { min: 1582, stlp: 1.1, blkp: 1.7 },
	holmgch01: { min: 877, stlp: 1.3, blkp: 8.2 },
	leonaka01: { min: 1180, stlp: 2.5, blkp: 1.4 },
	cunnica01: { min: 2452, stlp: 1.4, blkp: 2.1 },
	garlada01: { min: 2301, stlp: 1.9, blkp: 0.4 },
	herroty01: { min: 2725, stlp: 1.3, blkp: 0.6 },
	mobleev01: { min: 2167, stlp: 1.4, blkp: 4.7 },
	moranja01: { min: 1519, stlp: 1.9, blkp: 0.7 },
	wagnefr01: { min: 2023, stlp: 1.9, blkp: 1 },
	reaveau01: { min: 2550, stlp: 1.6, blkp: 0.8 },
	anunoog01: { min: 2706, stlp: 2, blkp: 2.2 },
	powelno01: { min: 1958, stlp: 1.8, blkp: 0.5 },
	banede01: { min: 2205, stlp: 1.7, blkp: 1.2 },
	allenja01: { min: 2296, stlp: 1.6, blkp: 2.9 },
	zubaciv01: { min: 2624, stlp: 1, blkp: 3.4 },
	thompam01: { min: 2225, stlp: 2.1, blkp: 3.6 },
	greenja05: { min: 2697, stlp: 1.3, blkp: 0.9 },
	murraja01: { min: 2418, stlp: 1.8, blkp: 1.2 },
} satisfies Record<string, PlayerDefenseShape>;

const ratingFromAdvanced = (
	player: Player,
	advanced: PlayerAdvancedStats,
	defenseShape: PlayerDefenseShape,
) => {
	const dwsRate = (advanced.dws * 2000) / Math.max(defenseShape.min, 1);
	const defensiveReliability = Math.max(
		0.35,
		Math.min(1, (defenseShape.min - 700) / 900),
	);
	const rimMultiplier = player.pos.includes("C")
		? 2.1
		: player.pos.includes("F")
			? 1.6
			: 1;
	const rimProtection =
		Math.max(0, defenseShape.blkp - 1.2) * rimMultiplier * defensiveReliability;
	const disruption =
		Math.max(0, defenseShape.stlp - 1.1) * 1.7 * defensiveReliability;
	const teamContext =
		(112 - advanced.drtg) * 0.5 +
		Math.max(-5, Math.min(10, advanced.pm100)) * 0.04;

	return {
		def: Math.round(
			Math.max(
				40,
				Math.min(
					96,
					60 +
						advanced.dbpm * 3 +
						dwsRate * 2.2 * defensiveReliability +
						teamContext +
						rimProtection +
						disruption,
				),
			),
		),
		off: Math.round(
			Math.max(
				40,
				Math.min(
					99,
					65 + advanced.obpm * 4.5 + advanced.ows * 1.5 + advanced.pm100 * 0.25,
				),
			),
		),
	};
};

const PLAYERS: Player[] = RAW_PLAYERS.map((player) => ({
	...player,
	...ratingFromAdvanced(
		player,
		PLAYER_ADVANCED_STATS[player.id]!,
		PLAYER_DEFENSE_SHAPE[player.id]!,
	),
}));

const PRICE_TIERS = [10, 8, 6, 4, 2];

const stableShuffle = <T,>(items: T[], seed: number) =>
	[...items]
		.map((item, i) => ({
			item,
			sort: hashString(`${seed}-${i}-${JSON.stringify(item)}`),
		}))
		.sort((a, b) => a.sort - b.sort)
		.map(({ item }) => item);

const playersByTierForSeed = (seed: number) =>
	PRICE_TIERS.map((price) => ({
		players: stableShuffle(
			PLAYERS.filter((p) => p.price === price),
			seed + price * 997,
		).slice(0, 5),
		price,
	}));

type RevealGroup = {
	id: string;
	label: string;
	players: Player[];
	revealLabel: string;
	slotLabel: string;
};

const REVEAL_SLOT_LABELS = ["PG", "SG", "SF", "PF", "C"];

const tierRevealGroupsForSeed = (seed: number): RevealGroup[] =>
	playersByTierForSeed(seed).map((tier, i) => ({
		id: `tier-${tier.price}`,
		label: `$${tier.price} tier`,
		players: tier.players,
		revealLabel: `Reveal $${tier.price} tier`,
		slotLabel: REVEAL_SLOT_LABELS[i]!,
	}));

const TEAM_REVEAL_TEAMS = [
	...new Set(TEAM_REVEAL_PLAYERS.map((player) => player.team)),
].sort();

const teamRevealQualityScore = (player: Player) =>
	player.price * 100 +
	player.vorp * 16 +
	player.per * 2 +
	player.mpg +
	player.pts * 0.8 +
	player.trb * 0.35 +
	player.ast * 0.35 +
	player.off * 0.15 +
	player.def * 0.15;

const TEAM_REVEAL_FREE_PLAYER_IDS = new Set(
	TEAM_REVEAL_TEAMS.flatMap((team) =>
		TEAM_REVEAL_PLAYERS.filter((player) => player.team === team)
			.sort(
				(a, b) =>
					teamRevealQualityScore(a) - teamRevealQualityScore(b) ||
					a.name.localeCompare(b.name),
			)
			.slice(0, 2)
			.map((player) => player.id),
	),
);

type TeamRevealRatingKey = "create" | "def" | "off" | "reb" | "space" | "usage";

const TEAM_REVEAL_RATING_KEYS: TeamRevealRatingKey[] = [
	"create",
	"usage",
	"off",
	"space",
	"def",
	"reb",
];

const TEAM_REVEAL_RATING_DISPLAY: {
	key: TeamRevealRatingKey;
	label: string;
	short: string;
}[] = [
	{ key: "create", label: "Playmaking", short: "PLY" },
	{ key: "usage", label: "Dunk/Layup", short: "D/L" },
	{ key: "off", label: "Shot Creation", short: "CRE" },
	{ key: "space", label: "3 Point Shooting", short: "3PT" },
	{ key: "def", label: "Perimeter D", short: "PER" },
	{ key: "reb", label: "Paint D", short: "PNT" },
];

const TEAM_REVEAL_CARD_SKILLS: {
	key: TeamRevealRatingKey;
	label: string;
}[] = [
	{ key: "create", label: "Pass" },
	{ key: "off", label: "2PT" },
	{ key: "usage", label: "Dunk/Layup" },
	{ key: "space", label: "3PT" },
	{ key: "def", label: "Perimeter D" },
	{ key: "reb", label: "Blk" },
];

type TeamRevealTier = "Big" | "Guard" | "Wing";

const TEAM_REVEAL_TIER_COLORS: Record<TeamRevealTier, string> = {
	Big: "#e6ac74",
	Guard: "#78b4ff",
	Wing: "#c1a0f2",
};

const teamRevealTierFor = (player: Player): TeamRevealTier => {
	if (player.pos === "PG" || player.pos === "SG" || player.pos === "G") {
		return "Guard";
	}
	if (
		player.pos === "C" ||
		player.pos === "PF" ||
		player.pos === "FC" ||
		player.pos === "C-F"
	) {
		return "Big";
	}
	return "Wing";
};

const teamRevealRatingTitle = (player: Player) =>
	TEAM_REVEAL_CARD_SKILLS.map(
		({ label, key }) => `${label} ${player[key]}`,
	).join(" | ");

const teamRevealValueScore = (player: Player) => {
	const ratingAverage =
		TEAM_REVEAL_CARD_SKILLS.reduce((sum, skill) => sum + player[skill.key], 0) /
		TEAM_REVEAL_CARD_SKILLS.length;
	return ratingAverage - (58 + player.price * 3);
};

const teamRevealValueLabel = (score: number) =>
	score >= 5 ? "Steal" : score <= -4 ? "Pricey" : "Fair";

const teamRevealValueColor = (score: number) =>
	score >= 5 ? "#43d69c" : score <= -4 ? "#e0885a" : "#8f9ba7";

const teamRevealOffenseComposite = (player: Player) => {
	const topStrength = Math.max(player.create, player.space, player.usage);
	return topStrength * (player.off / 100);
};

const teamRevealDefenseComposite = (player: Player) => {
	const primary = Math.max(player.def, player.reb);
	const secondary = Math.min(player.def, player.reb);
	return primary * Math.min(1, (secondary * 2) / 100);
};

const teamRevealRatingScore = (player: Player, key: TeamRevealRatingKey) => {
	if (key === "create") {
		return (
			player.create +
			player.ast * 0.72 +
			player.usage * 0.05 +
			player.off * 0.03 +
			player.vorp * 0.22
		);
	}
	if (key === "usage") {
		return (
			player.usage +
			player.pts * 0.08 +
			player.trb * 0.1 +
			(player.pos.includes("C") || player.pos.includes("F") ? 1.4 : 0) +
			(player.space < 60 ? 0.8 : 0)
		);
	}
	if (key === "off") {
		return (
			player.off +
			player.per * 0.12 +
			player.vorp * 0.62 +
			player.pts * 0.1 +
			player.create * 0.04
		);
	}
	if (key === "space") {
		return player.space + player.off * 0.025 + player.pts * 0.025;
	}
	if (key === "def") {
		return (
			player.def +
			(player.pos.includes("G") || player.pos === "SF" ? 1.3 : 0) +
			player.ast * 0.04 +
			player.mpg * 0.015 -
			(player.pos === "C" ? 1.4 : 0)
		);
	}
	return (
		player.reb +
		player.trb * 0.55 +
		(player.pos.includes("C") ? 2.2 : player.pos.includes("F") ? 1 : 0) +
		player.def * 0.04
	);
};

const TEAM_REVEAL_CALIBRATED_RATINGS = (() => {
	const ratings = new Map<string, Partial<Record<TeamRevealRatingKey, number>>>();
	for (const key of TEAM_REVEAL_RATING_KEYS) {
		const ranked = TEAM_REVEAL_PLAYERS.map((player) => ({
			id: player.id,
			score: teamRevealRatingScore(player, key),
		})).sort((a, b) => a.score - b.score || a.id.localeCompare(b.id));
		const maxIndex = Math.max(ranked.length - 1, 1);

		for (const [index, player] of ranked.entries()) {
			const percentile = index / maxIndex;
			const curved = 45 + percentile ** 1.05 * 50;
			const current = ratings.get(player.id) ?? {};
			current[key] = Math.round(curved);
			ratings.set(player.id, current);
		}
	}
	return ratings;
})();

const TEAM_REVEAL_PLAYER_POOL: Player[] = TEAM_REVEAL_PLAYERS.map((player) =>
	TEAM_REVEAL_FREE_PLAYER_IDS.has(player.id)
		? { ...player, ...TEAM_REVEAL_CALIBRATED_RATINGS.get(player.id), price: 0 }
		: { ...player, ...TEAM_REVEAL_CALIBRATED_RATINGS.get(player.id) },
);

const TEAM_REVEAL_PLAYERS_BY_TEAM = new Map(
	TEAM_REVEAL_TEAMS.map((team) => [
		team,
		TEAM_REVEAL_PLAYER_POOL.filter((player) => player.team === team),
	]),
);

const teamRevealGroupsForSeed = (seed: number): RevealGroup[] =>
	stableShuffle(TEAM_REVEAL_TEAMS, seed * 1543)
		.slice(0, ROSTER_SIZE)
		.map((team) => ({
			id: `team-${team}`,
			label: team,
			players: TEAM_REVEAL_PLAYERS_BY_TEAM.get(team) ?? [],
			revealLabel: `Reveal ${team}`,
			slotLabel: team,
		}));

const SHORT_NAMES: Record<string, string> = {
	adebaba01: "Bam",
	allenja01: "Allen",
	anunoog01: "OG",
	antetgi01: "Giannis",
	banchpa01: "Paolo",
	banede01: "Bane",
	bookede01: "Book",
	bridgmi01: "Mikal",
	brunsja01: "Brunson",
	caldwke01: "KCP",
	cunnica01: "Cade",
	curryst01: "Steph",
	davisan02: "AD",
	doncilu01: "Luka",
	dortlu01: "Dort",
	duranke01: "KD",
	edwaran01: "Ant",
	embiijo01: "Embiid",
	foxde01: "Fox",
	garlada01: "Garland",
	gilgesh01: "SGA",
	greenja05: "Jalen Green",
	halibty01: "Haliburton",
	hardeja01: "Harden",
	hartjo01: "Hart",
	herroty01: "Herro",
	holmgch01: "Chet",
	holidjr01: "Jrue",
	irvinky01: "Kyrie",
	jamesle01: "Bron",
	jokicni01: "Jokic",
	leonaka01: "Kawhi",
	lillada01: "Dame",
	mitchdo01: "Spida",
	mobleev01: "Mobley",
	moranja01: "Ja",
	murrake02: "Keegan",
	murraja01: "Jamal",
	powelno01: "Norm",
	reaveau01: "Reaves",
	sabondo01: "Sabonis",
	sengual01: "Sengun",
	tatumja01: "Tatum",
	thompam01: "Amen",
	townska01: "KAT",
	wagnefr01: "Franz",
	wembavi01: "Wemby",
	whitede01: "D. White",
	willija06: "J-Dub",
	willizi01: "Zion",
	zubaciv01: "Zubac",
};

const lastName = (p: Player) => {
	const suffixes = new Set(["Jr.", "Sr.", "II", "III", "IV", "V"]);
	const parts = p.name.split(/\s+/).filter(Boolean);
	return (
		[...parts].reverse().find((part) => !suffixes.has(part)) ??
		parts[parts.length - 1] ??
		p.name
	);
};

const firstName = (p: Player) => p.name.split(/\s+/).filter(Boolean)[0] ?? p.name;

const shortName = (p: Player) => SHORT_NAMES[p.id] ?? lastName(p);

const PHONE_NAMES: Record<string, string> = {
	greenja05: "Jalen",
	halibty01: "Hali",
	jamesle01: "Bron",
	leonaka01: "Kawhi",
	mitchdo01: "Spida",
	sabondo01: "Domas",
	whitede01: "White",
	willija06: "J-Dub",
};

const phoneName = (p: Player) => PHONE_NAMES[p.id] ?? shortName(p);

const playerInitials = (p: Player) =>
	p.name
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

const positionLabelFor = (pos: string) => {
	if (pos === "G") return "Guard";
	if (pos === "F") return "Forward";
	if (pos === "C") return "Center";
	if (pos === "GF") return "Guard-Forward";
	if (pos === "FC" || pos === "C-F") return "Center";
	if (pos === "PG") return "Point Guard";
	if (pos === "SG") return "Shooting Guard";
	if (pos === "SF") return "Small Forward";
	if (pos === "PF") return "Power Forward";
	return pos;
};

type PlayerVitals = {
	height: string;
	weight: string;
};

const PLAYER_VITALS_BY_ID: Record<string, PlayerVitals> = {
	derozde01: { height: "6' 6\"", weight: "220 lb" },
	duranke01: { height: "6' 11\"", weight: "240 lb" },
	jamesle01: { height: "6' 9\"", weight: "250 lb" },
	jokicni01: { height: "6' 11\"", weight: "284 lb" },
	lavinza01: { height: "6' 5\"", weight: "200 lb" },
	monkma01: { height: "6' 3\"", weight: "200 lb" },
	murrake02: { height: "6' 8\"", weight: "215 lb" },
	sabondo01: { height: "6' 11\"", weight: "240 lb" },
	strusma01: { height: "6' 5\"", weight: "215 lb" },
	wembavi01: { height: "7' 4\"", weight: "209 lb" },
	westbru01: { height: "6' 3\"", weight: "200 lb" },
	willizi01: { height: "6' 6\"", weight: "284 lb" },
};

const PLAYER_VITAL_DEFAULTS_BY_POS: Record<string, PlayerVitals> = {
	C: { height: "6' 11\"", weight: "245 lb" },
	"C-F": { height: "6' 10\"", weight: "240 lb" },
	F: { height: "6' 8\"", weight: "225 lb" },
	FC: { height: "6' 10\"", weight: "238 lb" },
	G: { height: "6' 4\"", weight: "200 lb" },
	GF: { height: "6' 6\"", weight: "215 lb" },
	PF: { height: "6' 9\"", weight: "235 lb" },
	PG: { height: "6' 3\"", weight: "190 lb" },
	SF: { height: "6' 7\"", weight: "220 lb" },
	SG: { height: "6' 5\"", weight: "205 lb" },
};

const playerVitalsFor = (player: Player) =>
	PLAYER_VITALS_BY_ID[player.id] ??
	PLAYER_VITAL_DEFAULTS_BY_POS[player.pos] ?? {
		height: "6' 7\"",
		weight: "215 lb",
	};

const playerBadges = (p: Player) =>
	TEAM_REVEAL_CARD_SKILLS.map(({ key, label }) =>
		p[key] >= 80 ? { code: label.toUpperCase(), label } : undefined,
	)
		.filter((badge): badge is { code: string; label: string } => Boolean(badge))
		.slice(0, 6);

const teamRevealCardSummary = (player: Player) => {
	const offense = Math.round(teamRevealOffenseComposite(player));
	const defense = Math.round(teamRevealDefenseComposite(player));
	const overall = Math.round((offense + defense) / 2);
	return { defense, offense, overall };
};

const teamRevealBoardTextFor = (seed: number) =>
	teamRevealGroupsForSeed(seed)
		.map(
			(group) =>
				`${group.label}: ${group.players.map((p) => shortName(p)).join(", ")}`,
		)
		.join("\n");

const challengeBoardTextFor = (seed: number) =>
	fantasyBasePath() === "/team-reveal"
		? teamRevealBoardTextFor(seed)
		: playersByTierForSeed(seed)
				.map(
					(tier) =>
						`$${tier.price}: ${tier.players.map((p) => shortName(p)).join(", ")}`,
				)
				.join("\n");

const clamp = (value: number, min: number, max: number) =>
	Math.max(min, Math.min(max, value));

const avg = (
	players: Player[],
	key: keyof Pick<Player, "off" | "def" | "space" | "create" | "usage" | "reb">,
) => players.reduce((sum, p) => sum + p[key], 0) / Math.max(players.length, 1);

const playerValues = (
	players: Player[],
	key: keyof Pick<Player, "off" | "def" | "space" | "create" | "usage" | "reb">,
) => players.map((p) => p[key]);

const pythagoreanAverage = (
	players: Player[],
	key: keyof Pick<Player, "off" | "def" | "space" | "create" | "usage" | "reb">,
) => {
	const values = playerValues(players, key);
	return Math.sqrt(
		values.reduce((sum, value) => sum + value ** 2, 0) /
			Math.max(values.length, 1),
	);
};

const standardDeviation = (values: number[]) => {
	if (values.length === 0) {
		return 0;
	}
	const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
	return Math.sqrt(
		values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length,
	);
};

const playerRatingSpread = (
	players: Player[],
	key: keyof Pick<Player, "off" | "def" | "space" | "create" | "usage" | "reb">,
) => standardDeviation(playerValues(players, key));

const rosterFitFor = (players: Player[]) => {
	const guards = players.filter((p) => p.pos.includes("G")).length;
	const bigs = players.filter((p) => p.pos.includes("C")).length;
	const primaryCreators = players.filter((p) => p.create >= 88).length;
	const eliteCreators = players.filter((p) => p.create >= 95).length;
	const highSpacers = players.filter((p) => p.space >= 85).length;
	const nonShooters = players.filter((p) => p.space < 60).length;
	const paintCloggers = players.filter((p) => p.space < 45).length;
	const plusDefenders = players.filter((p) => p.def >= 85).length;
	const eliteDefenders = players.filter((p) => p.def >= 94).length;
	const strongRebounders = players.filter((p) => p.reb >= 85).length;
	const highUsage = players.filter((p) => p.usage >= 82).length;
	const veryHighUsage = players.filter((p) => p.usage >= 90).length;
	const connectors = players.filter(
		(p) => p.usage <= 60 && (p.def >= 80 || p.space >= 80),
	).length;

	const creatorSpacing =
		(eliteCreators >= 1 && highSpacers >= 3 ? 4.5 : 0) +
		(primaryCreators >= 2 && highSpacers >= 2 ? 2.5 : 0) -
		(primaryCreators === 0 ? 7 : primaryCreators === 1 ? 2 : 0);

	const spacingPenalty =
		(highSpacers <= 1 ? 5 : highSpacers === 2 ? 2 : 0) +
		(nonShooters >= 2 ? 3.5 : 0) +
		(paintCloggers >= 2 ? 2.5 : 0);

	const defenseGlass =
		(eliteDefenders >= 2 && strongRebounders >= 2 ? 4 : 0) +
		(plusDefenders >= 4 ? 3 : 0);

	const roleBalance =
		(connectors >= 2 && highUsage <= 2 ? 2.5 : 0) -
		(veryHighUsage >= 3 ? 5 : highUsage >= 4 ? 4 : highUsage === 3 ? 2 : 0);

	const shape =
		(guards >= 3 && primaryCreators >= 2 ? 2 : 0) -
		(guards < 2 ? 4 : 0) -
		(bigs === 0 ? 4 : bigs > 2 ? 3 : 0);

	return clamp(
		creatorSpacing - spacingPenalty + defenseGlass + roleBalance + shape,
		-10,
		10,
	);
};

const resultFor = (players: Player[]) => {
	const offense = pythagoreanAverage(players, "off");
	const defense = pythagoreanAverage(players, "def");
	const spacing = avg(players, "space");
	const creation = avg(players, "create");
	const rebounding = avg(players, "reb");
	const offenseSpread = playerRatingSpread(players, "off");
	const defenseSpread = playerRatingSpread(players, "def");
	const volatility = clamp(
		(offenseSpread + defenseSpread) / 2 +
			Math.abs(offenseSpread - defenseSpread) * 0.25,
		3,
		18,
	);
	const guards = players.filter((p) => p.pos.includes("G")).length;
	const bigs = players.filter((p) => p.pos.includes("C")).length;
	const topUsage = players.filter((p) => p.usage >= 80).length;
	const lowSpacing = players.filter((p) => p.space < 60).length;
	const fit = rosterFitFor(players);

	const balance =
		(guards >= 2 ? 2 : -3) +
		(bigs >= 1 && bigs <= 2 ? 2 : -3) +
		(topUsage <= 2 ? 3 : -5) +
		(lowSpacing <= 1 ? 2 : -4);
	const net =
		(offense - 76) * 0.2 +
		(defense - 76) * 0.2 +
		(spacing - 70) * 0.04 +
		(creation - 62) * 0.045 +
		(rebounding - 65) * 0.03 +
		fit * 0.55 +
		balance * 0.45;
	const wins = Math.round(clamp(41 + net * 2.35, 18, 78));
	const titleOdds = clamp((wins - 44) * 1.9 + net * 1.8, 0.4, 48);
	const percentile = Math.round(
		clamp((wins - 35) * 2.6 + titleOdds * 0.45, 5, 99),
	);

	let identity = "Balanced Contender";
	if (spacing > 82 && creation > 72) identity = "Five-Out Firestorm";
	else if (defense > 85) identity = "Switch Everything";
	else if (offense > 88) identity = "Scoreboard Problem";
	else if (rebounding > 80) identity = "Glass Cleaner";

	let flaw =
		"Needs one clearer edge in shooting, creation, rebounding, or defense.";
	if (topUsage > 2) flaw = "Too many first options want the ball late.";
	else if (lowSpacing > 1) flaw = "The paint gets crowded in crunch time.";
	else if (guards < 3) flaw = "Not enough ball handling against pressure.";
	else if (bigs === 0) flaw = "The rim protection plan is mostly hope.";
	else if (defense < 74) flaw = "This group gives points back too easily.";

	return {
		defense,
		defenseSpread,
		fit,
		flaw,
		identity,
		creation,
		net,
		offense,
		offenseSpread,
		percentile,
		rebounding,
		spacing,
		titleOdds,
		volatility,
		wins,
	};
};

type TeamResult = ReturnType<typeof resultFor>;

type SimTeamBase = {
	conference: Conference;
	id: string;
	name: string;
	players: Player[];
	power: number;
	result: TeamResult;
};

type SimTeam = SimTeamBase & {
	conferenceSeed: number;
	losses: number;
	seed: number;
	winPct: number;
	wins: number;
};

type SeriesResult = {
	games: string;
	loser: SimTeam;
	round: string;
	winner: SimTeam;
};

type SimGame = {
	id: string;
	index: number;
	opponent: SimTeam;
	opponentScore: number;
	result: "W" | "L";
	userScore: number;
};

type MonthlySim = {
	games: SimGame[];
	label: string;
	losses: number;
	pointDiff: number;
	wins: number;
};

type PlayoffRoundSim = {
	games: SimGame[];
	losses: number;
	opponent: SimTeam;
	result: "Won" | "Lost";
	round: string;
	series: string;
	wins: number;
};

type SimSegment = {
	detail: string;
	games: SimGame[];
	id: string;
	label: string;
	losses: number;
	pointDiff?: number;
	type: "month" | "playoff";
	wins: number;
};

type PlayerSimLine = {
	ast: number;
	player: Player;
	pts: number;
	trb: number;
};

type BoxScoreLine = PlayerSimLine & {
	blk: number;
	plusMinus: number;
};

type TeamBoxStats = {
	astPct: number;
	fgPct: number;
	rebPct: number;
	threePct: number;
	toPct: number;
};

type TeamBoxStatKey = keyof TeamBoxStats;

type TeamStatBenchmark = Record<TeamBoxStatKey, number[]>;
type Conference = "East" | "West";

const AI_TEAM_INFOS: Array<{ conference: Conference; name: string }> = [
	{ conference: "East", name: "Arc Lab" },
	{ conference: "East", name: "SwitchBot" },
	{ conference: "East", name: "Two-MVP Tax" },
	{ conference: "East", name: "Rim Math" },
	{ conference: "East", name: "Shot Diet" },
	{ conference: "East", name: "Glass House" },
	{ conference: "East", name: "Small Ball" },
	{ conference: "East", name: "Length Tax" },
	{ conference: "East", name: "OKC Lab" },
	{ conference: "East", name: "Value Vultures" },
	{ conference: "East", name: "Pace Lab" },
	{ conference: "East", name: "Wing Stop" },
	{ conference: "East", name: "Rim Pressure" },
	{ conference: "East", name: "Corner Math" },
	{ conference: "West", name: "Paint Crew" },
	{ conference: "West", name: "Midrange Tax" },
	{ conference: "West", name: "Dunker Spot" },
	{ conference: "West", name: "Pick Rollers" },
	{ conference: "West", name: "Second Apron" },
	{ conference: "West", name: "Transition Co" },
	{ conference: "West", name: "Zone Breakers" },
	{ conference: "West", name: "High Wall" },
	{ conference: "West", name: "Low Man" },
	{ conference: "West", name: "Matchup Hunt" },
	{ conference: "West", name: "Motion Club" },
	{ conference: "West", name: "Drop Coverage" },
	{ conference: "West", name: "Heat Check" },
	{ conference: "West", name: "Late Clock" },
	{ conference: "West", name: "Clean Glass" },
];

const AI_BUDGET_PATTERNS = [
	[10, 8, 6, 4, 2],
	[10, 8, 6, 4, 2],
	[10, 10, 4, 4, 2],
	[10, 8, 8, 2, 2],
	[10, 6, 6, 6, 2],
	[10, 6, 6, 4, 4],
	[8, 8, 6, 6, 2],
	[8, 8, 6, 4, 4],
	[8, 6, 6, 6, 4],
	[6, 6, 6, 6, 6],
];

const SEASON_MONTHS = [
	{ games: 7, label: "Oct" },
	{ games: 14, label: "Nov" },
	{ games: 14, label: "Dec" },
	{ games: 15, label: "Jan" },
	{ games: 11, label: "Feb" },
	{ games: 15, label: "Mar" },
	{ games: 6, label: "Apr" },
];

const ALL_PLAYERS: Player[] = [...TEAM_REVEAL_PLAYER_POOL, ...PLAYERS];
const playerById = new Map(ALL_PLAYERS.map((p) => [p.id, p]));
const defaultPlayerById = new Map(PLAYERS.map((p) => [p.id, p]));
const teamRevealPlayerById = new Map(TEAM_REVEAL_PLAYER_POOL.map((p) => [p.id, p]));

const playersForIds = (ids: string[], source = playerById) =>
	ids
		.map((id) => source.get(id))
		.filter((p): p is Player => p !== undefined);

const playerSourceForCurrentPath = () =>
	window.location.pathname.startsWith("/team-reveal")
		? teamRevealPlayerById
		: defaultPlayerById;

const hashString = (value: string) => {
	let hash = 0;
	for (let i = 0; i < value.length; i++) {
		hash = (hash * 31 + value.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
};

const seededUnit = (seed: string) => (hashString(seed) % 1000) / 1000;

const seededNoise = (seed: string, spread: number) =>
	(seededUnit(seed) - 0.5) * spread;

const rosterKeyFor = (players: Player[]) =>
	players
		.map((p) => p.id)
		.sort()
		.join(",");

const round1 = (value: number) => Math.round(value * 10) / 10;

const teamPower = (result: TeamResult) => {
	return (
		result.net +
		result.wins * 0.12 +
		result.titleOdds * 0.05 +
		(result.offense - 76) * 0.18 +
		(result.defense - 76) * 0.18 +
		result.fit * 0.65 -
		result.volatility * 0.08
	);
};

const buildTeamBase = (
	id: string,
	name: string,
	players: Player[],
	conference: Conference,
): SimTeamBase => {
	const result = resultFor(players);
	return {
		conference,
		id,
		name,
		players,
		power: teamPower(result),
		result,
	};
};

const generateTeamRevealAiTeamBases = (
	seed: number,
	selected: Player[],
	opponentTeams: string[],
	teamInfos = AI_TEAM_INFOS,
) => {
	const selectedKey = rosterKeyFor(selected);
	const candidateKeys = new Set([selectedKey]);
	const candidates: { key: string; players: Player[]; score: number }[] = [];
	const teamOptions = opponentTeams.map(
		(team) => TEAM_REVEAL_PLAYERS_BY_TEAM.get(team) ?? [],
	);

	for (let attempt = 0; attempt < 900; attempt++) {
		const roster: Player[] = [];
		const used = new Set<string>();

		for (let i = 0; i < teamOptions.length; i++) {
			const options = stableShuffle(
				teamOptions[i] ?? [],
				seed * 1549 + attempt * 101 + i * 37,
			).filter((player) => !used.has(player.id));
			if (options.length === 0) {
				break;
			}
			const pickWindow = Math.min(options.length, 5);
			const pick =
				options[
					Math.floor(
						seededUnit(`team-ai-pick-${seed}-${attempt}-${i}`) * pickWindow,
					)
				]!;
			roster.push(pick);
			used.add(pick.id);
		}

		if (
			roster.length !== ROSTER_SIZE ||
			roster.reduce((sum, player) => sum + player.price, 0) > CAP
		) {
			continue;
		}

		const key = rosterKeyFor(roster);
		if (candidateKeys.has(key)) {
			continue;
		}
		candidateKeys.add(key);
		const result = resultFor(roster);
		candidates.push({
			key,
			players: roster,
			score:
				result.net +
				result.fit * 0.45 +
				seededNoise(`team-ai-candidate-${seed}-${attempt}`, 18),
		});
	}

	candidates.sort((a, b) => b.score - a.score);
	const available = candidates.slice(
		0,
		Math.max(teamInfos.length * 12, 120),
	);
	const teams: SimTeamBase[] = [];

	for (let i = 0; i < teamInfos.length && available.length > 0; i++) {
		const teamInfo = teamInfos[i]!;
		const windowSize = Math.min(available.length, 80);
		const index = Math.floor(
			seededUnit(`team-ai-select-${seed}-${i}`) ** 0.65 * windowSize,
		);
		const [candidate] = available.splice(index, 1);
		if (!candidate) {
			break;
		}
		teams.push(
			buildTeamBase(
				`ai-${i}`,
				teamInfo.name,
				candidate.players,
				teamInfo.conference,
			),
		);
	}

	return teams;
};

const generateAiTeamBases = (
	seed: number,
	selected: Player[],
	opponentTeams?: string[],
	count = AI_TEAM_INFOS.length,
) => {
	const teamInfos = AI_TEAM_INFOS.slice(0, count);
	if (opponentTeams && opponentTeams.length === ROSTER_SIZE) {
		return generateTeamRevealAiTeamBases(seed, selected, opponentTeams, teamInfos);
	}

	const playersByPrice = new Map(
		playersByTierForSeed(seed).map((tier) => [tier.price, tier.players]),
	);
	const selectedKey = rosterKeyFor(selected);
	const candidateKeys = new Set([selectedKey]);
	const candidates: { key: string; players: Player[]; score: number }[] = [];

	for (let attempt = 0; attempt < 650; attempt++) {
		const pattern =
			AI_BUDGET_PATTERNS[
				Math.floor(
					seededUnit(`ai-pattern-${seed}-${attempt}`) *
						AI_BUDGET_PATTERNS.length,
				)
			]!;
		const roster: Player[] = [];
		const used = new Set<string>();

		for (const price of pattern) {
			const options = stableShuffle(
				playersByPrice.get(price) ?? [],
				seed * 1009 + attempt * 97 + price * 13 + roster.length,
			).filter((p) => !used.has(p.id));
			if (options.length === 0) {
				break;
			}
			const pickWindow = Math.min(options.length, 3);
			const pick =
				options[
					Math.floor(
						seededUnit(`ai-pick-${seed}-${attempt}-${price}-${roster.length}`) *
							pickWindow,
					)
				]!;
			roster.push(pick);
			used.add(pick.id);
		}

		if (
			roster.length !== ROSTER_SIZE ||
			roster.reduce((sum, p) => sum + p.price, 0) > CAP
		) {
			continue;
		}

		const key = rosterKeyFor(roster);
		if (candidateKeys.has(key)) {
			continue;
		}
		candidateKeys.add(key);
		const result = resultFor(roster);
		candidates.push({
			key,
			players: roster,
			score:
				result.net +
				result.fit * 0.45 +
				seededNoise(`ai-candidate-${seed}-${attempt}`, 18),
		});
	}

	candidates.sort((a, b) => b.score - a.score);
	const available = candidates.slice(
		0,
		Math.max(teamInfos.length * 12, 120),
	);
	const teams: SimTeamBase[] = [];

	for (let i = 0; i < teamInfos.length && available.length > 0; i++) {
		const teamInfo = teamInfos[i]!;
		const windowSize = Math.min(available.length, 80);
		const index = Math.floor(
			seededUnit(`ai-select-${seed}-${i}`) ** 0.65 * windowSize,
		);
		const [candidate] = available.splice(index, 1);
		if (!candidate) {
			break;
		}
		teams.push(
			buildTeamBase(
				`ai-${i}`,
				teamInfo.name,
				candidate.players,
				teamInfo.conference,
			),
		);
	}

	return teams;
};

const generateReservedH2HTeamBase = (
	seed: number,
	selected: Player[],
	opponentTeams?: string[],
) => {
	const candidates = opponentTeams?.length === ROSTER_SIZE
		? generateTeamRevealAiTeamBases(seed + 37, selected, opponentTeams, [
				{ conference: "East", name: "Opponent" },
			])
		: generateAiTeamBases(seed + 37, selected, undefined, 1);
	const candidate = candidates[0];
	return candidate
		? {
				...candidate,
				conference: "East" as const,
				id: "opponent",
				name: "Opponent",
			}
		: undefined;
};

const matchupWinPct = (team: SimTeamBase, opponent: SimTeamBase) => {
	const offenseVsDefense = team.result.offense - opponent.result.defense;
	const defenseVsOffense = team.result.defense - opponent.result.offense;
	const fitEdge = team.result.fit - opponent.result.fit;
	const roleEdge =
		(team.result.spacing - opponent.result.spacing) * 0.04 +
		(team.result.creation - opponent.result.creation) * 0.05 +
		(team.result.rebounding - opponent.result.rebounding) * 0.06;
	const matchupEdge =
		(offenseVsDefense + defenseVsOffense) * 1.15 + fitEdge * 1.35 + roleEdge;
	const volatility = (team.result.volatility + opponent.result.volatility) / 2;
	const noise = seededNoise(
		`${team.id}-${rosterKeyFor(team.players)}-${opponent.id}-${rosterKeyFor(opponent.players)}`,
		clamp(5 + volatility * 0.34, 6, 12),
	);
	return clamp(50 + matchupEdge * 1.35 + noise, 8, 92);
};

const withSeasonRecord = (
	team: SimTeamBase,
	field: SimTeamBase[],
	index: number,
): SimTeam => {
	const opponents = field.filter((other) => other.id !== team.id);
	const avgWinPct =
		opponents.reduce(
			(sum, opponent) => sum + matchupWinPct(team, opponent),
			0,
		) / Math.max(opponents.length, 1);
	const rosterKey = rosterKeyFor(team.players);
	const wins = Math.round(
		clamp(
			82 * (avgWinPct / 100) +
				seededNoise(
					`${team.id}-${rosterKey}-season`,
					clamp(5 + team.result.volatility * 0.45, 6, 13),
				),
			20,
			72,
		),
	);
	return {
		...team,
		conferenceSeed: 0,
		losses: 82 - wins,
		seed: index + 1,
		winPct: wins / 82,
		wins,
	};
};

const compareSimTeams = (a: SimTeam, b: SimTeam) =>
	b.wins - a.wins || b.power - a.power || a.name.localeCompare(b.name);

const withConferenceSeeds = (standings: SimTeam[]) => {
	const conferenceRanks = new Map<string, number>();

	for (const conference of ["East", "West"] as const) {
		standings
			.filter((team) => team.conference === conference)
			.sort(compareSimTeams)
			.forEach((team, index) => {
				conferenceRanks.set(team.id, index + 1);
			});
	}

	return standings.map((team) => ({
		...team,
		conferenceSeed: conferenceRanks.get(team.id) ?? team.seed,
	}));
};

const seriesFor = (a: SimTeam, b: SimTeam, round: string): SeriesResult => {
	const pct = clamp(
		matchupWinPct(a, b) + seededNoise(`${round}-${a.id}-${b.id}`, 7),
		16,
		84,
	);
	const winner = pct >= 50 ? a : b;
	const loser = winner.id === a.id ? b : a;
	const winnerPct = winner.id === a.id ? pct : 100 - pct;
	const losses = winnerPct >= 65 ? 1 : winnerPct >= 56 ? 2 : 3;
	return {
		games: `4-${losses}`,
		loser,
		round,
		winner,
	};
};

const conferencePlayoffSeriesFor = (
	teams: SimTeam[],
	conference: Conference,
) => {
	const round1 = [
		seriesFor(teams[0]!, teams[7]!, "First Round"),
		seriesFor(teams[3]!, teams[4]!, "First Round"),
		seriesFor(teams[1]!, teams[6]!, "First Round"),
		seriesFor(teams[2]!, teams[5]!, "First Round"),
	];
	const semis = [
		seriesFor(round1[0]!.winner, round1[1]!.winner, "Conference Semifinals"),
		seriesFor(round1[2]!.winner, round1[3]!.winner, "Conference Semifinals"),
	];
	const finals = seriesFor(
		semis[0]!.winner,
		semis[1]!.winner,
		"Conference Finals",
	);

	return {
		finals,
		series: [...round1, ...semis, finals].map((series) => ({
			...series,
			conference,
		})),
	};
};

const gameScoreFor = (
	userTeam: SimTeam,
	opponent: SimTeam,
	seedKey: string,
	result: "W" | "L",
) => {
	const total = clamp(
		220 +
			(userTeam.result.offense + opponent.result.offense - 156) * 0.56 -
			(userTeam.result.defense + opponent.result.defense - 156) * 0.28 +
			seededNoise(`${seedKey}-total`, 22),
		184,
		260,
	);
	const expectedMargin =
		(matchupWinPct(userTeam, opponent) - 50) * 0.3 +
		(userTeam.result.fit - opponent.result.fit) * 0.18;
	const marginSize = clamp(
		Math.abs(expectedMargin + seededNoise(`${seedKey}-margin`, 18)),
		1,
		31,
	);
	const margin = Math.round(marginSize) * (result === "W" ? 1 : -1);
	let userScore = Math.round(clamp(total / 2 + margin / 2, 82, 154));
	let opponentScore = Math.round(clamp(userScore - margin, 80, 152));

	if (result === "W" && userScore <= opponentScore) {
		userScore = opponentScore + 1 + Math.floor(seededUnit(`${seedKey}-ot`) * 5);
	} else if (result === "L" && userScore >= opponentScore) {
		opponentScore = userScore + 1 + Math.floor(seededUnit(`${seedKey}-ot`) * 5);
	}

	return { opponentScore, userScore };
};

const regularSeasonDashboardFor = (
	userTeam: SimTeam,
	standings: SimTeam[],
	seed: number,
): MonthlySim[] => {
	const opponents = standings.filter((team) => team.id !== userTeam.id);
	const rosterKey = rosterKeyFor(userTeam.players);
	const scheduledOpponents = stableShuffle(
		Array.from({ length: 82 }, (_, i) => opponents[i % opponents.length]!),
		hashString(`schedule-${seed}-${rosterKey}`),
	);
	const gameCandidates = scheduledOpponents.map((opponent, index) => ({
		index,
		opponent,
		winScore:
			matchupWinPct(userTeam, opponent) +
			seededNoise(`season-game-${seed}-${rosterKey}-${index}`, 34),
	}));
	const wonIndexes = new Set(
		[...gameCandidates]
			.sort((a, b) => b.winScore - a.winScore)
			.slice(0, userTeam.wins)
			.map((game) => game.index),
	);
	const games = gameCandidates.map(({ index, opponent }) => {
		const result = wonIndexes.has(index) ? "W" : "L";
		const score = gameScoreFor(
			userTeam,
			opponent,
			`regular-${seed}-${rosterKey}-${index}-${opponent.id}`,
			result,
		);
		return {
			...score,
			id: `regular-${index}`,
			index: index + 1,
			opponent,
			result,
		};
	});

	let offset = 0;
	return SEASON_MONTHS.map((month) => {
		const monthGames = games.slice(offset, offset + month.games);
		offset += month.games;
		const wins = monthGames.filter((game) => game.result === "W").length;
		const losses = monthGames.length - wins;
		return {
			games: monthGames,
			label: month.label,
			losses,
			pointDiff: monthGames.reduce(
				(sum, game) => sum + game.userScore - game.opponentScore,
				0,
			),
			wins,
		};
	});
};

const parseSeriesLosses = (games: string) => {
	const [, losses] = games.split("-");
	const parsed = Number.parseInt(losses ?? "0", 10);
	return Number.isFinite(parsed) ? parsed : 0;
};

const playoffGameResults = ({
	seedKey,
	userLosses,
	userWon,
	userWins,
}: {
	seedKey: string;
	userLosses: number;
	userWon: boolean;
	userWins: number;
}) => {
	const finalResult: "W" | "L" = userWon ? "W" : "L";
	const priorWins = userWins - (userWon ? 1 : 0);
	const priorLosses = userLosses - (userWon ? 0 : 1);
	const priorResults = [
		...Array.from({ length: priorWins }, () => "W" as const),
		...Array.from({ length: priorLosses }, () => "L" as const),
	];

	return [...stableShuffle(priorResults, hashString(seedKey)), finalResult] as (
		| "W"
		| "L"
	)[];
};

const playoffDashboardFor = (
	userTeam: SimTeam,
	series: SeriesResult[],
	seed: number,
): PlayoffRoundSim[] => {
	const rosterKey = rosterKeyFor(userTeam.players);
	return series
		.filter(
			(seriesResult) =>
				seriesResult.winner.id === "user" || seriesResult.loser.id === "user",
		)
		.map((seriesResult, roundIndex) => {
			const userWon = seriesResult.winner.id === "user";
			const opponent = userWon ? seriesResult.loser : seriesResult.winner;
			const loserWins = parseSeriesLosses(seriesResult.games);
			const wins = userWon ? 4 : loserWins;
			const losses = userWon ? loserWins : 4;
			const results = playoffGameResults({
				seedKey: `playoff-results-${seed}-${rosterKey}-${seriesResult.round}`,
				userLosses: losses,
				userWon,
				userWins: wins,
			});
			const games = results.map((result, i) => {
				const score = gameScoreFor(
					userTeam,
					opponent,
					`playoff-${seed}-${rosterKey}-${roundIndex}-${i}-${opponent.id}`,
					result,
				);
				return {
					...score,
					id: `playoff-${roundIndex}-${i}`,
					index: i + 1,
					opponent,
					result,
				};
			});

			return {
				games,
				losses,
				opponent,
				result: userWon ? "Won" : "Lost",
				round: seriesResult.round,
				series: seriesResult.games,
				wins,
			};
		});
};

const playerSimLines = (players: Player[], team: SimTeam): PlayerSimLine[] => {
	const avgUsage = avg(players, "usage");
	const avgCreate = avg(players, "create");
	const avgReb = avg(players, "reb");
	const pace = clamp(
		0.93 + (team.result.offense + team.result.spacing - 150) / 650,
		0.88,
		1.1,
	);

	return players.map((player) => {
		const role = clamp(
			0.9 + (player.usage - avgUsage) / 240 + (player.create - avgCreate) / 650,
			0.78,
			1.18,
		);
		const rebRole = clamp(0.96 + (player.reb - avgReb) / 720, 0.82, 1.14);
		const astRole = clamp(0.94 + (player.create - avgCreate) / 520, 0.82, 1.16);
		return {
			ast: round1(player.ast * astRole),
			player,
			pts: round1(player.pts * pace * role),
			trb: round1(player.trb * rebRole),
		};
	});
};

const simulatedBlocksFor = ({
	game,
	player,
	team,
}: {
	game: SimGame;
	player: Player;
	team: SimTeam;
}) => {
	const defenseShape = PLAYER_DEFENSE_SHAPE[player.id];
	const fallbackBlockRate =
		(player.pos.includes("C") ? 2.4 : player.pos.includes("F") ? 1.4 : 0.7) +
		Math.max(0, player.def - 70) * 0.035 +
		Math.max(0, player.reb - 70) * 0.018;
	const blockBase =
		(defenseShape?.blkp ?? fallbackBlockRate) * player.mpg * 0.0086 || 0;

	return Math.max(
		0,
		Math.round(
			blockBase *
				(0.48 +
					seededUnit(`box-blk-${game.id}-${team.id}-${player.id}`) * 1.45),
		),
	);
};

const boxScoreLinesFor = ({
	game,
	opponentScore,
	score,
	team,
}: {
	game: SimGame;
	opponentScore: number;
	score: number;
	team: SimTeam;
}) => {
	const baseLines = playerSimLines(team.players, team);
	const rawPoints = baseLines.map((line) =>
		Math.max(
			1,
			line.pts *
				(0.74 +
					seededUnit(`box-pts-${game.id}-${team.id}-${line.player.id}`) * 0.62),
		),
	);
	const rawTotal = rawPoints.reduce((sum, value) => sum + value, 0);
	const pointLines = baseLines.map((line, i) => ({
		...line,
		blk: simulatedBlocksFor({ game, player: line.player, team }),
		plusMinus: Math.round(
			score -
				opponentScore +
				seededNoise(`box-plus-${game.id}-${team.id}-${line.player.id}`, 14),
		),
		pts: Math.max(
			1,
			Math.round((rawPoints[i]! / Math.max(rawTotal, 1)) * score),
		),
		trb: Math.max(
			1,
			Math.round(
				line.trb *
					(0.72 +
						seededUnit(`box-reb-${game.id}-${team.id}-${line.player.id}`) *
							0.72),
			),
		),
		ast: Math.max(
			0,
			Math.round(
				line.ast *
					(0.68 +
						seededUnit(`box-ast-${game.id}-${team.id}-${line.player.id}`) *
							0.72),
			),
		),
	}));
	const pointDiff = score - pointLines.reduce((sum, line) => sum + line.pts, 0);
	const topScorer = pointLines.reduce(
		(bestIndex, line, i) =>
			line.pts > pointLines[bestIndex]!.pts ? i : bestIndex,
		0,
	);
	pointLines[topScorer] = {
		...pointLines[topScorer]!,
		pts: Math.max(1, pointLines[topScorer]!.pts + pointDiff),
	};

	return pointLines.sort((a, b) => b.pts - a.pts);
};

const weightedAverage = (
	players: Player[],
	valueFor: (player: Player) => number,
	weightFor: (player: Player) => number,
) => {
	const totalWeight = players.reduce(
		(sum, player) => sum + weightFor(player),
		0,
	);
	return (
		players.reduce(
			(sum, player) => sum + valueFor(player) * weightFor(player),
			0,
		) / Math.max(totalWeight, 1)
	);
};

const teamBoxStatsFor = (players: Player[]): TeamBoxStats => {
	const fit = rosterFitFor(players);
	const guards = players.filter((p) => p.pos.includes("G")).length;
	const bigs = players.filter((p) => p.pos.includes("C")).length;
	const highUsage = players.filter((p) => p.usage >= 84).length;
	const highSpacers = players.filter((p) => p.space >= 85).length;
	const lowSpacing = players.filter((p) => p.space < 60).length;
	const primaryCreators = players.filter((p) => p.create >= 88).length;
	const usageWeight = (player: Player) =>
		0.85 + player.usage / 105 + player.pts / 42;
	const threeWeight = (player: Player) =>
		0.35 + Math.max(0, player.space - 42) / 44 + player.usage / 160;
	const fgPct =
		weightedAverage(
			players,
			(player) =>
				clamp(
					44.1 +
						(player.off - 76) * 0.105 +
						(player.create - 70) * 0.024 +
						(player.space - 70) * 0.018 +
						(player.reb - 70) * 0.012 -
						Math.max(0, player.usage - 88) * 0.035,
					39.5,
					64.5,
				),
			usageWeight,
		) +
		fit * 0.045;
	const threePct =
		weightedAverage(
			players,
			(player) =>
				clamp(
					31.2 +
						(player.space - 62) * 0.105 +
						(player.off - 76) * 0.035 +
						(player.create - 72) * 0.015,
					26.5,
					45,
				),
			threeWeight,
		) +
		fit * 0.035;
	const rebounding = pythagoreanAverage(players, "reb");
	const creation = avg(players, "create");
	const teamAst = players.reduce((sum, player) => sum + player.ast, 0);

	return {
		astPct: clamp(
			50.5 +
				(creation - 68) * 0.24 +
				(teamAst - 20) * 0.54 +
				highSpacers * 0.72 -
				Math.max(0, highUsage - 2) * 0.8 +
				fit * 0.13,
			43,
			74,
		),
		fgPct: clamp(fgPct, 41, 58.5),
		rebPct: clamp(
			48.6 +
				(rebounding - 72) * 0.12 +
				bigs * 0.35 -
				guards * 0.18 +
				fit * 0.035,
			43.5,
			57,
		),
		threePct: clamp(threePct, 28, 43),
		toPct: clamp(
			14.4 -
				(creation - 70) * 0.035 +
				Math.max(0, highUsage - 2) * 0.55 +
				lowSpacing * 0.38 -
				primaryCreators * 0.22 -
				fit * 0.08,
			9.5,
			18.5,
		),
	};
};

const TEAM_BOX_STAT_DEFS: {
	higherIsBetter: boolean;
	key: TeamBoxStatKey;
	label: string;
}[] = [
	{ key: "fgPct", label: "FG%", higherIsBetter: true },
	{ key: "threePct", label: "3P%", higherIsBetter: true },
	{ key: "rebPct", label: "REB%", higherIsBetter: true },
	{ key: "astPct", label: "AST%", higherIsBetter: true },
	{ key: "toPct", label: "TO%", higherIsBetter: false },
];

const teamStatBenchmarkCache = new Map<number, TeamStatBenchmark>();

const teamStatBenchmarkForSeed = (seed: number): TeamStatBenchmark => {
	const cached = teamStatBenchmarkCache.get(seed);
	if (cached) {
		return cached;
	}

	const boardPlayers = playersByTierForSeed(seed).flatMap(
		(tier) => tier.players,
	);
	const stats: TeamBoxStats[] = [];

	for (let a = 0; a < boardPlayers.length - 4; a++) {
		for (let b = a + 1; b < boardPlayers.length - 3; b++) {
			for (let c = b + 1; c < boardPlayers.length - 2; c++) {
				for (let d = c + 1; d < boardPlayers.length - 1; d++) {
					for (let e = d + 1; e < boardPlayers.length; e++) {
						const roster = [
							boardPlayers[a]!,
							boardPlayers[b]!,
							boardPlayers[c]!,
							boardPlayers[d]!,
							boardPlayers[e]!,
						];
						if (roster.reduce((sum, player) => sum + player.price, 0) <= CAP) {
							stats.push(teamBoxStatsFor(roster));
						}
					}
				}
			}
		}
	}

	const benchmark = Object.fromEntries(
		TEAM_BOX_STAT_DEFS.map(({ key }) => [
			key,
			stats.map((stat) => stat[key]).sort((a, b) => a - b),
		]),
	) as TeamStatBenchmark;
	teamStatBenchmarkCache.set(seed, benchmark);
	return benchmark;
};

const lowerBound = (values: number[], value: number) => {
	let low = 0;
	let high = values.length;
	while (low < high) {
		const mid = Math.floor((low + high) / 2);
		if (values[mid]! < value) {
			low = mid + 1;
		} else {
			high = mid;
		}
	}
	return low;
};

const upperBound = (values: number[], value: number) => {
	let low = 0;
	let high = values.length;
	while (low < high) {
		const mid = Math.floor((low + high) / 2);
		if (values[mid]! <= value) {
			low = mid + 1;
		} else {
			high = mid;
		}
	}
	return low;
};

const percentileForStat = (
	values: number[],
	value: number,
	higherIsBetter: boolean,
) => {
	if (values.length === 0) {
		return 0.5;
	}
	const betterCount = higherIsBetter
		? upperBound(values, value)
		: values.length - lowerBound(values, value);
	return clamp(betterCount / values.length, 0.01, 0.99);
};

const teamHeatMapStatsFor = (
	players: Player[],
	benchmark: TeamStatBenchmark,
) => {
	const stats = teamBoxStatsFor(players);
	return TEAM_BOX_STAT_DEFS.map((definition) => ({
		...definition,
		percentile: percentileForStat(
			benchmark[definition.key],
			stats[definition.key],
			definition.higherIsBetter,
		),
		value: stats[definition.key],
	}));
};

const heatMapStyleFor = (percentile: number) => {
	const gradientPoint = clamp((percentile - 0.25) / 0.5, 0, 1);
	const hue = Math.round(3 + gradientPoint * 142);
	const lightness = Math.round(29 + gradientPoint * 7);
	return {
		background: `linear-gradient(135deg, hsl(${hue} 74% ${lightness}% / 0.94), hsl(${hue} 76% ${lightness + 10}% / 0.72))`,
		borderColor: `hsl(${hue} 82% 58% / 0.72)`,
	};
};

const heatMapRankLabel = (percentile: number) => {
	const leagueSize = 30;
	const rank = clamp(Math.ceil((1 - percentile) * leagueSize), 1, leagueSize);
	return `${rank}/${leagueSize}`;
};

const formatTeamBoxPct = (value: number) => `${value.toFixed(1)}%`;

const teamStatBenchmarkForTeams = (teams: SimTeamBase[]): TeamStatBenchmark => {
	const stats = teams.map((team) => teamBoxStatsFor(team.players));
	return Object.fromEntries(
		TEAM_BOX_STAT_DEFS.map(({ key }) => [
			key,
			stats.map((stat) => stat[key]).sort((a, b) => a - b),
		]),
	) as TeamStatBenchmark;
};

const simulateField = (
	selected: Player[],
	seed: number,
	options: { opponentTeams?: string[]; reservedOpponent?: boolean } = {},
) => {
	const userBase = buildTeamBase("user", "Your Build", selected, "East");
	const reservedOpponent = options.reservedOpponent
		? generateReservedH2HTeamBase(seed, selected, options.opponentTeams)
		: undefined;
	const aiTeams = generateAiTeamBases(
		seed,
		selected,
		options.opponentTeams,
		AI_TEAM_INFOS.length - (reservedOpponent ? 1 : 0),
	).map((team, index) =>
		reservedOpponent && index === 0
			? { ...team, conference: "West" as const }
			: team,
	);
	const baseField = [
		userBase,
		...(reservedOpponent ? [reservedOpponent] : []),
		...aiTeams,
	];
	const standings = withConferenceSeeds(
		baseField
			.map((team, i) => withSeasonRecord(team, baseField, i))
			.sort(compareSimTeams)
			.map((team, i) => ({ ...team, seed: i + 1 })),
	);
	const userTeam = standings.find((team) => team.id === "user")!;
	const eastPlayoffTeams = standings
		.filter((team) => team.conference === "East")
		.sort(compareSimTeams)
		.slice(0, 8);
	const westPlayoffTeams = standings
		.filter((team) => team.conference === "West")
		.sort(compareSimTeams)
		.slice(0, 8);
	const userConferencePlayoffTeams =
		userTeam.conference === "East" ? eastPlayoffTeams : westPlayoffTeams;
	const inPlayoffs =
		userTeam.conferenceSeed <= 8 &&
		userConferencePlayoffTeams.some((team) => team.id === "user");
	const eastPlayoffs = conferencePlayoffSeriesFor(eastPlayoffTeams, "East");
	const westPlayoffs = conferencePlayoffSeriesFor(westPlayoffTeams, "West");
	const finals = seriesFor(
		eastPlayoffs.finals.winner,
		westPlayoffs.finals.winner,
		"Finals",
	);
	const allSeries = [...eastPlayoffs.series, ...westPlayoffs.series, finals];
	const userLoss = allSeries.find((series) => series.loser.id === "user");
	const userSeriesWins = allSeries.filter(
		(series) => series.winner.id === "user",
	);
	const monthly = regularSeasonDashboardFor(userTeam, standings, seed);
	const playoffRounds = playoffDashboardFor(userTeam, allSeries, seed);
	const playoffRound = !inPlayoffs
		? "Missed Playoffs"
		: userLoss
			? `Lost ${userLoss.round}`
			: "Champion";
	const playoffDetail = !inPlayoffs
		? `Finished ${ordinal(userTeam.conferenceSeed)} in the ${userTeam.conference}.`
		: userLoss
			? `Lost to ${userLoss.winner.name}, ${userLoss.games}.`
			: `Beat ${finals.loser.name}, ${finals.games}.`;

	return {
		champion: finals.winner,
		finals,
		leader: standings[0]!,
		monthly,
		playoffDetail,
		playoffRounds,
		playoffRound,
		playerLines: playerSimLines(selected, userTeam),
		series: allSeries,
		standings,
		statBenchmark: teamStatBenchmarkForTeams(baseField),
		userSeriesWins,
		userTeam,
	};
};

type SimField = ReturnType<typeof simulateField>;

const simSegmentsFor = (sim: SimField): SimSegment[] => [
	...sim.monthly.map((month) => ({
		detail:
			month.pointDiff === 0
				? "Even"
				: `${month.pointDiff > 0 ? "+" : ""}${month.pointDiff}`,
		games: month.games,
		id: `month-${month.label}`,
		label: month.label,
		losses: month.losses,
		pointDiff: month.pointDiff,
		type: "month" as const,
		wins: month.wins,
	})),
	...sim.playoffRounds.map((round) => ({
		detail: `vs ${round.opponent.name}`,
		games: round.games,
		id: `playoff-${round.round}`,
		label: round.round,
		losses: round.losses,
		type: "playoff" as const,
		wins: round.wins,
	})),
];

const ordinal = (value: number) => {
	const suffix =
		value % 10 === 1 && value % 100 !== 11
			? "st"
			: value % 10 === 2 && value % 100 !== 12
				? "nd"
				: value % 10 === 3 && value % 100 !== 13
					? "rd"
					: "th";
	return `${value}${suffix}`;
};

const formatFit = (value: number) =>
	value > 0 ? `+${round1(value)}` : `${round1(value)}`;

type FiveVFiveConstructionScores = {
	athleticism: number;
	fit: number;
	interiorDefense: number;
	interiorScoring: number;
	passing: number;
	perimeterDefense: number;
	rebounding: number;
	roleBalance: number;
	shotCreation: number;
	sizeBalance: number;
	spacing: number;
	starPower: number;
};

type FiveVFiveArchetype =
	| "balanced_contender"
	| "big_physical_five"
	| "defense_first_five"
	| "flawed_experiment"
	| "offense_first_five"
	| "one_way_lineup"
	| "paint_power_five"
	| "perfect_fit_low_ceiling"
	| "shooting_heavy_five"
	| "small_skill_five"
	| "star_carried_five"
	| "talented_but_redundant";

type FiveVFiveInsight = {
	detail: string;
	evidence: string[];
	key: string;
	polarity: "context" | "didnt_work" | "worked";
	priority: number;
	title: string;
};

type FiveVFiveSeasonAnalysis = {
	archetype: FiveVFiveArchetype;
	didntWork: FiveVFiveInsight[];
	headline: string;
	keyPlayerNotes: {
		name: string;
		note: string;
		playerId: string;
	}[];
	recommendation: string;
	scores: FiveVFiveConstructionScores;
	summary: string;
	worked: FiveVFiveInsight[];
};

const playerConstructionScore = (player: Player) =>
	player.off * 0.25 +
	player.def * 0.21 +
	player.create * 0.19 +
	player.space * 0.15 +
	player.reb * 0.12 +
	player.usage * 0.08;

const roleCountsFor = (players: Player[]) => ({
	bigs: players.filter((player) => player.pos.includes("C")).length,
	connectors: players.filter(
		(player) =>
			player.usage <= 68 &&
			(player.space >= 78 || player.def >= 78 || player.ast >= 4),
	).length,
	creators: players.filter((player) => player.create >= 86 || player.ast >= 6.5)
		.length,
	defenders: players.filter((player) => player.def >= 82).length,
	guards: players.filter((player) => player.pos.includes("G")).length,
	highUsage: players.filter((player) => player.usage >= 84).length,
	nonShooters: players.filter((player) => player.space < 60).length,
	rebounders: players.filter((player) => player.reb >= 82 || player.trb >= 7.5)
		.length,
	shooters: players.filter((player) => player.space >= 82).length,
	wings: players.filter((player) => player.pos.includes("F")).length,
});

const computeFiveVFiveConstructionScores = (
	players: Player[],
): FiveVFiveConstructionScores => {
	const sortedQuality = [...players]
		.map(playerConstructionScore)
		.sort((a, b) => b - a);
	const roles = roleCountsFor(players);
	const avgQuality = avg(players, "off") * 0.32 + avg(players, "def") * 0.28;
	const topQuality =
		(sortedQuality[0] ?? 70) * 0.52 +
		(sortedQuality[1] ?? 70) * 0.28 +
		(sortedQuality.slice(2).reduce((sum, score) => sum + score, 0) /
			Math.max(sortedQuality.length - 2, 1)) *
			0.2;
	const sizeBalance = clamp(
		78 -
			(roles.bigs === 0 ? 24 : 0) -
			(roles.guards < 2 ? 15 : 0) -
			(roles.bigs > 2 ? 12 : 0) +
			(roles.bigs >= 1 && roles.guards >= 2 && roles.wings >= 1 ? 14 : 0),
		20,
		100,
	);
	const roleBalance = clamp(
		38 +
			Math.min(roles.creators, 2) * 12 +
			Math.min(roles.shooters, 3) * 9 +
			Math.min(roles.defenders, 3) * 7 +
			Math.min(roles.rebounders, 2) * 7 +
			Math.min(roles.connectors, 2) * 5 -
			Math.max(0, roles.highUsage - 2) * 9 -
			Math.max(0, roles.nonShooters - 1) * 8,
		20,
		100,
	);

	return {
		athleticism: clamp(
			avg(players, "def") * 0.45 +
				avg(players, "reb") * 0.35 +
				avg(players, "create") * 0.2,
			20,
			100,
		),
		fit: clamp(50 + rosterFitFor(players) * 5 + roleBalance * 0.24, 20, 100),
		interiorDefense: clamp(
			avg(players, "def") * 0.52 +
				avg(players, "reb") * 0.32 +
				roles.bigs * 5 -
				(roles.bigs === 0 ? 12 : 0),
			20,
			100,
		),
		interiorScoring: clamp(
			avg(players, "off") * 0.55 +
				avg(players, "reb") * 0.28 +
				roles.bigs * 4 +
				roles.nonShooters * 2,
			20,
			100,
		),
		passing: clamp(
			avg(players, "create") * 0.58 +
				players.reduce((sum, player) => sum + player.ast, 0) * 1.7 +
				Math.min(roles.connectors, 2) * 4,
			20,
			100,
		),
		perimeterDefense: clamp(
			avg(players, "def") * 0.72 +
				roles.defenders * 4 +
				roles.guards * 1.5 -
				(roles.guards < 2 ? 10 : 0),
			20,
			100,
		),
		rebounding: clamp(
			avg(players, "reb") * 0.82 + roles.rebounders * 5,
			20,
			100,
		),
		roleBalance,
		shotCreation: clamp(
			pythagoreanAverage(players, "create") * 0.7 +
				avg(players, "off") * 0.22 +
				Math.min(roles.creators, 2) * 5,
			20,
			100,
		),
		sizeBalance,
		spacing: clamp(
			avg(players, "space") * 0.78 + roles.shooters * 5 - roles.nonShooters * 5,
			20,
			100,
		),
		starPower: clamp(topQuality * 0.78 + avgQuality * 0.22, 20, 100),
	};
};

const classifyFiveVFiveRoles = (player: Player) =>
	[
		player.off >= 88 && player.def >= 84 ? "two_way_star" : undefined,
		player.create >= 88 || player.ast >= 7 ? "primary_creator" : undefined,
		player.create >= 78 || player.ast >= 4.5 ? "secondary_creator" : undefined,
		player.space >= 82 ? "shooter" : undefined,
		player.off >= 87 && (player.reb >= 80 || player.pos.includes("C"))
			? "interior_scorer"
			: undefined,
		player.def >= 82 && (player.pos.includes("G") || player.pos.includes("F"))
			? "perimeter_defender"
			: undefined,
		player.def >= 84 && (player.pos.includes("C") || player.reb >= 86)
			? "rim_protector"
			: undefined,
		player.reb >= 82 || player.trb >= 7.5 ? "rebounder" : undefined,
		player.usage <= 68 &&
		(player.space >= 78 || player.def >= 78 || player.ast >= 4)
			? "connector"
			: undefined,
		playerConstructionScore(player) < 67 ? "weak_link" : undefined,
	].filter((role): role is string => Boolean(role));

const archetypeLabel = (archetype: FiveVFiveArchetype) =>
	({
		balanced_contender: "balanced contender",
		big_physical_five: "big, physical five",
		defense_first_five: "defense-first five",
		flawed_experiment: "flawed experiment",
		offense_first_five: "offense-first five",
		one_way_lineup: "one-way lineup",
		paint_power_five: "interior scoring lineup",
		perfect_fit_low_ceiling: "perfect-fit low-ceiling five",
		shooting_heavy_five: "shooting-heavy five",
		small_skill_five: "small skill five",
		star_carried_five: "star-carried five",
		talented_but_redundant: "talented but redundant five",
	})[archetype];

const selectFiveVFiveArchetype = (
	scores: FiveVFiveConstructionScores,
	players: Player[],
): FiveVFiveArchetype => {
	const roles = roleCountsFor(players);
	if (scores.starPower >= 82 && scores.roleBalance < 58)
		return "star_carried_five";
	if (scores.starPower >= 78 && scores.fit < 55)
		return "talented_but_redundant";
	if (
		scores.spacing >= 78 &&
		scores.shotCreation >= 76 &&
		scores.perimeterDefense < 66
	)
		return "offense_first_five";
	if (scores.spacing >= 82 && roles.shooters >= 3) return "shooting_heavy_five";
	if (
		scores.perimeterDefense >= 78 &&
		scores.interiorDefense >= 76 &&
		scores.spacing < 66
	)
		return "defense_first_five";
	if (
		scores.interiorScoring >= 78 &&
		scores.rebounding >= 78 &&
		scores.spacing < 66
	)
		return "paint_power_five";
	if (roles.bigs === 0 && scores.passing >= 70) return "small_skill_five";
	if (roles.bigs >= 2 && scores.rebounding >= 78) return "big_physical_five";
	if (scores.fit >= 73 && scores.starPower < 70)
		return "perfect_fit_low_ceiling";
	if (
		Math.max(scores.shotCreation, scores.spacing, scores.perimeterDefense) -
			Math.min(scores.shotCreation, scores.spacing, scores.perimeterDefense) >=
		28
	)
		return "one_way_lineup";
	if (
		scores.roleBalance >= 70 &&
		scores.fit >= 66 &&
		Object.values(scores).every((score) => score >= 54)
	)
		return "balanced_contender";
	return "flawed_experiment";
};

const recordDescriptorFor = (winPct: number) => {
	if (winPct >= 0.75) return "elite";
	if (winPct >= 0.65) return "high-end";
	if (winPct >= 0.55) return "solid";
	if (winPct >= 0.45) return "competitive but capped";
	return "unstable";
};

const articleForPhrase = (phrase: string) =>
	/^[aeiou]/i.test(phrase.trim()) ? "an" : "a";

const insight = ({
	detail,
	evidence,
	key,
	polarity,
	priority,
	title,
}: FiveVFiveInsight) => ({
	detail,
	evidence,
	key,
	polarity,
	priority,
	title,
});

const heatMapStatMapFor = (
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>,
) =>
	Object.fromEntries(heatMapStats.map((stat) => [stat.key, stat])) as Record<
		TeamBoxStatKey,
		(typeof heatMapStats)[number]
	>;

const limitingBasketballNeedInsightFor = ({
	heatMapStats,
	playoffExit,
	priority,
	scores,
}: {
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	playoffExit?: {
		baseEvidence: string;
		opponentName: string;
		round: string;
	};
	priority: number;
	scores: FiveVFiveConstructionScores;
}) => {
	const statByKey = heatMapStatMapFor(heatMapStats);
	const categoryIntro = playoffExit
		? {
				defense: `${playoffExit.opponentName} found enough clean scoring matchups in the ${playoffExit.round}, and that made every offensive drought harder to survive.`,
				outsideShooting: `${playoffExit.opponentName} could crowd the floor in the ${playoffExit.round}, which made your best possessions tougher to repeat.`,
				rebounding: `${playoffExit.opponentName} won too many possession battles in the ${playoffExit.round}, so close games tilted away after the first shot.`,
				shotCreation: `${playoffExit.opponentName} forced enough tough late-clock possessions in the ${playoffExit.round} that the offense needed another creator.`,
				shotEfficiency: `${playoffExit.opponentName} pushed the offense into enough low-efficiency shots in the ${playoffExit.round} that the scoring could not keep pace.`,
			}
		: {
				defense:
					"The team did not create enough defensive resistance, so strong offensive stretches kept getting answered.",
				outsideShooting:
					"The floor was not open enough, so help defenders could shade toward the ball and shrink the best driving lanes.",
				rebounding:
					"The group gave away too many possession edges on the glass, which made close games harder to flip.",
				shotCreation:
					"The offense needed one more player who could reliably bend the defense and create a clean look.",
				shotEfficiency:
					"The team did not turn enough possessions into efficient looks, so it had to rely on tougher shot-making.",
			};
	const categories = [
		{
			detail: categoryIntro.shotEfficiency,
			evidence: [
				`FG% ranked ${heatMapRankLabel(statByKey.fgPct.percentile)}`,
				`FG% ${formatTeamBoxPct(statByKey.fgPct.value)}`,
			],
			key: playoffExit
				? "playoff_exit_shot_efficiency"
				: "shot_efficiency_limit",
			score: statByKey.fgPct.percentile * 100,
			title: playoffExit
				? `${playoffExit.round} exposed shot efficiency`
				: "Shot efficiency capped the offense",
		},
		{
			detail: categoryIntro.shotCreation,
			evidence: [`Creation score ${Math.round(scores.shotCreation)}/100`],
			key: playoffExit ? "playoff_exit_shot_creation" : "shot_creation_limit",
			score: scores.shotCreation,
			title: playoffExit
				? `${playoffExit.round} needed more creation`
				: "Shot creation was light",
		},
		{
			detail: categoryIntro.outsideShooting,
			evidence: [
				`Spacing score ${Math.round(scores.spacing)}/100`,
				`3P% ranked ${heatMapRankLabel(statByKey.threePct.percentile)}`,
			],
			key: playoffExit
				? "playoff_exit_outside_shooting"
				: "outside_shooting_limit",
			score: Math.min(scores.spacing, statByKey.threePct.percentile * 100),
			title: playoffExit
				? `${playoffExit.round} exposed the shooting gap`
				: "Outside shooting tightened the floor",
		},
		{
			detail: categoryIntro.rebounding,
			evidence: [
				`Rebounding score ${Math.round(scores.rebounding)}/100`,
				`REB% ranked ${heatMapRankLabel(statByKey.rebPct.percentile)}`,
			],
			key: playoffExit ? "playoff_exit_rebounding" : "rebounding_limit",
			score: Math.min(scores.rebounding, statByKey.rebPct.percentile * 100),
			title: playoffExit
				? `${playoffExit.round} came down to the glass`
				: "Rebounding cost possessions",
		},
		{
			detail: categoryIntro.defense,
			evidence: [
				`Perimeter defense ${Math.round(scores.perimeterDefense)}/100`,
				`Interior defense ${Math.round(scores.interiorDefense)}/100`,
			],
			key: playoffExit ? "playoff_exit_defense" : "defense_limit",
			score: Math.min(scores.perimeterDefense, scores.interiorDefense),
			title: playoffExit
				? `${playoffExit.round} stressed the defense`
				: "Defense capped the run",
		},
	].sort((a, b) => a.score - b.score)[0]!;

	return insight({
		detail: categories.detail,
		evidence: playoffExit
			? [playoffExit.baseEvidence, categories.evidence[0]!]
			: categories.evidence,
		key: categories.key,
		polarity: "didnt_work",
		priority,
		title: categories.title,
	});
};

const playoffExitInsightFor = (
	sim: SimField,
	scores: FiveVFiveConstructionScores,
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>,
): FiveVFiveInsight | undefined => {
	const exitSeries = sim.series.find((series) => series.loser.id === "user");
	if (!exitSeries) {
		return;
	}

	const opponent = exitSeries.winner;
	const opponentScores = computeFiveVFiveConstructionScores(opponent.players);
	const userTeam = sim.userTeam;
	const userAttackEdge = userTeam.result.offense - opponent.result.defense;
	const opponentAttackEdge = opponent.result.offense - userTeam.result.defense;
	const baseEvidence = `Lost to ${opponent.name}, ${exitSeries.games}`;

	if (opponentAttackEdge - userAttackEdge >= 5) {
		return insight({
			detail: `${opponent.name} created the better scoring matchups in the ${exitSeries.round}, so your offense had to win too many possession trades just to keep pace.`,
			evidence: [
				baseEvidence,
				`Opponent OFF ${Math.round(opponent.result.offense)} vs your DEF ${Math.round(userTeam.result.defense)}`,
			],
			key: "playoff_exit_defense",
			polarity: "didnt_work",
			priority: 112,
			title: `${exitSeries.round} matchup stressed the defense`,
		});
	}
	if (userAttackEdge <= -4) {
		return insight({
			detail: `${opponent.name}'s defense took away enough clean looks in the ${exitSeries.round} that your best creators had to work uphill.`,
			evidence: [
				baseEvidence,
				`Your OFF ${Math.round(userTeam.result.offense)} vs opponent DEF ${Math.round(opponent.result.defense)}`,
			],
			key: "playoff_exit_offense",
			polarity: "didnt_work",
			priority: 112,
			title: `${exitSeries.round} defense squeezed the offense`,
		});
	}
	if (opponentScores.rebounding - scores.rebounding >= 8) {
		return insight({
			detail: `${opponent.name} had the rebounding edge in the ${exitSeries.round}, which made it harder to flip close games after the first shot.`,
			evidence: [
				baseEvidence,
				`Rebounding gap ${Math.round(scores.rebounding)}-${Math.round(opponentScores.rebounding)}`,
			],
			key: "playoff_exit_rebounding",
			polarity: "didnt_work",
			priority: 112,
			title: `${exitSeries.round} came down to possessions`,
		});
	}
	if (opponentScores.spacing - scores.spacing >= 8) {
		return insight({
			detail: `${opponent.name} had cleaner spacing in the ${exitSeries.round}, which opened the floor while your tougher possessions got more crowded.`,
			evidence: [
				baseEvidence,
				`Spacing gap ${Math.round(scores.spacing)}-${Math.round(opponentScores.spacing)}`,
			],
			key: "playoff_exit_spacing",
			polarity: "didnt_work",
			priority: 112,
			title: `${exitSeries.round} exposed the spacing gap`,
		});
	}
	return limitingBasketballNeedInsightFor({
		heatMapStats,
		playoffExit: {
			baseEvidence,
			opponentName: opponent.name,
			round: exitSeries.round,
		},
		priority: 112,
		scores,
	});
};

const detectFiveVFiveInsights = ({
	heatMapStats,
	players,
	scores,
	sim,
}: {
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	players: Player[];
	scores: FiveVFiveConstructionScores;
	sim: SimField;
}) => {
	const roles = roleCountsFor(players);
	const worked: FiveVFiveInsight[] = [];
	const didntWork: FiveVFiveInsight[] = [];
	const statByKey = heatMapStatMapFor(heatMapStats);
	const team = sim.userTeam;
	const playoffExitInsight = playoffExitInsightFor(sim, scores, heatMapStats);
	if (playoffExitInsight) {
		didntWork.push(playoffExitInsight);
	}

	if (scores.shotCreation >= 78) {
		worked.push(
			insight({
				detail:
					"Multiple players could start possessions, which kept the offense from depending on one action.",
				evidence: [
					`Creation score ${Math.round(scores.shotCreation)}/100`,
					`${roles.creators} creator${roles.creators === 1 ? "" : "s"} in the five`,
				],
				key: "shot_creation",
				polarity: "worked",
				priority: 96,
				title: "The lineup could create its own offense",
			}),
		);
	}
	if (scores.spacing >= 76 || statByKey.threePct.percentile >= 0.7) {
		worked.push(
			insight({
				detail:
					"The main options had enough room to attack because the lineup carried credible shooting.",
				evidence: [
					`Spacing score ${Math.round(scores.spacing)}/100`,
					`3P% ranked ${heatMapRankLabel(statByKey.threePct.percentile)}`,
				],
				key: "spacing",
				polarity: "worked",
				priority: 88,
				title: "Spacing gave the stars room",
			}),
		);
	}
	if (scores.perimeterDefense >= 76 || scores.interiorDefense >= 76) {
		worked.push(
			insight({
				detail:
					"The five had enough defensive resistance to survive cold stretches and keep games within reach.",
				evidence: [
					`Defense rating ${Math.round(team.result.defense)}`,
					`Perimeter defense ${Math.round(scores.perimeterDefense)}/100`,
				],
				key: "defense",
				polarity: "worked",
				priority: 84,
				title: "Defensive structure traveled",
			}),
		);
	}
	if (scores.rebounding >= 76 || statByKey.rebPct.percentile >= 0.7) {
		worked.push(
			insight({
				detail:
					"The team protected enough possessions on the glass to keep its best scoring lineups viable.",
				evidence: [
					`Rebounding score ${Math.round(scores.rebounding)}/100`,
					`REB% ranked ${heatMapRankLabel(statByKey.rebPct.percentile)}`,
				],
				key: "rebounding",
				polarity: "worked",
				priority: 80,
				title: "The glass held up",
			}),
		);
	}
	if (scores.roleBalance >= 70) {
		worked.push(
			insight({
				detail:
					"The roles were clean enough: creation, shooting, defense, and rebounding were not all coming from the same player.",
				evidence: [
					`Role balance ${Math.round(scores.roleBalance)}/100`,
					`${roles.connectors} connector${roles.connectors === 1 ? "" : "s"}`,
				],
				key: "role_balance",
				polarity: "worked",
				priority: 78,
				title: "The construction had a readable hierarchy",
			}),
		);
	}

	if (scores.spacing < 58 || statByKey.threePct.percentile <= 0.35) {
		didntWork.push(
			insight({
				detail:
					"Too many possessions got compressed because opponents could load up toward the ball or the paint.",
				evidence: [
					`Spacing score ${Math.round(scores.spacing)}/100`,
					`3P% ranked ${heatMapRankLabel(statByKey.threePct.percentile)}`,
				],
				key: "poor_spacing",
				polarity: "didnt_work",
				priority: 96,
				title: "Spacing capped the offense",
			}),
		);
	}
	if (scores.interiorDefense < 60) {
		didntWork.push(
			insight({
				detail:
					"Opponents had too much margin at the rim, so every perimeter breakdown became expensive.",
				evidence: [
					`Interior defense ${Math.round(scores.interiorDefense)}/100`,
					`${roles.bigs} true big${roles.bigs === 1 ? "" : "s"}`,
				],
				key: "interior_defense",
				polarity: "didnt_work",
				priority: 90,
				title: "The back line was thin",
			}),
		);
	}
	if (scores.rebounding < 60 || statByKey.rebPct.percentile <= 0.35) {
		didntWork.push(
			insight({
				detail:
					"Bigger groups could extend possessions, which made close losses harder to flip.",
				evidence: [
					`Rebounding score ${Math.round(scores.rebounding)}/100`,
					`REB% ranked ${heatMapRankLabel(statByKey.rebPct.percentile)}`,
				],
				key: "rebounding_leak",
				polarity: "didnt_work",
				priority: 86,
				title: "Rebounding leaked possessions",
			}),
		);
	}
	if (statByKey.toPct.percentile <= 0.35 || scores.passing < 58) {
		didntWork.push(
			insight({
				detail:
					"The team did not create enough clean advantages, so tough passes and late-clock possessions pushed too many empty trips.",
				evidence: [
					`TO% ranked ${heatMapRankLabel(statByKey.toPct.percentile)}`,
					`Passing score ${Math.round(scores.passing)}/100`,
				],
				key: "shot_creation_turnovers",
				polarity: "didnt_work",
				priority: 82,
				title: "Shot creation got expensive",
			}),
		);
	}
	if (scores.roleBalance < 56) {
		didntWork.push(
			insight({
				detail:
					"The five had overlapping on-ball strengths without enough complementary pressure, so the extra talent did not always turn into cleaner looks.",
				evidence: [
					`Role balance ${Math.round(scores.roleBalance)}/100`,
					`${roles.highUsage} high-usage player${roles.highUsage === 1 ? "" : "s"}`,
				],
				key: "shot_creation_overlap",
				polarity: "didnt_work",
				priority: 80,
				title: "Shot creation did not scale",
			}),
		);
	}
	if (worked.length === 0) {
		worked.push(
			insight({
				detail:
					"The five still had enough individual quality to generate runs, even if the overall shape was uneven.",
				evidence: [`Star power ${Math.round(scores.starPower)}/100`],
				key: "baseline_talent",
				polarity: "worked",
				priority: 50,
				title: "Talent kept it afloat",
			}),
		);
	}
	if (didntWork.length === 0) {
		didntWork.push(
			limitingBasketballNeedInsightFor({
				heatMapStats,
				priority: 50,
				scores,
			}),
		);
	}

	return {
		didntWork: didntWork.sort((a, b) => b.priority - a.priority).slice(0, 3),
		worked: worked.sort((a, b) => b.priority - a.priority).slice(0, 3),
	};
};

const keyPlayerNotesFor = (players: Player[]) =>
	[...players]
		.sort((a, b) => playerConstructionScore(b) - playerConstructionScore(a))
		.slice(0, 2)
		.map((player) => {
			const roles = classifyFiveVFiveRoles(player);
			const role = roles.includes("two_way_star")
				? "two-way pressure"
				: roles.includes("primary_creator")
					? "shot creation"
					: roles.includes("shooter")
						? "spacing"
						: roles.includes("rim_protector")
							? "rim protection"
							: roles.includes("rebounder")
								? "rebounding"
								: "connective value";
			return {
				name: shortName(player),
				note: `${shortName(player)} drove ${role} with ${Math.round(player.off)} OFF / ${Math.round(player.def)} DEF.`,
				playerId: player.id,
			};
		});

const recommendationFor = ({
	didntWork,
	players,
	scores,
}: {
	didntWork: FiveVFiveInsight[];
	players: Player[];
	scores: FiveVFiveConstructionScores;
}) => {
	const roles = roleCountsFor(players);
	const topProblem = didntWork[0]?.key;
	if (topProblem?.includes("defense") || topProblem === "interior_defense") {
		return "Next time, keep the scoring core but use one slot on a stronger point-of-attack defender or back-line stopper.";
	}
	if (
		topProblem === "shot_efficiency_limit" ||
		topProblem === "playoff_exit_shot_efficiency"
	) {
		return "Next time, add one more efficient scorer or finisher so the offense gets cleaner makes without needing perfect possessions.";
	}
	if (
		topProblem?.includes("shot_creation") ||
		topProblem === "playoff_exit_offense"
	) {
		return "Next time, add one more clean-shot creator or spacer so a playoff defense cannot load up on the first option.";
	}
	if (topProblem?.includes("rebounding") || topProblem === "rebounding_leak") {
		return "Next time, add a rebounder who can finish possessions without wrecking the offensive spacing.";
	}
	if (
		topProblem?.includes("outside_shooting") ||
		topProblem === "poor_spacing" ||
		topProblem === "playoff_exit_spacing"
	) {
		return "Next time, prioritize shooting in the last slot so the playoff floor stays open when defenses tighten up.";
	}
	if (topProblem === "poor_spacing" || scores.spacing < 58) {
		return "Next time, use the final slot on a real shooter or low-usage spacer so the creators have a cleaner runway.";
	}
	if (topProblem === "interior_defense" || roles.bigs === 0) {
		return "Next time, spend one slot on a defensive big or a rebounding forward instead of adding another scorer.";
	}
	if (topProblem === "rebounding_leak") {
		return "Next time, prioritize a glass-cleaning wing or center who can finish possessions after the first stop.";
	}
	return "Next time, keep the core idea but use the last pick to patch the clearest team need instead of chasing raw talent.";
};

const buildFiveVFiveSeasonAnalysis = ({
	heatMapStats,
	sim,
}: {
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	sim: SimField;
}): FiveVFiveSeasonAnalysis => {
	const players = sim.userTeam.players;
	const scores = computeFiveVFiveConstructionScores(players);
	const archetype = selectFiveVFiveArchetype(scores, players);
	const { didntWork, worked } = detectFiveVFiveInsights({
		heatMapStats,
		players,
		scores,
		sim,
	});
	const topWorked =
		worked[0]?.detail ??
		"the five had enough combined talent to create advantages across several matchups.";
	const topProblem =
		didntWork[0]?.detail ??
		"the margin was more about playoff matchup stress than one fatal construction flaw.";
	const descriptor = recordDescriptorFor(sim.userTeam.winPct);
	const archetypeName = archetypeLabel(archetype);
	const headline =
		sim.userTeam.winPct >= 0.65
			? "This build had a real identity"
			: sim.userTeam.winPct >= 0.5
				? "This build was competitive, but capped"
				: "This build needed one cleaner role";

	return {
		archetype,
		didntWork,
		headline,
		keyPlayerNotes: keyPlayerNotesFor(players),
		recommendation: recommendationFor({ didntWork, players, scores }),
		scores,
		summary: `You built ${articleForPhrase(archetypeName)} ${archetypeName}. It worked because ${topWorked} The main limitation was that ${topProblem} Overall, this was ${descriptor} construction with a ${Math.round(scores.fit)}/100 fit score.`,
		worked,
	};
};

const initialChallengeSeed = () => {
	const seed = Number(new URLSearchParams(window.location.search).get("seed"));
	return Number.isFinite(seed) && seed > 0 ? Math.floor(seed) : 1;
};

const leagueYearLabel = (year: number) => `${year}-${year + 1}`;

const initialLeagueYear = () => {
	const year = Number(new URLSearchParams(window.location.search).get("year"));
	return LEAGUE_YEARS.includes(year) ? year : DEFAULT_LEAGUE_YEAR;
};

type TeamRevealMode = "h2h" | "multi" | "solo";
type TeamRevealRole = "opponent";

const TEAM_REVEAL_MODES: Array<{
	description: string;
	icon: string;
	id: TeamRevealMode;
	meta: string;
	title: string;
}> = [
	{
		description:
			"Build your five and sim your season solo. No waiting - jump straight in.",
		icon: "1",
		id: "solo",
		meta: "1 player",
		title: "Single Player",
	},
	{
		description:
			"Face one opponent in the same league. They build their own team from the same pool, and the season won't sim until you've both locked in and hit Sim.",
		icon: "VS",
		id: "h2h",
		meta: "2 players · shared league",
		title: "Head-to-Head",
	},
	{
		description:
			"Invite a group to run the same challenge. Everyone plays their own independent copy of the league - teams never face off, you just compare how each build finishes.",
		icon: "2+",
		id: "multi",
		meta: "group · separate leagues",
		title: "Multiplayer",
	},
];

const teamRevealModeFromSearch = (): TeamRevealMode | undefined => {
	const mode = new URLSearchParams(window.location.search).get("mode");
	return mode === "solo" || mode === "h2h" || mode === "multi"
		? mode
		: undefined;
};

const teamRevealRoleFromSearch = (): TeamRevealRole | undefined =>
	new URLSearchParams(window.location.search).get("role") === "opponent"
		? "opponent"
		: undefined;

const teamRevealReadyFromSearch = () =>
	new URLSearchParams(window.location.search).get("ready") === "1";

const teamRevealHostHandleFromSearch = () =>
	normalizePlayerHandle(new URLSearchParams(window.location.search).get("host") ?? "");

const fantasyBasePath = () =>
	window.location.pathname.startsWith("/team-reveal")
		? "/team-reveal"
		: window.location.pathname.startsWith("/fantasy-reveal")
			? "/fantasy-reveal"
			: window.location.pathname.startsWith("/fantasy-sim")
				? "/fantasy-sim"
				: window.location.pathname.startsWith("/fantasy")
					? "/fantasy"
					: "/snacks/cap-challenge";

const challengePathFor = ({
	hostHandle,
	leagueYear,
	mode,
	playerIds,
	role,
	seed,
}: {
	hostHandle?: string;
	leagueYear: number;
	mode?: TeamRevealMode;
	playerIds?: string[];
	role?: TeamRevealRole;
	seed: number;
}) => {
	const params = new URLSearchParams({
		seed: String(seed),
		year: String(leagueYear),
	});
	if (mode) {
		params.set("mode", mode);
	}
	const normalizedHostHandle = normalizePlayerHandle(hostHandle ?? "");
	if (normalizedHostHandle.length === 4) {
		params.set("host", normalizedHostHandle);
	}
	if (role) {
		params.set("role", role);
	}
	if (playerIds && playerIds.length > 0) {
		params.set("picks", playerIds.join(","));
	}
	return `${fantasyBasePath()}?${params.toString()}`;
};

const challengeUrlFor = (args: Parameters<typeof challengePathFor>[0]) =>
	`${window.location.origin}${challengePathFor(args)}`;

const resultPathFor = ({
	autoStart,
	hostHandle,
	leagueYear,
	mode,
	playerIds,
	playerHandle,
	role,
	seed,
}: {
	autoStart?: boolean;
	hostHandle?: string;
	leagueYear: number;
	mode?: TeamRevealMode;
	playerHandle?: string;
	playerIds: string[];
	role?: TeamRevealRole;
	seed: number;
}) => {
	const params = new URLSearchParams({
		players: playerIds.join(","),
		seed: String(seed),
		year: String(leagueYear),
	});
	const handle = normalizePlayerHandle(playerHandle ?? "");
	if (handle.length === 4) {
		params.set("handle", handle);
	}
	if (autoStart) {
		params.set("auto", "1");
	}
	if (mode) {
		params.set("mode", mode);
	}
	const normalizedHostHandle = normalizePlayerHandle(hostHandle ?? "");
	if (normalizedHostHandle.length === 4) {
		params.set("host", normalizedHostHandle);
	}
	if (role) {
		params.set("role", role);
	}
	return `${fantasyBasePath()}/results?${params.toString()}`;
};

const resultUrlFor = (args: Parameters<typeof resultPathFor>[0]) =>
	`${window.location.origin}${resultPathFor(args)}`;

const resultPlayerIdsFromSearch = () => {
	const ids = new URLSearchParams(window.location.search).get("players");
	if (!ids) {
		return [];
	}
	const playerSource = playerSourceForCurrentPath();

	return ids
		.split(",")
		.map((id) => id.trim())
		.filter((id) => playerSource.has(id));
};

const normalizePlayerHandle = (raw: string) =>
	raw
		.toUpperCase()
		.replaceAll(/[^A-Z0-9]/g, "")
		.slice(0, 4);

const cachedPlayerHandle = () => {
	try {
		return normalizePlayerHandle(
			window.localStorage.getItem(PLAYER_HANDLE_STORAGE_KEY) ?? "",
		);
	} catch {
		return "";
	}
};

const cachePlayerHandle = (handle: string) => {
	const normalized = normalizePlayerHandle(handle);
	if (normalized.length !== 4) {
		return;
	}

	try {
		window.localStorage.setItem(PLAYER_HANDLE_STORAGE_KEY, normalized);
	} catch {
		// Private browsing and locked-down webviews can reject localStorage writes.
	}
};

const resultPlayerHandleFromSearch = () =>
	normalizePlayerHandle(
		new URLSearchParams(window.location.search).get("handle") ?? "",
	);

const shouldAutoStartSim = () =>
	new URLSearchParams(window.location.search).get("auto") === "1";

const playerHandleLabel = (handle: string) =>
	handle.length === 4 ? `#${handle}` : "Player";

const selectedIdsFromSearch = () => {
	const ids = new URLSearchParams(window.location.search).get("picks");
	if (!ids) {
		return [];
	}
	const playerSource = playerSourceForCurrentPath();

	return ids
		.split(",")
		.map((id) => id.trim())
		.filter((id) => playerSource.has(id));
};

const shareOrCopy = async ({
	text,
	title,
	url,
}: {
	text: string;
	title: string;
	url?: string;
}) => {
	if ("share" in navigator && typeof navigator.share === "function") {
		await navigator.share({ text, title, url });
		return;
	}

	await navigator.clipboard?.writeText(url ? `${text}\n${url}` : text);
};

const StatBar = ({
	label,
	value,
	variant = "default",
}: {
	label: string;
	value: number;
	variant?: "default" | "teamReveal";
}) =>
	variant === "teamReveal" ? (
		<div className="snacks-skill-bar">
			<div className="snacks-skill-bar-head">
				<span className="snacks-skill-bar-label">
					<i>{label === "3PT" ? "3" : label[0]}</i>
					<span>{label}</span>
				</span>
				<strong>{Math.round(value)}</strong>
			</div>
			<div className="snacks-skill-bar-track">
				<div
					className="snacks-skill-bar-fill"
					style={{ width: `${clamp(value, 0, 100)}%` }}
				/>
			</div>
		</div>
	) : (
		<div style={{ display: "grid", gap: 5 }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					fontSize: 12,
				}}
			>
				<span>{label}</span>
				<strong>{Math.round(value)}</strong>
			</div>
			<div
				style={{
					background: "rgba(255,255,255,0.11)",
					borderRadius: 999,
					height: 7,
					overflow: "hidden",
				}}
			>
				<div
					style={{
						background: "linear-gradient(90deg, #18e0a8, #ffca3a)",
						height: "100%",
						width: `${clamp(value, 0, 100)}%`,
					}}
				/>
			</div>
		</div>
	);

const LeagueYearSelect = ({
	leagueYear,
	onChange,
}: {
	leagueYear: number;
	onChange: (year: number) => void;
}) => (
	<div className="snacks-year-control">
		<label htmlFor="snacks-league-year">League year</label>
		<select
			id="snacks-league-year"
			onChange={(event) => onChange(Number(event.currentTarget.value))}
			value={leagueYear}
		>
			{LEAGUE_YEARS.map((year) => (
				<option key={year} value={year}>
					{leagueYearLabel(year)}
				</option>
			))}
		</select>
	</div>
);

const SnacksStyles = () => (
	<style>{`
		@import url("https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,600;0,700;0,800;0,900&display=swap");

		.snacks-page {
			color: #f8fafc;
			padding-bottom: 32px;
		}

		.snacks-header {
			align-items: end;
			display: grid;
			gap: 14px;
			grid-template-columns: minmax(0, 1fr) minmax(240px, 310px);
			margin: 16px 0 14px;
		}

		.snacks-reveal-header {
			display: block;
		}

		.snacks-back-link {
			color: #9aa4b2;
			font-size: 13px;
			text-decoration: none;
		}

		.snacks-flow-back {
			align-items: center;
			appearance: none;
			background: rgba(13, 21, 29, 0.92);
			border: 1px solid rgba(255, 255, 255, 0.14);
			border-radius: 20px;
			color: #e8edf2;
			cursor: pointer;
			display: inline-flex;
			font-family: inherit;
			font-size: 13px;
			font-weight: 900;
			height: 36px;
			justify-content: center;
			left: max(14px, env(safe-area-inset-left));
			line-height: 1;
			padding: 0 16px;
			position: fixed;
			top: max(10px, env(safe-area-inset-top));
			width: fit-content;
			z-index: 120;
		}

		.snacks-sim-stop-button {
			flex: 0 0 15%;
			min-width: 74px;
			position: static;
			width: auto;
			z-index: auto;
		}

		.snacks-summary-back {
			margin: 0 0 14px;
			position: static;
			width: fit-content;
			z-index: auto;
		}

		.snacks-kicker {
			color: #9aa4b2;
			font-size: 12px;
			font-weight: 800;
			margin-top: 8px;
			text-transform: uppercase;
		}

		.snacks-title {
			font-size: 38px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 0.95;
			margin: 3px 0 0;
		}

		.snacks-cap-panel {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
			display: grid;
			gap: 9px;
			padding: 12px;
		}

		.snacks-cap-line {
			align-items: baseline;
			display: flex;
			justify-content: space-between;
			gap: 12px;
		}

		.snacks-cap-spent {
			font-size: 26px;
			font-weight: 900;
			line-height: 1;
		}

		.snacks-cap-meta {
			color: #9aa4b2;
			font-size: 12px;
			font-weight: 800;
			text-align: right;
			text-transform: uppercase;
		}

		.snacks-cap-track {
			background: rgba(255, 255, 255, 0.09);
			border-radius: 999px;
			height: 7px;
			overflow: hidden;
		}

		.snacks-cap-fill {
			background: linear-gradient(90deg, #18e0a8, #f2b84b);
			height: 100%;
			transition: width 140ms ease;
		}

		.snacks-year-control {
			align-items: center;
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			margin-top: 10px;
		}

		.snacks-year-control label {
			color: #9aa4b2;
			font-size: 11px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-year-control select {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.14);
			border-radius: 6px;
			color: #f8fafc;
			font-weight: 800;
			padding: 7px 10px;
		}

		.snacks-game-flow {
			display: grid;
			gap: 14px;
		}

		.snacks-board-scroll {
			overflow-x: auto;
			padding-bottom: 2px;
		}

		.snacks-board {
			background: #0b0f14;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			box-shadow: 0 18px 44px rgba(0, 0, 0, 0.26);
			display: grid;
			gap: 6px;
			min-width: 0;
			padding: 6px;
		}

		.snacks-player-grid {
			display: grid;
		}

		.snacks-tier-row {
			display: grid;
			gap: 6px;
			grid-template-columns: 42px repeat(5, minmax(0, 1fr));
			min-width: 0;
		}

		.snacks-tier-label {
			align-items: center;
			background: #161c24;
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 6px;
			display: flex;
			flex-direction: column;
			height: 108px;
			justify-content: center;
			line-height: 1;
		}

		.snacks-tier-label strong {
			font-size: 20px;
			font-weight: 900;
		}

		.snacks-tier-label span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 800;
			margin-top: 7px;
			text-transform: uppercase;
		}

		.snacks-player-card {
			--accent: #18e0a8;
			--accent-rgb: 24, 224, 168;
			background: #141a22;
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 6px;
			color: #f8f9fa;
			cursor: pointer;
			display: flex;
			flex-direction: column;
			gap: 5px;
			height: 108px;
			justify-content: space-between;
			min-width: 0;
			overflow: hidden;
			padding: 7px;
			position: relative;
			text-align: center;
			transition:
				background 140ms ease,
				border-color 140ms ease,
				transform 140ms ease;
		}

		.snacks-player-card::before {
			background: var(--accent);
			content: "";
			height: 3px;
			left: 0;
			position: absolute;
			right: 0;
			top: 0;
		}

		.snacks-player-card:hover:not(:disabled) {
			background: #18212b;
			border-color: rgba(var(--accent-rgb), 0.7);
			transform: translateY(-1px);
		}

		.snacks-player-card:disabled {
			cursor: not-allowed;
			opacity: 0.38;
		}

		.snacks-player-card-unavailable {
			opacity: 0.42;
		}

		.snacks-player-card-price-10 {
			--accent: #f2b84b;
			--accent-rgb: 242, 184, 75;
		}

		.snacks-player-card-price-8 {
			--accent: #18e0a8;
			--accent-rgb: 24, 224, 168;
		}

		.snacks-player-card-price-6 {
			--accent: #65a8ff;
			--accent-rgb: 101, 168, 255;
		}

		.snacks-player-card-price-4 {
			--accent: #ff6b7d;
			--accent-rgb: 255, 107, 125;
		}

		.snacks-player-card-price-2 {
			--accent: #c7d0dc;
			--accent-rgb: 199, 208, 220;
		}

		.snacks-player-card-price-0 {
			--accent: #8f9baa;
			--accent-rgb: 143, 155, 170;
		}

		.snacks-player-card-picked {
			background: rgba(var(--accent-rgb), 0.16);
			border-color: rgba(var(--accent-rgb), 0.82);
		}

		.snacks-player-card-locked {
			cursor: default;
		}

		.snacks-player-card-locked:hover:not(:disabled) {
			background: rgba(var(--accent-rgb), 0.16);
			transform: none;
		}

		.snacks-player-card-top {
			align-items: center;
			display: grid;
			gap: 4px;
			grid-template-columns: minmax(0, 1fr) auto;
			min-width: 0;
		}

		.snacks-player-name {
			display: -webkit-box;
			font-size: 12px;
			font-weight: 900;
			line-height: 1.1;
			min-width: 0;
			overflow: hidden;
			text-align: left;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
		}

		.snacks-player-name-phone {
			display: none;
		}

		.snacks-player-cost {
			align-items: center;
			background: rgba(255, 255, 255, 0.08);
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 999px;
			color: #f8fafc;
			display: flex;
			font-size: 9px;
			font-weight: 950;
			height: 22px;
			justify-content: center;
			justify-self: end;
			line-height: 1;
			width: 28px;
		}

		.snacks-player-meta {
			color: #9ca3af;
			font-size: 9.5px;
			font-weight: 700;
			overflow: hidden;
			text-align: center;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-player-badges {
			display: flex;
			gap: 3px;
			justify-content: center;
			min-height: 16px;
		}

		.snacks-player-badge {
			align-items: center;
			background: rgba(var(--accent-rgb), 0.16);
			border: 1px solid rgba(var(--accent-rgb), 0.42);
			border-radius: 4px;
			color: #f8fafc;
			display: inline-flex;
			font-size: 9px;
			font-weight: 950;
			height: 16px;
			justify-content: center;
			line-height: 1;
			min-width: 16px;
			padding: 0 4px;
		}

		.snacks-player-stats {
			display: grid;
			gap: 3px;
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		.snacks-player-stats-compact {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-player-stat {
			background: rgba(255, 255, 255, 0.075);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 4px;
			display: grid;
			font-size: 8.5px;
			font-weight: 900;
			gap: 1px;
			justify-items: center;
			line-height: 1.05;
			overflow: hidden;
			padding: 4px;
			text-align: center;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-player-stat span {
			color: #b8c2cf;
			font-size: 8px;
			font-weight: 950;
			line-height: 1;
		}

		.snacks-player-stat strong {
			color: #ffffff;
			font-size: 12px;
			font-weight: 950;
			line-height: 1.05;
		}

		.snacks-team-locker {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			box-shadow: 0 18px 44px rgba(0, 0, 0, 0.24);
			display: grid;
			gap: 12px;
			padding: 12px;
			position: sticky;
			top: 8px;
			z-index: 2;
		}

		.snacks-team-locker-head {
			align-items: baseline;
			display: flex;
			gap: 10px;
			justify-content: space-between;
		}

		.snacks-team-locker-head strong {
			font-size: 16px;
		}

		.snacks-team-slots {
			display: grid;
			gap: 7px;
			grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		.snacks-team-slot {
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 6px;
			display: grid;
			gap: 2px;
			grid-template-rows: auto auto auto;
			min-height: 74px;
			min-width: 0;
			padding: 8px;
		}

		.snacks-team-slot-filled {
			background: rgba(24, 224, 168, 0.08);
			border-color: rgba(24, 224, 168, 0.2);
		}

		.snacks-team-slot strong {
			font-size: 13px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-view-player {
			align-self: end;
			background: rgba(255, 255, 255, 0.08);
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 4px;
			color: #f8fafc;
			font-size: 10px;
			font-weight: 900;
			justify-self: start;
			padding: 3px 6px;
			text-transform: uppercase;
		}

		.snacks-view-player:hover {
			background: rgba(24, 224, 168, 0.14);
			border-color: rgba(24, 224, 168, 0.4);
		}

		.snacks-team-actions {
			align-items: center;
			display: flex;
			gap: 8px;
			justify-content: space-between;
		}

		.snacks-team-actions-buttons {
			display: flex;
			gap: 8px;
		}

		.snacks-team-actions-buttons .btn-success {
			min-width: 116px;
		}

		.snacks-team-actions .btn {
			white-space: nowrap;
		}

		.snacks-submit-row {
			display: flex;
			justify-content: center;
		}

		.snacks-submit-row .btn {
			min-width: 180px;
		}

		.snacks-reveal-stage {
			background: #0b0f14;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			box-shadow: 0 18px 44px rgba(0, 0, 0, 0.26);
			display: grid;
			gap: 14px;
			padding: 14px;
		}

		.snacks-reveal-card-shell {
			display: contents;
		}

		.snacks-reveal-head {
			align-items: end;
			display: flex;
			gap: 12px;
			justify-content: space-between;
		}

		.snacks-reveal-head strong {
			display: block;
			font-size: 28px;
			font-weight: 950;
			line-height: 1;
		}

		.snacks-reveal-head span,
		.snacks-reveal-tier-pill {
			color: #9aa4b2;
			font-size: 11px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-reveal-tier-pill {
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 999px;
			color: #f8fafc;
			padding: 7px 10px;
			white-space: nowrap;
		}

		.snacks-reveal-slots {
			display: grid;
			gap: 10px;
			grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		.snacks-reveal-slot {
			align-items: center;
			appearance: none;
			background: transparent;
			border: 0;
			color: inherit;
			display: grid;
			font-family: inherit;
			gap: 6px;
			justify-items: center;
			min-width: 0;
			padding: 0;
		}

		.snacks-reveal-slot-mark {
			align-items: center;
			aspect-ratio: 1;
			background: rgba(255, 255, 255, 0.045);
			border: 2px dashed rgba(255, 255, 255, 0.22);
			border-radius: 999px;
			color: #9aa4b2;
			display: flex;
			font-size: 18px;
			font-weight: 950;
			justify-content: center;
			width: min(74px, 100%);
		}

		.snacks-reveal-slot-filled .snacks-reveal-slot-mark {
			background: rgba(24, 224, 168, 0.12);
			border-color: rgba(24, 224, 168, 0.55);
			border-style: solid;
			color: #f8fafc;
		}

		.snacks-reveal-slot-label {
			color: #9aa4b2;
			font-size: 13px;
			font-weight: 900;
			min-height: 18px;
			overflow: hidden;
			text-align: center;
			text-overflow: ellipsis;
			white-space: nowrap;
			width: 100%;
		}

		.snacks-reveal-cap-ledger {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 9px;
			padding: 10px;
		}

		.snacks-reveal-cap-ledger-head {
			align-items: end;
			display: flex;
			gap: 10px;
			justify-content: space-between;
		}

		.snacks-reveal-cap-ledger-head span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-reveal-cap-ledger-head strong {
			color: #f8fafc;
			display: block;
			font-size: 24px;
			font-weight: 950;
			line-height: 1;
		}

		.snacks-reveal-cap-ledger-head small {
			color: #9aa4b2;
			font-size: 11px;
			font-weight: 900;
			text-align: right;
			text-transform: uppercase;
		}

		.snacks-reveal-cap-ledger-items {
			display: grid;
			gap: 6px;
			grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		.snacks-reveal-cap-ledger-item {
			background: rgba(255, 255, 255, 0.045);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 6px;
			display: grid;
			gap: 2px;
			min-width: 0;
			padding: 7px;
		}

		.snacks-reveal-cap-ledger-item strong {
			font-size: 11px;
			font-weight: 950;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-reveal-cap-ledger-item span {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-reveal-cap-ledger-item-filled {
			background: rgba(24, 224, 168, 0.08);
			border-color: rgba(24, 224, 168, 0.22);
		}

		.snacks-reveal-board {
			display: grid;
			gap: 12px;
		}

		.snacks-reveal-empty {
			align-items: center;
			background: rgba(255, 255, 255, 0.035);
			border: 1px dashed rgba(255, 255, 255, 0.14);
			border-radius: 8px;
			color: #9aa4b2;
			display: flex;
			font-size: 13px;
			font-weight: 850;
			justify-content: center;
			min-height: 116px;
			padding: 14px;
			text-align: center;
		}

		.snacks-reveal-options {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		.snacks-reveal-actions {
			display: flex;
			justify-content: center;
		}

		.snacks-reveal-actions .btn {
			min-width: 220px;
		}

		.snacks-team-reveal-page {
			background: radial-gradient(140% 80% at 50% 0%, #0d141c 0%, #05080c 65%);
			box-sizing: border-box;
			font-family: "Nunito", system-ui, sans-serif;
			min-height: 100vh;
			padding: 26px 20px 40px;
		}

		.snacks-team-reveal-page *,
		.snacks-player-modal-team,
		.snacks-player-modal-team * {
			font-family: "Nunito", system-ui, sans-serif;
		}

		.snacks-team-reveal-page .snacks-header {
			display: block;
			margin: 0 0 18px;
		}

		.snacks-team-reveal-page .snacks-back-link {
			color: #6b7683;
			display: inline-block;
			font-size: 15px;
			font-weight: 800;
			margin-bottom: 18px;
		}

		.snacks-team-reveal-page .snacks-kicker {
			color: #6b7683;
			font-size: 12px;
			font-weight: 800;
			letter-spacing: 0.12em;
			margin: 0 0 2px;
		}

		.snacks-team-reveal-page .snacks-title {
			color: #f4f7fa;
			font-size: 46px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 0.98;
			margin: 0;
		}

		.snacks-team-reveal-page .snacks-year-control {
			margin-top: 8px;
		}

		.snacks-team-reveal-shell {
			background: transparent;
			border: 0;
			border-radius: 0;
			box-shadow: none;
			gap: 22px;
			padding: 0;
		}

		.snacks-team-reveal-card {
			background: linear-gradient(180deg, #101922 0%, #0b131b 100%);
			border: 1px solid #1e2a36;
			border-radius: 20px;
			box-shadow: none;
			display: grid;
			gap: 16px;
			padding: 20px;
		}

		.snacks-team-reveal-card .snacks-reveal-head {
			align-items: flex-start;
		}

		.snacks-team-reveal-card .snacks-reveal-head span {
			color: #7f8b98;
			font-size: 12px;
			font-weight: 800;
			letter-spacing: 0.1em;
		}

		.snacks-team-reveal-card .snacks-reveal-head strong {
			color: #f4f7fa;
			font-size: 30px;
			font-weight: 900;
			letter-spacing: 0;
		}

		.snacks-team-reveal-card .snacks-reveal-tier-pill {
			background: #0f1822;
			border: 1px solid #2a3744;
			border-radius: 40px;
			color: #d6dee6;
			font-size: 16px;
			font-weight: 900;
			letter-spacing: 0;
			padding: 12px 22px;
			text-transform: none;
		}

		.snacks-team-reveal-card .snacks-reveal-slots {
			display: flex;
			gap: 8px;
			justify-content: space-between;
			margin: 2px 0 0;
		}

		.snacks-team-reveal-card .snacks-reveal-slot {
			appearance: none;
			background: transparent;
			border: 0;
			color: inherit;
			cursor: pointer;
			flex: 1;
			font-family: inherit;
			padding: 0;
		}

		.snacks-team-reveal-card .snacks-reveal-slot:disabled {
			cursor: default;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-mark {
			background: transparent;
			border-color: #3a4753;
			color: #5b6773;
			font-size: 17px;
			height: 60px;
			width: 60px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-filled .snacks-reveal-slot-mark {
			background: #0e2b22;
			border-color: #1f9d6b;
			color: #43d69c;
			font-size: 16px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-label {
			color: #7f8b98;
			font-size: 11px;
			font-weight: 800;
			margin-top: 2px;
			min-height: 14px;
		}

		.snacks-reveal-slot-price {
			color: #5b6773;
			font-size: 14px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-reveal-slot-filled .snacks-reveal-slot-price {
			color: #43d69c;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger {
			background: transparent;
			border: 0;
			border-radius: 0;
			border-top: 1px solid #1c2733;
			gap: 12px;
			padding: 16px 0 0;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head span {
			color: #7f8b98;
			font-size: 12px;
			font-weight: 800;
			letter-spacing: 0.1em;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head strong {
			color: #f4f7fa;
			font-size: 32px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head small {
			color: #43d69c;
			font-size: 14px;
			font-weight: 900;
			letter-spacing: 0.06em;
		}

		.snacks-team-reveal-card .snacks-cap-track {
			background: #182029;
			height: 9px;
		}

		.snacks-team-reveal-card .snacks-cap-fill {
			background: linear-gradient(90deg, #22c993 0%, #7ed36a 55%, #f3c14a 100%);
			border-radius: 6px;
		}

		.snacks-team-reveal-page .snacks-reveal-board {
			display: contents;
		}

		.snacks-team-reveal-page .snacks-reveal-options {
			gap: 12px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card {
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 16px;
			gap: 0;
			height: auto;
			min-height: 160px;
			padding: 0;
			text-align: left;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card::before {
			height: 4px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card:hover:not(:disabled) {
			border-color: #37485a;
			transform: translateY(-2px);
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card-top {
			gap: 8px;
			padding: 13px 14px 0;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-name {
			color: #f4f7fa;
			display: block;
			font-size: 17px;
			font-weight: 900;
			line-height: 1.1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-cost {
			background: #0e2018;
			border-color: #1f6b4c;
			color: #43d69c;
			font-size: 13px;
			font-weight: 900;
			height: auto;
			padding: 4px 9px;
			width: auto;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-badges {
			gap: 6px;
			justify-content: flex-start;
			margin-bottom: 12px;
			min-height: 22px;
			padding: 12px 14px 0;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-badge {
			background: #1f9d6b;
			border: 0;
			border-radius: 7px;
			font-size: 12px;
			height: 22px;
			min-width: 24px;
			padding: 0 6px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stats {
			gap: 8px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			padding: 0 14px 14px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat {
			background: #0d151d;
			border-color: #1c2731;
			border-radius: 10px;
			justify-items: start;
			padding: 7px 10px;
			text-align: left;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat span {
			color: #74808d;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.08em;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat strong {
			color: #eef2f6;
			font-size: 20px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-team-reveal-page .snacks-reveal-empty {
			background: #101922;
			border-color: #1f2934;
			border-radius: 40px;
			color: #5c6773;
			font-size: 20px;
			font-weight: 900;
			justify-self: center;
			min-height: auto;
			padding: 20px 60px;
		}

		.snacks-team-reveal-page .snacks-reveal-actions .snacks-advance-button {
			background: #101922;
			border: 1px solid #1f2934;
			box-shadow: none;
			color: #5c6773;
			font-size: 20px;
			padding: 20px 60px;
		}

		.snacks-advance-button {
			background: #18e0a8;
			border: 0;
			border-radius: 28px;
			box-shadow: 0 10px 26px rgba(24, 224, 168, 0.3);
			color: #0a0a0a;
			font-size: 16px;
			font-weight: 900;
			padding: 15px 22px;
		}

		.snacks-advance-button:hover,
		.snacks-advance-button:focus {
			background: #18e0a8;
			color: #0a0a0a;
		}

		.snacks-advance-button:disabled {
			background: #232323;
			box-shadow: none;
			color: #7f8b93;
			cursor: default;
		}

		.snacks-sim-card {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			box-shadow: 0 18px 44px rgba(0, 0, 0, 0.24);
			padding: 16px;
		}

		.snacks-results-page {
			padding-top: 12px;
		}

		.snacks-results-page .snacks-header {
			margin: 0 0 10px;
		}

		.snacks-results-page .snacks-title {
			font-size: 34px;
		}

		.snacks-results-page-sim {
			padding-top: 0;
		}

		.snacks-results-page-sim .snacks-header {
			margin: 0 0 6px;
		}

		.snacks-results-page-sim .snacks-kicker {
			margin-top: 4px;
		}

		.snacks-results-page-sim .snacks-title {
			font-size: 30px;
			line-height: 0.96;
		}

		.snacks-results-page .snacks-sim-card {
			padding: 14px;
		}

		.snacks-results-share {
			align-items: center;
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.085);
			border-radius: 8px;
			display: grid;
			gap: 10px;
			grid-template-columns: minmax(0, 1fr);
			padding: 11px 12px;
		}

		.snacks-results-share strong {
			display: block;
			font-size: 15px;
		}

		.snacks-results-share-actions {
			display: flex;
			gap: 8px;
			justify-content: end;
		}

		.snacks-results-share-actions .btn {
			white-space: nowrap;
		}

		.snacks-share-link-button {
			background: rgba(24, 224, 168, 0.08);
			border-color: rgba(24, 224, 168, 0.72);
			color: #18e0a8;
		}

		.snacks-share-link-button:hover,
		.snacks-share-link-button:focus {
			background: rgba(24, 224, 168, 0.16);
			border-color: #18e0a8;
			color: #18e0a8;
		}

		.snacks-sim-empty {
			align-items: center;
			display: flex;
			gap: 14px;
			justify-content: space-between;
			min-height: 88px;
		}

		.snacks-sim-empty strong {
			font-size: 20px;
		}

		.snacks-sim-muted {
			color: #9aa4b2;
		}

		.snacks-sim-kicker {
			color: #9aa4b2;
			font-size: 11px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-sim-head {
			align-items: end;
			display: grid;
			gap: 16px;
			grid-template-columns: minmax(0, 1fr) minmax(220px, 300px);
		}

		.snacks-sim-title {
			font-size: 40px;
			font-weight: 950;
			letter-spacing: 0;
			line-height: 0.95;
			margin: 4px 0 0;
		}

		.snacks-sim-subtitle {
			color: #c4ccd7;
			margin: 8px 0 0;
		}

		.snacks-sim-final {
			background: rgba(24, 224, 168, 0.08);
			border: 1px solid rgba(24, 224, 168, 0.2);
			border-radius: 8px;
			display: grid;
			gap: 4px;
			padding: 12px;
		}

		.snacks-sim-final strong {
			display: block;
			font-size: 16px;
		}

		.snacks-summary-shell {
			background: transparent;
			border: 0;
			box-shadow: none;
			padding: 0;
		}

		.snacks-summary-shell .snacks-sim-grid {
			margin-top: 26px;
		}

		.snacks-season-summary {
			background: radial-gradient(140% 70% at 50% 0%, #0d141c 0%, #05080c 62%);
			border-radius: 0;
			box-sizing: border-box;
			color: #f4f7fa;
			font-family: "Nunito", system-ui, sans-serif;
			margin: 0 auto;
			max-width: 440px;
			overflow-x: clip;
			padding: 24px 18px 36px;
			width: 100%;
		}

		.snacks-season-summary * {
			font-family: "Nunito", system-ui, sans-serif;
		}

		.snacks-summary-eyebrow,
		.snacks-summary-section-label {
			color: #6b7683;
			font-size: 12px;
			font-weight: 800;
			letter-spacing: 0.14em;
			line-height: 1.2;
			margin: 0 0 10px;
			text-transform: uppercase;
		}

		.snacks-summary-result-card {
			background: linear-gradient(180deg, #17140c 0%, #0f0d07 100%);
			border: 1px solid #2c2a1c;
			border-radius: 18px;
			margin-bottom: 26px;
			min-width: 0;
			padding: 18px;
		}

		.snacks-summary-result-card-champ {
			background: linear-gradient(180deg, #0f1d18 0%, #0b1512 100%);
			border-color: #234034;
		}

		.snacks-summary-result-top {
			align-items: center;
			display: flex;
			gap: 12px;
			justify-content: space-between;
			margin-bottom: 20px;
		}

		.snacks-summary-result-top > div:first-child > span {
			color: #96814f;
			display: block;
			font-size: 11px;
			font-weight: 800;
			letter-spacing: 0.1em;
			margin-bottom: 5px;
			text-transform: uppercase;
		}

		.snacks-summary-record {
			color: #f4f7fa;
			display: block;
			font-size: 42px;
			font-weight: 900;
			letter-spacing: -0.01em;
			line-height: 1;
			white-space: nowrap;
		}

		.snacks-summary-record span {
			color: #5b6773;
		}

		.snacks-summary-result-status {
			text-align: right;
		}

		.snacks-summary-result-status strong {
			background: #2a1f0e;
			border: 1px solid #6e5a2e;
			border-radius: 40px;
			color: #e6b04a;
			display: inline-flex;
			font-size: 14px;
			font-weight: 900;
			padding: 8px 16px;
			white-space: nowrap;
		}

		.snacks-summary-result-card-champ .snacks-summary-result-status strong {
			background: #0e2018;
			border-color: #1f6b4c;
			color: #43d69c;
		}

		.snacks-summary-result-status span {
			color: #7f8b98;
			display: block;
			font-size: 13px;
			font-weight: 800;
			margin-top: 9px;
		}

		.snacks-summary-ladder {
			padding: 0 4px;
			position: relative;
		}

		.snacks-summary-ladder-track {
			background: #2a2416;
			height: 2px;
			left: 30px;
			position: absolute;
			right: 30px;
			top: 13px;
		}

		.snacks-summary-ladder-steps {
			display: flex;
			justify-content: space-between;
			min-width: 0;
			position: relative;
		}

		.snacks-summary-ladder-step {
			flex: 1 1 0;
			min-width: 0;
			text-align: center;
		}

		.snacks-summary-ladder-step > span {
			background: #100d07;
			border: 2px solid #3a3320;
			border-radius: 50%;
			display: block;
			height: 26px;
			margin: 0 auto;
			width: 26px;
		}

		.snacks-summary-ladder-step small {
			color: #6b6350;
			display: block;
			font-size: 10px;
			font-weight: 800;
			line-height: 1.1;
			margin-top: 7px;
		}

		.snacks-summary-ladder-step-reached > span {
			background: #e6b04a;
			border-color: #e6b04a;
		}

		.snacks-summary-ladder-step-reached small {
			color: #e6b04a;
			font-weight: 900;
		}

		.snacks-summary-ladder-step-current > span {
			box-shadow: 0 0 0 4px rgba(230, 176, 74, 0.16);
		}

		.snacks-summary-ladder-step-champ.snacks-summary-ladder-step-reached > span {
			background: #43d69c;
			border-color: #43d69c;
		}

		.snacks-summary-ladder-step-champ.snacks-summary-ladder-step-reached small {
			color: #43d69c;
		}

		.snacks-summary-ladder-step-champ.snacks-summary-ladder-step-current > span {
			box-shadow: 0 0 0 4px rgba(67, 214, 156, 0.16);
		}

		.snacks-summary-caption {
			color: #7f8b98;
			font-size: 12.5px;
			font-weight: 700;
			line-height: 1.35;
			margin: 16px 0 0;
		}

		.snacks-summary-caption strong {
			color: #c7d0da;
			font-weight: 800;
		}

		.snacks-summary-headline {
			color: #f4f7fa;
			font-size: 34px;
			font-weight: 900;
			letter-spacing: -0.015em;
			line-height: 1.04;
			margin: 0 0 14px;
			text-wrap: balance;
		}

		.snacks-summary-score-grid {
			display: grid;
			gap: 10px;
			grid-template-columns: repeat(3, minmax(0, 1fr));
			margin-bottom: 22px;
		}

		.snacks-summary-score-card {
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 14px;
			min-width: 0;
			padding: 12px 12px 13px;
		}

		.snacks-summary-score-card-amber {
			background: #17140c;
			border-color: #2c2a1c;
		}

		.snacks-summary-score-card > span {
			color: #74808d;
			display: block;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.09em;
			margin-bottom: 6px;
			text-transform: uppercase;
		}

		.snacks-summary-score-card-amber > span {
			color: #b39152;
		}

		.snacks-summary-score-card > strong {
			color: #f4f7fa;
			display: block;
			font-size: 26px;
			font-weight: 900;
			line-height: 1;
			margin-bottom: 9px;
		}

		.snacks-summary-mini-bar,
		.snacks-summary-bar {
			background: #182029;
			border-radius: 4px;
			height: 6px;
			overflow: hidden;
		}

		.snacks-summary-mini-bar > div,
		.snacks-summary-bar > div {
			background: linear-gradient(90deg, #22c993, #43d69c);
			border-radius: inherit;
			height: 100%;
		}

		.snacks-summary-score-card-amber .snacks-summary-mini-bar {
			background: #241f12;
		}

		.snacks-summary-score-card-amber .snacks-summary-mini-bar > div,
		.snacks-summary-insight-amber .snacks-summary-bar > div {
			background: linear-gradient(90deg, #d68f2a, #f3c14a);
		}

		.snacks-summary-lead {
			color: #aeb8c3;
			font-size: 15px;
			font-weight: 700;
			line-height: 1.5;
			margin: 0 0 4px;
		}

		.snacks-summary-section-label {
			margin: 26px 0 12px;
		}

		.snacks-summary-section-label-worked {
			color: #6f9f88;
		}

		.snacks-summary-section-label-capped {
			color: #b39152;
		}

		.snacks-summary-insight-stack {
			display: flex;
			flex-direction: column;
			gap: 12px;
		}

		.snacks-summary-insight {
			background: #121b25;
			border: 1px solid #1f2b37;
			border-left: 4px solid #43d69c;
			border-radius: 14px;
			min-width: 0;
			padding: 16px;
		}

		.snacks-summary-insight-amber {
			background: linear-gradient(180deg, #17140c 0%, #120f09 100%);
			border-color: #2c2a1c;
			border-left-color: #e0a93c;
		}

		.snacks-summary-insight h3 {
			color: #f4f7fa;
			font-size: 18px;
			font-weight: 900;
			line-height: 1.15;
			margin: 0 0 6px;
		}

		.snacks-summary-insight p {
			color: #97a2ae;
			font-size: 14.5px;
			font-weight: 700;
			line-height: 1.45;
			margin: 0 0 14px;
		}

		.snacks-summary-insight-amber p {
			color: #a99c82;
		}

		.snacks-summary-insight-metric {
			align-items: baseline;
			display: flex;
			justify-content: space-between;
			margin-bottom: 7px;
		}

		.snacks-summary-insight-metric > span {
			color: #74808d;
			font-size: 11px;
			font-weight: 800;
			letter-spacing: 0.09em;
			text-transform: uppercase;
		}

		.snacks-summary-insight-amber .snacks-summary-insight-metric > span {
			color: #96814f;
		}

		.snacks-summary-insight-metric strong {
			color: #f4f7fa;
			font-size: 15px;
			font-weight: 900;
		}

		.snacks-summary-insight-metric strong span {
			color: #5b6773;
		}

		.snacks-summary-insight-amber .snacks-summary-insight-metric strong span {
			color: #6e5f38;
		}

		.snacks-summary-bar {
			height: 8px;
		}

		.snacks-summary-insight-amber .snacks-summary-bar {
			background: #241f12;
		}

		.snacks-summary-drivers {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}

		.snacks-summary-driver {
			align-items: center;
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 14px;
			display: flex;
			gap: 12px;
			justify-content: space-between;
			min-width: 0;
			padding: 13px 14px 13px 16px;
		}

		.snacks-summary-driver > div:first-child {
			min-width: 0;
		}

		.snacks-summary-driver > div:first-child strong {
			color: #f4f7fa;
			display: block;
			font-size: 17px;
			font-weight: 900;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-summary-driver > div:first-child span {
			color: #7f8b98;
			display: block;
			font-size: 12px;
			font-weight: 800;
			margin-top: 2px;
		}

		.snacks-summary-driver-stats {
			display: flex;
			flex: none;
			gap: 8px;
		}

		.snacks-summary-driver-stats > span {
			background: #0d151d;
			border: 1px solid #1c2731;
			border-radius: 10px;
			display: block;
			min-width: 50px;
			padding: 7px 13px;
			text-align: center;
		}

		.snacks-summary-driver-stats small {
			color: #74808d;
			display: block;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.06em;
			line-height: 1.1;
		}

		.snacks-summary-driver-stats strong {
			color: #eef2f6;
			display: block;
			font-size: 19px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-summary-driver-stats > span:first-child strong {
			color: #43d69c;
		}

		.snacks-summary-next-build {
			background: linear-gradient(180deg, #0f1d18 0%, #0b1512 100%);
			border: 1px solid #234034;
			border-left: 4px solid #43d69c;
			border-radius: 14px;
			margin-top: 24px;
			min-width: 0;
			padding: 16px;
		}

		.snacks-summary-next-build span {
			color: #43d69c;
			display: block;
			font-size: 11px;
			font-weight: 800;
			letter-spacing: 0.12em;
			margin-bottom: 7px;
			text-transform: uppercase;
		}

		.snacks-summary-next-build p {
			color: #eef2f6;
			font-size: 16px;
			font-weight: 800;
			line-height: 1.42;
			margin: 0;
		}

		.snacks-result-spotlight {
			display: grid;
			gap: 10px;
			grid-template-columns: minmax(180px, 0.8fr) minmax(0, 1.2fr);
		}

		.snacks-result-tile {
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.09);
			border-radius: 8px;
			display: grid;
			gap: 5px;
			min-height: 92px;
			padding: 13px 14px;
		}

		.snacks-result-tile span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-result-tile strong {
			font-size: 32px;
			font-weight: 950;
			letter-spacing: 0;
			line-height: 0.95;
		}

		.snacks-result-tile small {
			color: #aeb7c4;
			font-size: 12px;
			font-weight: 750;
			line-height: 1.25;
		}

		.snacks-result-tile-record {
			background: linear-gradient(
				135deg,
				rgba(24, 224, 168, 0.18),
				rgba(255, 255, 255, 0.05)
			);
			border-color: rgba(24, 224, 168, 0.28);
		}

		.snacks-result-tile-round {
			background: linear-gradient(
				135deg,
				rgba(242, 184, 75, 0.16),
				rgba(255, 255, 255, 0.05)
			);
			border-color: rgba(242, 184, 75, 0.26);
		}

		.snacks-season-analysis {
			background: rgba(255, 255, 255, 0.04);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 11px;
			margin-top: 10px;
			padding: 12px;
		}

		.snacks-season-analysis-head strong {
			display: block;
			font-size: 21px;
			font-weight: 950;
			letter-spacing: 0;
			line-height: 1.02;
			margin-top: 3px;
		}

		.snacks-season-analysis-head p {
			color: #c4ccd7;
			font-size: 12px;
			font-weight: 750;
			line-height: 1.35;
			margin: 6px 0 0;
		}

		.snacks-season-analysis-grid {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-season-analysis-column {
			display: grid;
			gap: 7px;
			min-width: 0;
		}

		.snacks-season-analysis-column > span,
		.snacks-season-recommendation span {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 950;
			letter-spacing: 0.08em;
			text-transform: uppercase;
		}

		.snacks-season-insight {
			background: rgba(255, 255, 255, 0.045);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 7px;
			display: grid;
			gap: 4px;
			padding: 9px;
		}

		.snacks-season-insight-worked {
			background: rgba(24, 224, 168, 0.07);
			border-color: rgba(24, 224, 168, 0.2);
		}

		.snacks-season-insight-didnt {
			background: rgba(242, 184, 75, 0.07);
			border-color: rgba(242, 184, 75, 0.2);
		}

		.snacks-season-insight strong {
			font-size: 13px;
			font-weight: 950;
			line-height: 1.1;
		}

		.snacks-season-insight p {
			color: #c4ccd7;
			font-size: 11px;
			font-weight: 750;
			line-height: 1.32;
			margin: 0;
		}

		.snacks-season-insight small {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 900;
			line-height: 1.2;
		}

		.snacks-season-player-notes {
			border-top: 1px solid rgba(255, 255, 255, 0.08);
			color: #aeb7c4;
			display: grid;
			gap: 4px;
			font-size: 11px;
			font-weight: 800;
			line-height: 1.3;
			padding-top: 9px;
		}

		.snacks-season-recommendation {
			background: rgba(24, 224, 168, 0.08);
			border: 1px solid rgba(24, 224, 168, 0.18);
			border-radius: 7px;
			display: grid;
			gap: 4px;
			padding: 10px;
		}

		.snacks-season-recommendation strong {
			font-size: 13px;
			font-weight: 950;
			line-height: 1.28;
		}

		.snacks-season-dashboard {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 8px;
			margin-top: 8px;
			padding: 10px;
		}

			.snacks-dashboard-head,
			.snacks-playoff-dashboard-head,
			.snacks-month-head,
			.snacks-playoff-round-head {
			align-items: center;
			display: flex;
			gap: 10px;
			justify-content: space-between;
			min-width: 0;
		}

			.snacks-dashboard-head strong,
			.snacks-playoff-dashboard-head strong {
				display: block;
				font-size: 17px;
				font-weight: 950;
				line-height: 1.05;
		}

			.snacks-dashboard-head {
				align-items: start;
				display: grid;
				gap: 8px 10px;
				grid-template-areas:
					"title record"
					"actions actions";
				grid-template-columns: minmax(0, 1fr) auto;
			}

			.snacks-dashboard-title-block {
				grid-area: title;
				min-width: 0;
			}

			.snacks-dashboard-actions {
				grid-area: actions;
			}

			.snacks-dashboard-record {
				background: linear-gradient(
					135deg,
					rgba(24, 224, 168, 0.2),
					rgba(255, 255, 255, 0.055)
				);
				border: 1px solid rgba(24, 224, 168, 0.38);
				border-radius: 7px;
				box-shadow:
					0 0 0 1px rgba(24, 224, 168, 0.08),
					0 10px 24px rgba(24, 224, 168, 0.08);
				color: #f8fafc;
				display: grid;
				gap: 1px;
				grid-area: record;
				line-height: 1;
				min-width: 78px;
				padding: 9px 12px;
				text-align: center;
				white-space: nowrap;
			}

		.snacks-dashboard-record span {
			color: #b7f6df;
			font-size: 11px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-dashboard-record strong {
			color: #f8fafc;
			font-size: 20px;
			font-weight: 950;
		}

		.snacks-dashboard-actions {
			align-items: center;
			display: flex;
			flex-wrap: nowrap;
			gap: 7px;
			justify-content: end;
		}

		.snacks-dashboard-actions:not(.snacks-dashboard-actions-unlocked) {
			justify-content: stretch;
		}

		.snacks-dashboard-actions:not(.snacks-dashboard-actions-unlocked)
			.snacks-sim-advance-button {
			flex: 1 1 auto;
		}

		.snacks-dashboard-empty {
			align-items: center;
			background: rgba(255, 255, 255, 0.045);
			border: 1px dashed rgba(255, 255, 255, 0.14);
			border-radius: 8px;
			color: #9aa4b2;
			display: flex;
			font-size: 13px;
			font-weight: 800;
			justify-content: center;
			min-height: 86px;
			padding: 12px;
			text-align: center;
		}

		.snacks-month-grid {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
		}

		.snacks-month-card,
		.snacks-playoff-round-card {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 7px;
			min-width: 0;
			padding: 8px;
		}

		.snacks-month-head strong,
		.snacks-playoff-round-head strong {
			font-size: 15px;
			font-weight: 950;
		}

		.snacks-month-head span,
		.snacks-playoff-round-head small {
			background: rgba(255, 255, 255, 0.08);
			border-radius: 999px;
			color: #f8fafc;
			font-size: 11px;
			font-weight: 900;
			padding: 4px 7px;
			white-space: nowrap;
		}

		.snacks-month-head small,
		.snacks-playoff-round-head span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-score-list {
			display: grid;
			gap: 4px;
			grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
		}

		.snacks-game-score {
			appearance: none;
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 6px;
			color: #f8fafc;
			cursor: pointer;
			display: grid;
			gap: 1px;
			grid-template-columns: auto minmax(0, 1fr);
			font-family: inherit;
			min-width: 0;
			padding: 5px 6px;
			text-align: left;
		}

		.snacks-game-score:hover {
			background: rgba(255, 255, 255, 0.085);
			border-color: rgba(255, 255, 255, 0.16);
		}

		.snacks-game-score > span {
			align-items: center;
			border-radius: 4px;
			display: inline-flex;
			font-size: 9px;
			font-weight: 950;
			grid-row: span 2;
			justify-content: center;
			min-width: 17px;
		}

		.snacks-game-score strong {
			font-size: 11px;
			font-weight: 950;
			line-height: 1;
			white-space: nowrap;
		}

		.snacks-game-score small {
			color: #8f9baa;
			font-size: 8px;
			font-weight: 850;
			line-height: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-game-score-win > span {
			background: rgba(24, 224, 168, 0.16);
			color: #18e0a8;
		}

		.snacks-game-score-loss > span {
			background: rgba(226, 80, 78, 0.16);
			color: #ff7171;
		}

		.snacks-playoff-dashboard {
			border-top: 1px solid rgba(255, 255, 255, 0.08);
			display: grid;
			gap: 8px;
			padding-top: 10px;
		}

		.snacks-playoff-dashboard-head > span,
		.snacks-playoff-empty {
			color: #9aa4b2;
			font-size: 11px;
			font-weight: 800;
			line-height: 1.25;
			text-align: right;
		}

		.snacks-playoff-rounds {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
		}

		.snacks-playoff-score-list {
			grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
		}

		.snacks-sim-stage {
			background: #101720;
			border: 1px solid rgba(255, 255, 255, 0.09);
			border-radius: 8px;
			display: grid;
			gap: 10px;
			min-width: 0;
			padding: 9px;
		}

		.snacks-sim-tabs {
			display: flex;
			gap: 6px;
			min-width: 0;
			overflow-x: auto;
			padding-bottom: 2px;
			scrollbar-width: none;
		}

		.snacks-sim-tabs::-webkit-scrollbar {
			display: none;
		}

		.snacks-sim-tab {
			appearance: none;
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 999px;
			color: #9aa4b2;
			cursor: pointer;
			flex: none;
			font-family: inherit;
			font-size: 11px;
			font-weight: 950;
			padding: 6px 9px;
			white-space: nowrap;
		}

		.snacks-sim-tab:disabled {
			cursor: default;
			opacity: 0.38;
		}

		.snacks-sim-tab-active {
			background: rgba(24, 224, 168, 0.13);
			border-color: rgba(24, 224, 168, 0.42);
			color: #f8fafc;
		}

		.snacks-current-segment {
			display: grid;
			gap: 9px;
			min-width: 0;
		}

		.snacks-current-segment-head {
			align-items: center;
			display: grid;
			gap: 10px;
			grid-template-columns: 32px minmax(0, 1fr) 32px;
		}

		.snacks-current-segment-title {
			text-align: center;
			min-width: 0;
		}

		.snacks-current-segment-title strong {
			display: block;
			font-size: 24px;
			font-weight: 950;
			line-height: 0.95;
		}

		.snacks-current-segment-title span {
			color: #9aa4b2;
			display: block;
			font-size: 11px;
			font-weight: 900;
			margin-top: 4px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-sim-nav-button {
			align-items: center;
			appearance: none;
			background: rgba(255, 255, 255, 0.065);
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 999px;
			color: #f8fafc;
			cursor: pointer;
			display: flex;
			font-family: inherit;
			font-size: 18px;
			font-weight: 950;
			height: 32px;
			justify-content: center;
			width: 32px;
		}

		.snacks-sim-nav-button:disabled {
			cursor: default;
			opacity: 0.32;
		}

		.snacks-current-games {
			display: grid;
			gap: 5px;
			grid-auto-flow: column;
			grid-auto-columns: minmax(64px, 1fr);
			grid-template-columns: repeat(5, minmax(64px, 1fr));
			grid-template-rows: repeat(3, minmax(42px, 1fr));
			min-height: 136px;
			overflow-x: auto;
			padding-bottom: 2px;
		}

		.snacks-current-games .snacks-game-score {
			background: rgba(255, 255, 255, 0.032);
			border-color: rgba(255, 255, 255, 0.055);
			color: #cfd7e3;
			gap: 1px 3px;
			grid-template-columns: 14px minmax(0, 1fr);
			padding: 5px 3px;
		}

		.snacks-current-games .snacks-game-score > span {
			font-size: 8px;
			min-width: 14px;
		}

		.snacks-current-games .snacks-game-score strong {
			color: #e5ebf3;
			font-size: 9px;
		}

		.snacks-current-games .snacks-game-score small {
			font-size: 7px;
		}

		.snacks-current-games .snacks-game-score-active {
			background: rgba(24, 224, 168, 0.12);
			border-color: rgba(24, 224, 168, 0.68);
			box-shadow:
				0 0 0 1px rgba(24, 224, 168, 0.42),
				0 0 18px rgba(24, 224, 168, 0.12);
			color: #f8fafc;
		}

		.snacks-current-games .snacks-game-score-active > span {
			background: #18e0a8;
			color: #06110d;
		}

		.snacks-current-games .snacks-game-score-active small,
		.snacks-current-games .snacks-game-score-active strong {
			color: #f8fafc;
		}

		.snacks-current-games .snacks-game-score-win > span {
			background: rgba(24, 224, 168, 0.09);
			color: #65dfbd;
		}

		.snacks-current-games .snacks-game-score-loss > span {
			background: rgba(226, 80, 78, 0.09);
			color: #df8585;
		}

		.snacks-game-slot-empty {
			background: rgba(255, 255, 255, 0.028);
			border: 1px dashed rgba(255, 255, 255, 0.07);
			border-radius: 6px;
			min-width: 0;
			min-height: 42px;
		}

		.snacks-inline-box-score {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 8px;
			display: grid;
			gap: 10px;
			padding: 10px;
		}

		.snacks-inline-box-empty {
			align-items: center;
			background: rgba(255, 255, 255, 0.025);
			border: 1px dashed rgba(255, 255, 255, 0.08);
			border-radius: 7px;
			color: #9aa4b2;
			display: flex;
			font-size: 10px;
			font-weight: 800;
			justify-content: center;
			min-height: 40px;
			padding: 8px 10px;
			text-align: center;
			text-transform: uppercase;
		}

		.snacks-sim-finish-card {
			background: rgba(24, 224, 168, 0.08);
			border: 1px solid rgba(24, 224, 168, 0.2);
			border-radius: 8px;
			display: grid;
			gap: 4px;
			padding: 11px;
		}

		.snacks-sim-finish-card strong {
			font-size: 16px;
			font-weight: 950;
		}

		.snacks-box-score-modal {
			max-width: 720px;
			width: min(720px, 100%);
		}

		.snacks-box-score-final {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-box-score-final-side-by-side {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-box-score-final div {
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 3px;
			padding: 10px;
		}

		.snacks-box-score-final .snacks-box-score-final-winner {
			background: linear-gradient(
				135deg,
				rgba(24, 224, 168, 0.22),
				rgba(24, 224, 168, 0.08)
			);
			border-color: rgba(24, 224, 168, 0.5);
			box-shadow: inset 0 0 0 1px rgba(24, 224, 168, 0.08);
		}

		.snacks-box-score-final span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-box-score-final strong {
			font-size: 28px;
			font-weight: 950;
			line-height: 1;
		}

		.snacks-box-score-tabs {
			background: rgba(255, 255, 255, 0.055);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 999px;
			display: grid;
			gap: 4px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			padding: 4px;
		}

		.snacks-box-score-tab {
			appearance: none;
			background: transparent;
			border: 0;
			border-radius: 999px;
			color: #9aa4b2;
			cursor: pointer;
			font-family: inherit;
			font-size: 12px;
			font-weight: 950;
			padding: 8px 10px;
		}

		.snacks-box-score-tab-active {
			background: #18e0a8;
			color: #06110d;
		}

		.snacks-box-score-grid {
			display: grid;
			gap: 12px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.snacks-box-score-tab-panel {
			grid-template-columns: minmax(0, 1fr);
		}

		.snacks-box-score-table {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 8px;
			overflow: hidden;
		}

		.snacks-box-score-table-title {
			background: rgba(255, 255, 255, 0.06);
			font-size: 13px;
			font-weight: 950;
			padding: 8px 9px;
		}

		.snacks-box-score-row {
			align-items: center;
			border-top: 1px solid rgba(255, 255, 255, 0.07);
			display: grid;
			gap: 7px;
			grid-template-columns: minmax(0, 1fr) repeat(4, 36px);
			padding: 7px 9px;
			text-align: right;
		}

		.snacks-box-score-row-head {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-box-score-row-head span:first-child {
			text-align: left;
		}

		.snacks-box-score-player {
			display: grid;
			gap: 1px;
			min-width: 0;
			text-align: left;
		}

		.snacks-box-score-player strong {
			font-size: 12px;
			font-weight: 950;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-box-score-player small {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 850;
		}

		.snacks-running-stats-card {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 8px;
			overflow: hidden;
		}

		.snacks-running-stats-head {
			align-items: center;
			background: rgba(255, 255, 255, 0.055);
			display: flex;
			gap: 8px;
			justify-content: space-between;
			padding: 8px 9px;
		}

		.snacks-running-stats-head strong {
			font-size: 14px;
			font-weight: 950;
		}

		.snacks-running-stats-head span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-running-stats-row {
			align-items: center;
			border-top: 1px solid rgba(255, 255, 255, 0.07);
			display: grid;
			gap: 5px;
			grid-template-columns: minmax(72px, 1fr) repeat(5, minmax(32px, 0.28fr));
			padding: 7px 9px;
			text-align: right;
		}

		.snacks-running-stats-row-head {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-running-stats-row-head span:first-child {
			text-align: left;
		}

		.snacks-running-stats-player {
			display: grid;
			gap: 1px;
			min-width: 0;
			text-align: left;
		}

		.snacks-running-stats-player strong {
			font-size: 12px;
			font-weight: 950;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-running-stats-player small {
			color: #9aa4b2;
			font-size: 8px;
			font-weight: 850;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-running-stats-row:not(.snacks-running-stats-row-head) > span:not(:first-child) {
			font-size: 11px;
			font-weight: 950;
		}

		.snacks-standings-card {
			background: rgba(255, 255, 255, 0.035);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 8px;
			overflow: hidden;
		}

		.snacks-standings-tabs {
			margin: 8px 9px 6px;
		}

		.snacks-standings-scroll {
			max-height: 318px;
			overflow: auto;
		}

		.snacks-standings-row {
			align-items: center;
			border-top: 1px solid rgba(255, 255, 255, 0.07);
			display: grid;
			gap: 6px;
			grid-template-columns: 26px minmax(0, 1fr) 54px 38px;
			padding: 7px 9px;
			text-align: right;
		}

		.snacks-standings-row-head {
			color: #9aa4b2;
			font-size: 9px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-standings-row-head span:nth-child(2) {
			text-align: left;
		}

		.snacks-standings-row-user {
			background: linear-gradient(
				90deg,
				rgba(24, 224, 168, 0.16),
				rgba(24, 224, 168, 0.04)
			);
			box-shadow: inset 3px 0 0 rgba(24, 224, 168, 0.82);
		}

		.snacks-standings-row:not(.snacks-standings-row-head) > span {
			font-size: 11px;
			font-weight: 950;
		}

		.snacks-standings-team {
			display: grid;
			gap: 1px;
			min-width: 0;
			text-align: left;
		}

		.snacks-standings-team strong {
			font-size: 12px;
			font-weight: 950;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-standings-team small {
			color: #9aa4b2;
			font-size: 8px;
			font-weight: 850;
			text-transform: uppercase;
		}

		.snacks-team-ratings {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(3, minmax(0, 1fr));
			margin-bottom: 14px;
		}

		.snacks-team-rating {
			background: rgba(255, 255, 255, 0.045);
			border: 1px solid rgba(255, 255, 255, 0.075);
			border-radius: 7px;
			display: grid;
			gap: 3px;
			min-width: 0;
			padding: 8px 10px;
		}

		.snacks-team-rating span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-team-rating strong {
			font-size: 20px;
			line-height: 1;
		}

		.snacks-team-heat-map {
			background: rgba(255, 255, 255, 0.04);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: grid;
			gap: 8px;
			margin-bottom: 14px;
			padding: 10px;
		}

		.snacks-team-heat-map-head {
			align-items: center;
			display: flex;
			gap: 8px;
			justify-content: space-between;
		}

		.snacks-team-heat-map-head strong {
			font-size: 15px;
		}

		.snacks-team-heat-map-grid {
			display: grid;
			gap: 6px;
			grid-template-columns: repeat(5, minmax(0, 1fr));
		}

		.snacks-team-heat-cell {
			border: 1px solid transparent;
			border-radius: 7px;
			color: #ffffff;
			display: grid;
			gap: 2px;
			min-height: 68px;
			padding: 8px 7px;
			text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
		}

		.snacks-team-heat-cell span {
			font-size: 9px;
			font-weight: 950;
			text-transform: uppercase;
		}

		.snacks-team-heat-cell strong {
			font-size: 17px;
			line-height: 1;
		}

		.snacks-team-heat-cell small {
			color: rgba(255, 255, 255, 0.82);
			font-size: 9px;
			font-weight: 900;
			line-height: 1.05;
			text-transform: uppercase;
		}

		.snacks-sim-grid {
			border-top: 1px solid rgba(255, 255, 255, 0.1);
			display: grid;
			gap: 18px;
			grid-template-columns: minmax(0, 1.15fr) minmax(260px, 0.85fr);
			margin-top: 14px;
			padding-top: 14px;
		}

		.snacks-section-title {
			font-size: 16px;
			font-weight: 900;
			margin: 0 0 10px;
		}

		.snacks-player-line,
		.snacks-leaderboard-row {
			align-items: center;
			border-top: 1px solid rgba(255, 255, 255, 0.075);
			display: grid;
			gap: 8px;
			min-width: 0;
			padding: 8px 0;
		}

		.snacks-player-line {
			grid-template-columns: minmax(110px, 1fr) repeat(3, minmax(46px, 0.32fr));
		}

		.snacks-player-line-name {
			display: grid;
			gap: 2px;
			min-width: 0;
		}

		.snacks-player-line-name strong,
		.snacks-leaderboard-name {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-player-line-name span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-stat-cell {
			text-align: right;
		}

		.snacks-stat-cell span {
			color: #9aa4b2;
			display: block;
			font-size: 9px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-stat-cell strong {
			font-size: 14px;
		}

		.snacks-leaderboard-row {
			color: inherit;
			grid-template-columns: 34px minmax(0, 1fr) 76px 52px;
			text-decoration: none;
		}

		a.snacks-leaderboard-row:hover {
			background: rgba(255, 255, 255, 0.045);
		}

		.snacks-leaderboard-rank {
			align-items: center;
			background: rgba(255, 255, 255, 0.07);
			border-radius: 999px;
			display: inline-flex;
			font-size: 12px;
			font-weight: 900;
			height: 26px;
			justify-content: center;
			width: 26px;
		}

		.snacks-leaderboard-user {
			color: #18e0a8;
		}

		.snacks-leaderboard-active {
			color: #ffffff;
		}

		.snacks-leaderboard-active .snacks-leaderboard-rank {
			background: #18e0a8;
			color: #06110d;
		}

		.snacks-competitors {
			color: #9aa4b2;
			font-size: 12px;
			line-height: 1.35;
			margin-top: 12px;
		}

		.snacks-waiting-card {
			align-items: center;
			background: rgba(255, 255, 255, 0.045);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 8px;
			display: flex;
			gap: 10px;
			margin-top: 10px;
			padding: 11px;
		}

		.snacks-waiting-card strong {
			display: block;
			font-size: 14px;
		}

		.snacks-waiting-dot {
			background: #18e0a8;
			border-radius: 999px;
			box-shadow: 0 0 0 0 rgba(24, 224, 168, 0.45);
			display: inline-block;
			height: 10px;
			width: 10px;
			animation: snacksPulse 1.8s ease-out infinite;
		}

		.snacks-copy-row {
			align-items: center;
			display: flex;
			gap: 10px;
			justify-content: space-between;
			margin-top: 16px;
		}

		.snacks-copy-row.snacks-copy-row-single {
			align-items: stretch;
			display: grid;
			grid-template-columns: minmax(0, 1fr);
			justify-content: stretch;
			width: 100%;
		}

		.snacks-copy-row button {
			white-space: nowrap;
		}

		.snacks-copy-actions {
			display: flex;
			gap: 8px;
			justify-content: end;
		}

		.snacks-copy-actions-single {
			display: grid;
			grid-template-columns: minmax(0, 1fr);
			justify-content: stretch;
			width: 100%;
		}

		.snacks-new-challenge-button {
			align-items: center;
			display: flex;
			justify-content: center;
			max-width: none;
			text-align: center;
			width: 100%;
		}

		.snacks-money-warning {
			color: #f2b84b;
		}

		.snacks-modal-backdrop {
			align-items: center;
			background: rgba(0, 0, 0, 0.66);
			display: flex;
			inset: 0;
			justify-content: center;
			padding: 18px;
			position: fixed;
			z-index: 1060;
		}

		.snacks-player-modal-team-backdrop {
			backdrop-filter: blur(6px);
			background: rgba(3, 6, 10, 0.7);
			padding: 24px;
		}

		.snacks-top-drawer-backdrop {
			align-items: flex-start;
			background: rgba(0, 0, 0, 0.38);
			padding: calc(10px + env(safe-area-inset-top)) 12px 12px;
		}

		.snacks-player-modal {
			background: #111821;
			border: 1px solid rgba(255, 255, 255, 0.14);
			border-radius: 8px;
			box-shadow: 0 24px 70px rgba(0, 0, 0, 0.5);
			color: #f8fafc;
			display: grid;
			gap: 14px;
			max-width: 420px;
			padding: 18px;
			touch-action: pan-y;
			width: min(420px, 100%);
		}

		.snacks-player-modal-team {
			background: linear-gradient(180deg, #101922 0%, #0a121a 100%);
			border: 1px solid #1e2a36;
			border-radius: 20px;
			box-shadow: 0 40px 90px -30px rgba(0, 0, 0, 0.85);
			gap: 12px;
			max-width: 400px;
			padding: 16px;
			position: relative;
			width: min(400px, 100%);
		}

		.snacks-top-drawer {
			animation: snacksDrawerDown 0.22s cubic-bezier(0.2, 0.9, 0.3, 1) both;
			border-radius: 0 0 10px 10px;
			width: min(520px, 100%);
		}

		.snacks-invite-modal {
			max-width: 460px;
		}

		.snacks-link-copied-modal {
			max-width: 420px;
		}

		.snacks-link-copied-modal .btn {
			justify-self: end;
		}

		.snacks-invite-actions {
			display: flex;
			gap: 8px;
			justify-content: end;
		}

		.snacks-copy-invite-button {
			align-items: center;
			display: inline-flex;
			gap: 7px;
		}

		.snacks-copy-invite-button svg {
			flex: none;
		}

		.snacks-handle-modal {
			max-width: 460px;
		}

		.snacks-handle-cells {
			cursor: text;
			display: flex;
			gap: 8px;
			justify-content: center;
			margin: 4px 0 2px;
			position: relative;
		}

		.snacks-handle-input {
			background: transparent;
			border: 0;
			caret-color: transparent;
			color: transparent;
			inset: 0;
			opacity: 0;
			position: absolute;
			width: 100%;
		}

		.snacks-handle-cell {
			align-items: center;
			background: #141c26;
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 8px;
			color: #f8fafc;
			display: flex;
			font-size: 34px;
			font-weight: 950;
			height: 62px;
			justify-content: center;
			line-height: 1;
			width: 52px;
		}

		.snacks-handle-cell-filled,
		.snacks-handle-cell-active {
			background: rgba(24, 224, 168, 0.08);
			border-color: #18e0a8;
			color: #18e0a8;
		}

		.snacks-handle-caret {
			animation: snacksCaret 1s step-end infinite;
			background: #18e0a8;
			height: 32px;
			width: 3px;
		}

		.snacks-player-modal-head {
			align-items: start;
			display: flex;
			gap: 14px;
			justify-content: space-between;
		}

		.snacks-player-modal-team .snacks-player-modal-head {
			display: block;
			padding-right: 44px;
		}

		.snacks-player-modal-team .snacks-sim-kicker {
			color: #7f8b98;
			font-size: 12px;
			font-weight: 800;
			letter-spacing: 0.06em;
		}

		.snacks-player-modal-team .snacks-sim-kicker span {
			color: #43d69c;
		}

		.snacks-player-modal-name {
			font-size: 28px;
			font-weight: 950;
			line-height: 0.95;
			margin: 4px 0 0;
		}

		.snacks-player-modal-team .snacks-player-modal-name {
			color: #f4f7fa;
			font-size: 27px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 1.05;
		}

		.snacks-player-modal-close {
			background: rgba(255, 255, 255, 0.08);
			border: 1px solid rgba(255, 255, 255, 0.12);
			border-radius: 6px;
			color: #f8fafc;
			font-weight: 900;
			height: 32px;
			width: 32px;
		}

		.snacks-player-modal-team .snacks-player-modal-close {
			background: #121c26;
			border-color: #263341;
			border-radius: 11px;
			color: #cfd8e0;
			font-size: 15px;
			height: 36px;
			position: absolute;
			right: 14px;
			top: 14px;
			width: 36px;
		}

		.snacks-player-modal-vitals {
			background: #1c2733;
			border: 1px solid #1c2733;
			border-radius: 11px;
			display: grid;
			gap: 1px;
			grid-template-columns: repeat(3, minmax(0, 1fr));
			overflow: hidden;
		}

		.snacks-player-modal-vitals div {
			background: #0e1720;
			display: grid;
			gap: 2px;
			min-width: 0;
			padding: 8px 11px;
		}

		.snacks-player-modal-vitals span {
			color: #6f7c8a;
			font-size: 9px;
			font-weight: 800;
			letter-spacing: 0.1em;
			text-transform: uppercase;
		}

		.snacks-player-modal-vitals strong {
			color: #eef2f6;
			font-size: 13px;
			font-weight: 800;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-player-modal-badges {
			align-items: center;
			display: flex;
			gap: 10px;
		}

		.snacks-player-modal-badges > span {
			color: #6f7c8a;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.1em;
			text-transform: uppercase;
		}

		.snacks-player-modal-badges div {
			display: flex;
			gap: 7px;
			min-height: 24px;
		}

		.snacks-player-modal-badges strong {
			align-items: center;
			background: #1f9d6b;
			border-radius: 7px;
			color: #fff;
			display: flex;
			font-size: 13px;
			font-weight: 900;
			height: 24px;
			justify-content: center;
			min-width: 26px;
			padding: 0 7px;
		}

		.snacks-player-modal-nav {
			align-items: center;
			display: grid;
			grid-template-columns: 44px 1fr 44px;
			gap: 10px;
		}

		.snacks-player-modal-nav-button {
			align-items: center;
			background: rgba(255, 255, 255, 0.07);
			border: 1px solid rgba(255, 255, 255, 0.14);
			border-radius: 999px;
			color: #f8fafc;
			display: flex;
			font-size: 22px;
			font-weight: 950;
			height: 40px;
			justify-content: center;
			line-height: 1;
			width: 40px;
		}

		.snacks-player-modal-team .snacks-player-modal-nav {
			grid-template-columns: 56px 1fr 56px;
		}

		.snacks-player-modal-team .snacks-player-modal-nav-button {
			background: #101a24;
			border-color: #283542;
			color: #dfe6ec;
			height: 56px;
			width: 56px;
		}

		.snacks-player-modal-nav-count {
			color: #9aa4b2;
			font-size: 13px;
			font-weight: 950;
			text-align: center;
			text-transform: uppercase;
		}

		.snacks-player-modal-team .snacks-player-modal-nav-count {
			color: #9aa6b2;
			font-size: 22px;
			font-weight: 800;
			letter-spacing: 0;
			text-transform: none;
		}

		.snacks-player-modal-stats {
			display: grid;
			gap: 8px;
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		.snacks-player-modal-team .snacks-player-modal-stats {
			gap: 7px;
		}

		.snacks-player-modal-stat {
			background: rgba(255, 255, 255, 0.055);
			border-radius: 6px;
			display: grid;
			gap: 2px;
			padding: 9px;
		}

		.snacks-player-modal-team .snacks-player-modal-stat {
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 11px;
			gap: 3px;
			padding: 9px 11px;
			text-align: left;
		}

		.snacks-player-modal-stat span {
			color: #9aa4b2;
			font-size: 10px;
			font-weight: 900;
			text-transform: uppercase;
		}

		.snacks-player-modal-team .snacks-player-modal-stat span {
			color: #74808d;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.08em;
		}

		.snacks-player-modal-stat strong {
			font-size: 18px;
		}

		.snacks-player-modal-team .snacks-player-modal-stat strong {
			color: #f4f7fa;
			font-size: 20px;
			font-weight: 900;
			line-height: 1;
		}

		.snacks-player-modal-skill-bars {
			display: grid;
			column-gap: 14px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			padding: 2px 0;
			row-gap: 9px;
		}

		.snacks-skill-bar {
			display: grid;
			gap: 5px;
		}

		.snacks-skill-bar-head {
			align-items: baseline;
			display: flex;
			gap: 8px;
			justify-content: space-between;
		}

		.snacks-skill-bar-label {
			align-items: center;
			display: flex;
			gap: 7px;
			min-width: 0;
		}

		.snacks-skill-bar-label i {
			align-items: center;
			background: #141d27;
			border: 1px solid #26313d;
			border-radius: 7px;
			color: #56626f;
			display: flex;
			flex: none;
			font-size: 12px;
			font-style: normal;
			font-weight: 900;
			height: 22px;
			justify-content: center;
			line-height: 1;
			width: 22px;
		}

		.snacks-skill-bar-label span,
		.snacks-skill-bar-head strong {
			color: #eef2f6;
			font-size: 12px;
			font-weight: 900;
			line-height: 1;
		}

		.snacks-skill-bar-head strong {
			font-size: 13px;
		}

		.snacks-skill-bar-track {
			background: #182029;
			border-radius: 5px;
			height: 6px;
			overflow: hidden;
		}

		.snacks-skill-bar-fill {
			background: linear-gradient(90deg, #22c993 0%, #7ed36a 58%, #f3c14a 100%);
			border-radius: 5px;
			height: 100%;
		}

		.snacks-player-modal-actions {
			display: grid;
			gap: 10px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			margin-top: 4px;
		}

		.snacks-player-modal-team .snacks-player-modal-actions {
			align-items: stretch;
			display: flex;
			gap: 10px;
			margin-top: 2px;
		}

		.snacks-player-modal-team .snacks-player-modal-actions .btn {
			border-radius: 13px;
			flex: 0 0 38%;
			font-size: 16px;
			font-weight: 800;
			min-height: 48px;
		}

		.snacks-player-modal-team .snacks-player-modal-actions .snacks-advance-button {
			background: linear-gradient(180deg, #2fd08a 0%, #23b978 100%);
			border: 0;
			border-radius: 40px;
			box-shadow: 0 12px 26px -10px rgba(47, 208, 138, 0.6);
			color: #05231a;
			flex: 1;
			font-size: 16px;
			font-weight: 900;
		}

		.snacks-player-modal-team
			.snacks-player-modal-actions
			.snacks-advance-button:disabled {
			background: #121b24;
			border: 1px solid #1f2934;
			box-shadow: none;
			color: #5c6773;
		}

		@keyframes snacksDrawerDown {
			from {
				opacity: 0;
				transform: translateY(-24px);
			}
			to {
				opacity: 1;
				transform: translateY(0);
			}
		}

		@keyframes snacksPulse {
			0% {
				box-shadow: 0 0 0 0 rgba(24, 224, 168, 0.45);
			}
			70% {
				box-shadow: 0 0 0 7px rgba(24, 224, 168, 0);
			}
			100% {
				box-shadow: 0 0 0 0 rgba(24, 224, 168, 0);
			}
		}

		@keyframes snacksCaret {
			0%,
			49% {
				opacity: 1;
			}
			50%,
			100% {
				opacity: 0;
			}
		}

		@media (max-width: 860px) {
			.snacks-header {
				align-items: stretch;
				grid-template-columns: minmax(0, 1fr);
			}

			.snacks-sim-head,
			.snacks-sim-grid,
			.snacks-box-score-grid {
				grid-template-columns: minmax(0, 1fr);
			}
		}

		@media (max-width: 520px) {
			.container.snacks-page {
				padding-left: 6px;
				padding-right: 6px;
			}

			.container.snacks-page.snacks-team-reveal-page {
				padding: 26px 20px 40px;
			}

			.snacks-results-page {
				overflow-x: clip;
				padding-bottom: 86px;
			}

			.snacks-header {
				gap: 10px;
				margin: 10px 0;
			}

			.snacks-title {
				font-size: 32px;
			}

			.snacks-cap-panel {
				gap: 7px;
				padding: 9px 10px;
			}

			.snacks-cap-spent {
				font-size: 22px;
			}

			.snacks-game-flow {
				gap: 10px;
			}

			.snacks-board {
				gap: 4px;
				padding: 4px;
			}

			.snacks-tier-row {
				gap: 4px;
				grid-template-columns: 30px repeat(5, minmax(0, 1fr));
			}

			.snacks-tier-label {
				border-radius: 5px;
				height: 74px;
			}

			.snacks-tier-label strong {
				font-size: 15px;
			}

			.snacks-tier-label span {
				font-size: 7px;
				margin-top: 5px;
			}

			.snacks-player-card {
				border-radius: 5px;
				gap: 3px;
				height: 74px;
				padding: 5px 4px;
			}

			.snacks-player-card::before {
				height: 2px;
			}

			.snacks-player-card-top {
				display: grid;
				gap: 3px;
			}

			.snacks-player-name {
				font-size: 10.5px;
				line-height: 1;
				-webkit-line-clamp: 1;
			}

			.snacks-player-cost {
				font-size: 8px;
				height: 18px;
				width: 23px;
			}

			.snacks-player-name-default {
				display: none;
			}

			.snacks-player-name-phone {
				display: inline;
			}

			.snacks-player-meta {
				font-size: 8px;
			}

			.snacks-player-badges {
				gap: 2px;
				justify-content: center;
				min-height: 12px;
			}

			.snacks-player-badge {
				border-radius: 3px;
				font-size: 7px;
				height: 12px;
				min-width: 12px;
				padding: 0 2px;
			}

			.snacks-player-stats {
				gap: 2px;
				grid-template-columns: repeat(2, minmax(0, 1fr));
			}

			.snacks-player-stat {
				border-radius: 4px;
				gap: 1px;
				padding: 3px 2px;
			}

			.snacks-player-stat span {
				font-size: 8px;
			}

			.snacks-player-stat strong {
				font-size: 11.5px;
			}

			.snacks-player-stat-pts {
				display: none;
			}

			.snacks-team-locker {
				gap: 10px;
				padding: 10px;
				position: static;
			}

			.snacks-team-slots {
				gap: 4px;
			}

			.snacks-team-slot {
				min-height: 62px;
				padding: 5px 4px;
			}

			.snacks-team-slot strong {
				font-size: 10px;
			}

			.snacks-view-player {
				font-size: 8px;
				padding: 2px 4px;
			}

			.snacks-team-actions {
				align-items: stretch;
				display: grid;
			}

			.snacks-team-actions-buttons {
				display: grid;
				grid-template-columns: 1fr 1fr;
			}

			.snacks-sim-card {
				padding: 12px;
			}

			.snacks-sim-empty {
				align-items: start;
				display: grid;
				min-height: 0;
			}

			.snacks-results-share {
				align-items: stretch;
				grid-template-columns: minmax(0, 1fr);
			}

			.snacks-results-share-actions {
				display: grid;
				grid-template-columns: 1fr 1fr;
			}

			.snacks-result-spotlight {
				grid-template-columns: repeat(2, minmax(0, 1fr));
			}

			.snacks-result-tile {
				min-height: 76px;
				padding: 8px;
			}

			.snacks-result-tile strong {
				font-size: 24px;
			}

			.snacks-result-tile small {
				font-size: 10px;
			}

			.snacks-season-analysis-grid {
				grid-template-columns: minmax(0, 1fr);
			}

			.snacks-season-analysis-head strong {
				font-size: 18px;
			}

			.snacks-season-summary {
				max-width: 100%;
				padding: 20px 14px 32px;
			}

			.snacks-summary-result-card {
				padding: 16px;
			}

			.snacks-summary-result-top {
				gap: 10px;
			}

			.snacks-summary-record {
				font-size: 38px;
			}

			.snacks-summary-result-status strong {
				font-size: 12px;
				padding: 7px 12px;
			}

			.snacks-summary-result-status span {
				font-size: 11px;
			}

			.snacks-summary-ladder-track {
				left: 16px;
				right: 16px;
			}

			.snacks-summary-ladder-step > span {
				height: 22px;
				width: 22px;
			}

			.snacks-summary-ladder-step small {
				font-size: 9px;
			}

			.snacks-summary-headline {
				font-size: 31px;
			}

			.snacks-summary-score-grid {
				gap: 8px;
			}

			.snacks-summary-score-card {
				padding: 11px 9px 12px;
			}

			.snacks-summary-score-card > strong {
				font-size: 24px;
			}

			.snacks-summary-insight h3 {
				font-size: 17px;
			}

			.snacks-summary-insight p,
			.snacks-summary-lead {
				font-size: 14px;
			}

			.snacks-summary-driver {
				padding: 12px;
			}

			.snacks-summary-driver-stats > span {
				min-width: 44px;
				padding: 7px 10px;
			}

			.snacks-dashboard-head {
				align-items: start;
			}

			.snacks-dashboard-actions {
				justify-content: stretch;
			}

			.snacks-dashboard-actions:not(.snacks-dashboard-actions-unlocked) {
				background: rgba(8, 13, 18, 0.94);
				border-top: 1px solid rgba(255, 255, 255, 0.12);
				bottom: 0;
				box-shadow: 0 -16px 34px rgba(0, 0, 0, 0.38);
				left: 0;
				padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
				position: fixed;
				right: 0;
				z-index: 1050;
			}

			.snacks-dashboard-actions .btn {
				flex: 1;
			}

			.snacks-dashboard-actions:not(.snacks-dashboard-actions-unlocked)
				.snacks-sim-advance-button {
				font-size: 16px;
				min-height: 52px;
				padding: 15px;
			}

			.snacks-dashboard-record {
				min-width: 88px;
				text-align: center;
			}

			.snacks-score-list {
				grid-template-columns: repeat(auto-fill, minmax(68px, 1fr));
			}

			.snacks-box-score-final {
				grid-template-columns: repeat(2, minmax(0, 1fr));
			}

			.snacks-box-score-row {
				grid-template-columns: minmax(0, 1fr) repeat(4, 30px);
				padding: 6px 7px;
			}

			.snacks-team-ratings {
				grid-template-columns: repeat(3, minmax(0, 1fr));
			}

			.snacks-team-heat-map-grid {
				grid-template-columns: repeat(5, minmax(52px, 1fr));
				overflow-x: auto;
			}

			.snacks-team-heat-cell {
				min-height: 58px;
				padding: 7px 6px;
			}

			.snacks-team-heat-cell strong {
				font-size: 14px;
			}

			.snacks-player-line {
				grid-template-columns: minmax(92px, 1fr) repeat(3, minmax(38px, 0.32fr));
			}

			.snacks-stat-cell strong {
				font-size: 12px;
			}

			.snacks-leaderboard-row {
				font-size: 12px;
				grid-template-columns: 28px minmax(0, 1fr) 64px 46px;
			}

			.snacks-copy-row {
				align-items: stretch;
				display: grid;
			}

			.snacks-copy-actions {
				display: grid;
			}

			.snacks-player-modal {
				padding: 14px;
			}

			.snacks-player-modal-name {
				font-size: 24px;
			}

			.snacks-reveal-stage {
				padding: 10px;
			}

			.snacks-reveal-head strong {
				font-size: 22px;
			}

			.snacks-reveal-slots {
				gap: 6px;
			}

			.snacks-reveal-slot-mark {
				font-size: 13px;
				width: min(54px, 100%);
			}

			.snacks-reveal-slot-label {
				font-size: 10px;
			}

			.snacks-reveal-options {
				gap: 6px;
				grid-template-columns: repeat(5, minmax(58px, 1fr));
				overflow-x: auto;
			}

			.snacks-reveal-options .snacks-player-card {
				height: 104px;
				padding: 6px 4px;
			}
		}

		.container.snacks-page.snacks-team-reveal-page {
			background: radial-gradient(140% 80% at 50% 0%, #0d141c 0%, #05080c 65%);
			box-sizing: border-box;
			color: #f4f7fa;
			font-family: "Nunito", system-ui, sans-serif;
			max-width: min(1180px, 100vw);
			min-height: 100vh;
			padding: 21px clamp(16px, 4vw, 32px) 33px;
		}

		.snacks-team-reveal-page *,
		.snacks-player-modal-team,
		.snacks-player-modal-team * {
			box-sizing: border-box;
			font-family: "Nunito", system-ui, sans-serif;
		}

		.snacks-team-reveal-page .snacks-header {
			display: block;
			margin: 0 0 15px;
		}

		.snacks-team-reveal-page .snacks-back-link {
			color: #6b7683;
			display: inline-block;
			font-size: 12px;
			font-weight: 800;
			line-height: 1.2;
			margin-bottom: 15px;
			text-decoration: none;
		}

		.snacks-team-reveal-page .snacks-back-link::before {
			content: "‹ ";
		}

		.snacks-team-reveal-page .snacks-kicker {
			color: #6b7683;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.12em;
			line-height: 1.2;
			margin: 0 0 2px;
			text-transform: uppercase;
		}

		.snacks-team-reveal-page .snacks-title {
			color: #f4f7fa;
			font-size: 34px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 0.98;
			margin: 0;
		}

		.snacks-team-mode-screen {
			display: grid;
			gap: 20px;
			margin: 0 auto;
			max-width: 520px;
		}

		.snacks-team-mode-header {
			display: grid;
			gap: 6px;
		}

		.snacks-team-mode-header .snacks-back-link {
			margin-bottom: 14px;
		}

		.snacks-team-mode-header p {
			color: #9aa4b2;
			font-size: 14px;
			font-weight: 700;
			line-height: 1.35;
			margin: 6px 0 0;
		}

		.snacks-team-mode-options {
			display: grid;
			gap: 10px;
		}

		.snacks-team-mode-card {
			align-items: start;
			appearance: none;
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 16px;
			color: #f4f7fa;
			cursor: pointer;
			display: grid;
			font-family: inherit;
			gap: 12px;
			grid-template-columns: 42px minmax(0, 1fr) 20px;
			padding: 14px;
			text-align: left;
			transition:
				background 160ms ease,
				border-color 160ms ease,
				transform 160ms ease;
		}

		.snacks-team-mode-card:hover {
			border-color: #314353;
			transform: translateY(-1px);
		}

		.snacks-team-mode-card-selected {
			background: linear-gradient(180deg, rgba(20, 52, 39, 0.72), rgba(10, 28, 21, 0.64));
			border-color: #24d895;
			box-shadow: inset 0 0 0 1px rgba(36, 216, 149, 0.08);
		}

		.snacks-team-mode-icon {
			align-items: center;
			background: #0d151d;
			border: 1px solid #23313d;
			border-radius: 14px;
			color: #9aa4b2;
			display: flex;
			font-size: 15px;
			font-weight: 900;
			height: 42px;
			justify-content: center;
			line-height: 1;
			width: 42px;
		}

		.snacks-team-mode-card-selected .snacks-team-mode-icon {
			background: #2fd08a;
			border-color: #2fd08a;
			color: #05231a;
		}

		.snacks-team-mode-copy {
			display: grid;
			gap: 5px;
			min-width: 0;
		}

		.snacks-team-mode-copy strong {
			color: #f4f7fa;
			display: block;
			font-size: 18px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 1.05;
		}

		.snacks-team-mode-copy small {
			color: #7f8b98;
			display: block;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.11em;
			line-height: 1.2;
			text-transform: uppercase;
		}

		.snacks-team-mode-copy span {
			color: #a9b3bf;
			display: block;
			font-size: 13px;
			font-weight: 700;
			line-height: 1.38;
		}

		.snacks-team-mode-radio {
			background: transparent;
			border: 3px solid #405060;
			border-radius: 50%;
			display: block;
			height: 20px;
			margin-top: 2px;
			position: relative;
			width: 20px;
		}

		.snacks-team-mode-card-selected .snacks-team-mode-radio {
			background: #2fd08a;
			border-color: #2fd08a;
		}

		.snacks-team-mode-card-selected .snacks-team-mode-radio::after {
			background: #05231a;
			border-radius: 50%;
			content: "";
			height: 7px;
			left: 50%;
			position: absolute;
			top: 50%;
			transform: translate(-50%, -50%);
			width: 7px;
		}

		.snacks-team-mode-start {
			border-radius: 48px;
			font-size: 16px;
			min-height: 52px;
			width: 100%;
		}

		.snacks-team-mode-note {
			color: #5f6b76;
			font-size: 11px;
			font-weight: 800;
			letter-spacing: 0.12em;
			text-align: center;
			text-transform: uppercase;
		}

		.snacks-team-reveal-page .snacks-game-flow {
			gap: 0;
		}

		.snacks-team-reveal-shell {
			background: transparent;
			border: 0;
			border-radius: 0;
			box-shadow: none;
			display: grid;
			gap: 18px;
			margin: 0 auto;
			max-width: 1120px;
			padding: 0;
			width: 100%;
		}

		.snacks-team-reveal-card {
			background: linear-gradient(180deg, #101922 0%, #0b131b 100%);
			border: 1px solid #1e2a36;
			border-radius: 16px;
			box-shadow: none;
			display: grid;
			gap: 0;
			padding: 16px;
		}

		.snacks-team-reveal-card .snacks-reveal-head {
			align-items: flex-start;
			display: flex;
			gap: 10px;
			justify-content: space-between;
			margin: 0 0 18px;
		}

		.snacks-team-reveal-card .snacks-reveal-head span {
			color: #7f8b98;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.1em;
			line-height: 1.2;
			margin: 0 0 2px;
			text-transform: uppercase;
		}

		.snacks-team-reveal-card .snacks-reveal-head strong {
			color: #f4f7fa;
			display: block;
			font-size: 25px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 1.05;
		}

		.snacks-team-reveal-card .snacks-reveal-tier-pill {
			background: #0f1822;
			border: 1px solid #2a3744;
			border-radius: 33px;
			color: #d6dee6;
			flex: none;
			font-size: 13px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 1.2;
			padding: 10px 18px;
			text-transform: none;
			white-space: nowrap;
		}

		.snacks-team-reveal-card .snacks-reveal-slots {
			display: flex;
			gap: 7px;
			justify-content: space-between;
			margin: 0 0 16px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot {
			appearance: none;
			background: transparent;
			border: 0;
			color: inherit;
			cursor: pointer;
			display: block;
			flex: 1;
			font-family: inherit;
			min-width: 0;
			padding: 0;
			text-align: center;
		}

		.snacks-team-reveal-card .snacks-reveal-slot:disabled {
			cursor: default;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-mark {
			align-items: center;
			background: transparent;
			border: 2px dashed #3a4753;
			border-radius: 50%;
			color: #5b6773;
			display: flex;
			font-size: 14px;
			font-weight: 900;
			height: 49px;
			justify-content: center;
			line-height: 1;
			margin: 0 auto;
			width: 49px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-filled .snacks-reveal-slot-mark {
			background: #0e2b22;
			border-color: #1f9d6b;
			border-style: solid;
			color: #43d69c;
			font-size: 13px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-active .snacks-reveal-slot-mark {
			background: #0e2018;
			border: 3px solid #43d69c;
			box-shadow: 0 0 0 4px rgba(47, 208, 138, 0.16);
			color: #eaf7f1;
			font-size: 13px;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-label {
			color: #7f8b98;
			font-size: 9px;
			font-weight: 800;
			line-height: 1.15;
			margin-top: 7px;
			min-height: 10px;
			overflow: hidden;
			text-align: center;
			text-overflow: ellipsis;
			white-space: nowrap;
			width: 100%;
		}

		.snacks-team-reveal-card .snacks-reveal-slot-price {
			color: #5b6773;
			font-size: 12px;
			font-weight: 900;
			line-height: 1.15;
			margin-top: 1px;
		}

		.snacks-team-reveal-card
			.snacks-reveal-slot-filled
			.snacks-reveal-slot-price {
			color: #43d69c;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger {
			background: transparent;
			border: 0;
			border-radius: 0;
			border-top: 1px solid #1c2733;
			display: grid;
			gap: 10px;
			padding: 13px 0 0;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head {
			align-items: baseline;
			display: flex;
			gap: 10px;
			justify-content: space-between;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head span {
			color: #7f8b98;
			font-size: 10px;
			font-weight: 800;
			letter-spacing: 0.1em;
			line-height: 1.2;
			text-transform: uppercase;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head strong {
			color: #f4f7fa;
			display: block;
			font-size: 26px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head strong span {
			color: #5b6773;
			font-size: inherit;
			letter-spacing: 0;
			text-transform: none;
		}

		.snacks-team-reveal-card .snacks-reveal-cap-ledger-head small {
			color: #43d69c;
			font-size: 12px;
			font-weight: 900;
			letter-spacing: 0.06em;
			line-height: 1.2;
			text-align: right;
			text-transform: uppercase;
		}

		.snacks-team-reveal-card .snacks-cap-track {
			background: #182029;
			border-radius: 6px;
			height: 7px;
		}

		.snacks-team-reveal-card .snacks-cap-fill {
			background: linear-gradient(90deg, #22c993 0%, #7ed36a 55%, #f3c14a 100%);
			border-radius: 6px;
		}

		.snacks-team-reveal-page .snacks-reveal-board {
			display: contents;
		}

		.snacks-team-reveal-page .snacks-reveal-options {
			display: grid;
			gap: 10px;
			grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
			overflow: visible;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card {
			background: #121b25;
			border: 1px solid #1f2b37;
			border-radius: 13px;
			color: #f4f7fa;
			display: flex;
			flex-direction: column;
			gap: 0;
			height: auto;
			justify-content: flex-start;
			min-height: 116px;
			overflow: hidden;
			padding: 0;
			position: relative;
			text-align: left;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card::before {
			height: 3px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card:hover:not(:disabled) {
			border-color: #37485a;
			transform: translateY(-2px);
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-card-top {
			align-items: center;
			display: grid;
			gap: 7px;
			grid-template-columns: minmax(0, 1fr) auto;
			padding: 11px 12px 0;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-name {
			color: #f4f7fa;
			display: block;
			font-size: 14px;
			font-weight: 900;
			letter-spacing: 0;
			line-height: 1.1;
			min-width: 0;
			overflow: hidden;
			text-align: left;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-team-reveal-page
			.snacks-reveal-options
			.snacks-player-name-default {
			display: inline;
		}

		.snacks-team-reveal-page
			.snacks-reveal-options
			.snacks-player-name-phone {
			display: none;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-cost {
			align-items: center;
			background: #0e2018;
			border: 1px solid #1f6b4c;
			border-radius: 16px;
			color: #43d69c;
			display: inline-flex;
			flex: none;
			font-size: 11px;
			font-weight: 900;
			height: auto;
			justify-content: center;
			line-height: 1.1;
			padding: 3px 7px;
			width: auto;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-badges {
			display: flex;
			gap: 5px;
			justify-content: flex-start;
			margin: 0 0 10px;
			min-height: 18px;
			padding: 10px 12px 0;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-badge {
			align-items: center;
			border: 0;
			border-radius: 6px;
			color: #ffffff;
			display: inline-flex;
			font-size: 10px;
			font-weight: 900;
			height: 18px;
			justify-content: center;
			line-height: 1;
			min-width: 20px;
			padding: 0 5px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stats {
			display: grid;
			gap: 7px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			margin-top: auto;
			padding: 0 12px 12px;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat {
			background: #0d151d;
			border: 1px solid #1c2731;
			border-radius: 8px;
			display: grid;
			gap: 0;
			justify-items: start;
			line-height: 1.1;
			padding: 6px 8px;
			text-align: left;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat span {
			color: #74808d;
			font-size: 8px;
			font-weight: 800;
			letter-spacing: 0.08em;
			line-height: 1.1;
		}

		.snacks-team-reveal-page .snacks-reveal-options .snacks-player-stat strong {
			color: #eef2f6;
			font-size: 16px;
			font-weight: 900;
			line-height: 1.1;
		}

		.snacks-team-reveal-page .snacks-reveal-empty {
			align-items: center;
			background: #101922;
			border: 1px solid #1f2934;
			border-radius: 33px;
			color: #5c6773;
			display: flex;
			font-size: 16px;
			font-weight: 900;
			justify-content: center;
			justify-self: center;
			line-height: 1.2;
			min-height: auto;
			padding: 16px 49px;
			text-align: center;
		}

		.snacks-team-board {
			display: grid;
			gap: 10px;
		}

		.snacks-team-board-prompt {
			align-items: center;
			display: flex;
			gap: 12px;
			justify-content: space-between;
		}

		.snacks-team-board-prompt span {
			color: #97a2ae;
			font-size: 13px;
			font-weight: 700;
			line-height: 1.25;
		}

		.snacks-team-board-prompt strong {
			color: #566373;
			font-size: 11px;
			font-weight: 900;
			letter-spacing: 0.05em;
			line-height: 1;
			text-transform: uppercase;
			white-space: nowrap;
		}

		.snacks-team-board-shell {
			border: 1px solid #1f2b37;
			border-radius: 16px;
			overflow: hidden;
		}

		.snacks-team-board-scroll {
			-webkit-overflow-scrolling: touch;
			overflow-x: auto;
		}

		.snacks-team-board-grid {
			background: #0e141b;
			min-width: 780px;
		}

		.snacks-team-board-row {
			align-items: stretch;
			background: transparent;
			border: 0;
			color: inherit;
			display: flex;
			font-family: inherit;
			padding: 0;
			text-align: left;
			width: 100%;
		}

		.snacks-team-board-head {
			background: #0b1119;
			border-bottom: 1px solid #1f2b37;
		}

		.snacks-team-board-body-row {
			border-top: 1px solid #16202b;
			cursor: pointer;
		}

		.snacks-team-board-body-row:first-of-type {
			border-top: 0;
		}

		.snacks-team-board-body-row:hover {
			background: rgba(255, 255, 255, 0.025);
		}

		.snacks-team-board-player-head,
		.snacks-team-board-player-cell {
			background: #0b1119;
			box-shadow: 10px 0 10px -8px rgba(0, 0, 0, 0.62);
			box-sizing: border-box;
			flex: none;
			left: 0;
			position: sticky;
			width: 142px;
			z-index: 3;
		}

		.snacks-team-board-player-head {
			align-items: center;
			color: #566373;
			display: flex;
			font-size: 10px;
			font-weight: 900;
			letter-spacing: 0.07em;
			padding: 9px 10px 9px 11px;
			text-transform: uppercase;
		}

		.snacks-team-board-player-cell {
			background: #0e141b;
			border-left: 3px solid var(--tier-color);
			display: flex;
			flex-direction: column;
			justify-content: center;
			min-height: 58px;
			padding: 10px 10px 10px 11px;
			z-index: 2;
		}

		.snacks-team-board-player-cell strong {
			color: #eef2f6;
			display: block;
			font-size: 15px;
			font-weight: 900;
			line-height: 1.1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-team-board-player-cell span {
			color: #6b7683;
			display: block;
			font-size: 11px;
			font-weight: 800;
			line-height: 1.2;
			margin-top: 3px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.snacks-team-board-player-cell i {
			color: #45505d;
			font-style: normal;
		}

		.snacks-team-board-head-cell,
		.snacks-team-board-value-head {
			appearance: none;
			background: transparent;
			border: 0;
			color: #566373;
			cursor: pointer;
			flex: none;
			font-family: inherit;
			font-size: 10px;
			font-weight: 900;
			letter-spacing: 0.02em;
			line-height: 1.1;
			padding: 0;
			text-transform: uppercase;
		}

		.snacks-team-board-head-cell,
		.snacks-team-board-cell {
			align-items: center;
			box-sizing: border-box;
			display: flex;
			flex: none;
			justify-content: center;
			width: 52px;
		}

		.snacks-team-board-cell {
			color: #8b96a2;
			font-size: 14.5px;
			font-weight: 900;
			min-height: 58px;
		}

		.snacks-team-board-cell-good {
			color: #dce4ec;
		}

		.snacks-team-board-cell-elite,
		.snacks-team-board-cell-active,
		.snacks-team-board-head-active {
			color: #43d69c;
		}

		.snacks-team-board-value-head,
		.snacks-team-board-value-cell {
			background: #0b1119;
			box-shadow: -10px 0 10px -8px rgba(0, 0, 0, 0.62);
			box-sizing: border-box;
			flex: none;
			position: sticky;
			right: 0;
			width: 66px;
			z-index: 3;
		}

		.snacks-team-board-value-head {
			align-items: center;
			display: flex;
			justify-content: flex-end;
			padding: 0 12px 0 8px;
		}

		.snacks-team-board-value-cell {
			align-items: flex-end;
			background: #0e141b;
			display: flex;
			flex-direction: column;
			justify-content: center;
			min-height: 58px;
			padding: 10px 12px 10px 8px;
			z-index: 2;
		}

		.snacks-team-board-value-cell strong {
			color: #f4f7fa;
			font-size: 14.5px;
			font-weight: 900;
			line-height: 1;
		}

		.snacks-team-board-value-cell span {
			font-size: 10px;
			font-weight: 900;
			letter-spacing: 0.04em;
			line-height: 1.1;
			margin-top: 3px;
			text-transform: uppercase;
		}

		.snacks-team-reveal-page .snacks-reveal-actions {
			display: flex;
			justify-content: center;
		}

		.snacks-team-reveal-page .snacks-reveal-actions .snacks-advance-button {
			background: linear-gradient(180deg, #2fd08a 0%, #23b978 100%);
			border: 0;
			border-radius: 33px;
			box-shadow: 0 12px 26px -10px rgba(47, 208, 138, 0.6);
			color: #05231a;
			font-size: 16px;
			font-weight: 900;
			line-height: 1.2;
			min-width: auto;
			padding: 16px 49px;
		}

		.snacks-team-reveal-page .snacks-reveal-actions .snacks-advance-button:hover,
		.snacks-team-reveal-page .snacks-reveal-actions .snacks-advance-button:focus {
			background: linear-gradient(180deg, #36dc94 0%, #26c77f 100%);
			color: #05231a;
		}

		.snacks-team-reveal-page
			.snacks-reveal-actions
			.snacks-advance-button:disabled {
			background: #101922;
			border: 1px solid #1f2934;
			box-shadow: none;
			color: #5c6773;
		}
	`}</style>
);

const GameScoreChip = ({
	active = false,
	game,
	onSelect,
}: {
	active?: boolean;
	game: SimGame;
	onSelect: (game: SimGame) => void;
}) => (
	<button
		className={`snacks-game-score snacks-game-score-${game.result === "W" ? "win" : "loss"} ${
			active ? "snacks-game-score-active" : ""
		}`}
		onClick={() => onSelect(game)}
		title={`Game ${game.index} vs ${game.opponent.name}: ${game.result}`}
		type="button"
	>
		<span>{game.result}</span>
		<strong>Game {game.index}</strong>
		<small>{game.opponent.name}</small>
	</button>
);

const BoxScoreRows = ({
	lines,
	title,
}: {
	lines: BoxScoreLine[];
	title: string;
}) => (
	<div className="snacks-box-score-table">
		<div className="snacks-box-score-table-title">{title}</div>
		<div className="snacks-box-score-row snacks-box-score-row-head">
			<span>Player</span>
			<span>PTS</span>
			<span>REB</span>
			<span>AST</span>
			<span>+/-</span>
		</div>
		{lines.map((line) => (
			<div className="snacks-box-score-row" key={line.player.id}>
				<span className="snacks-box-score-player">
					<strong>{shortName(line.player)}</strong>
					<small>
						{line.player.team} {line.player.pos}
					</small>
				</span>
				<span>{line.pts}</span>
				<span>{line.trb}</span>
				<span>{line.ast}</span>
				<span>
					{line.plusMinus > 0 ? `+${line.plusMinus}` : line.plusMinus}
				</span>
			</div>
		))}
	</div>
);

const InlineBoxScore = ({
	game,
	userTeam,
}: {
	game: SimGame;
	userTeam: SimTeam;
}) => {
	const [activeTeamTab, setActiveTeamTab] = useState<"opponent" | "user">(
		"user",
	);
	const userLines = boxScoreLinesFor({
		game,
		opponentScore: game.opponentScore,
		score: game.userScore,
		team: userTeam,
	});
	const opponentLines = boxScoreLinesFor({
		game,
		opponentScore: game.userScore,
		score: game.opponentScore,
		team: game.opponent,
	});
	const activeLines = activeTeamTab === "user" ? userLines : opponentLines;
	const activeTitle = activeTeamTab === "user" ? "My Team" : game.opponent.name;

	useEffect(() => {
		setActiveTeamTab("user");
	}, [game.id]);

	return (
		<div className="snacks-inline-box-score">
			<div className="snacks-box-score-tabs" role="tablist">
				<button
					aria-selected={activeTeamTab === "user"}
					className={`snacks-box-score-tab ${
						activeTeamTab === "user" ? "snacks-box-score-tab-active" : ""
					}`}
					onClick={() => setActiveTeamTab("user")}
					role="tab"
					type="button"
				>
					My Team
				</button>
				<button
					aria-selected={activeTeamTab === "opponent"}
					className={`snacks-box-score-tab ${
						activeTeamTab === "opponent" ? "snacks-box-score-tab-active" : ""
					}`}
					onClick={() => setActiveTeamTab("opponent")}
					role="tab"
					title={game.opponent.name}
					type="button"
				>
					Opponent
				</button>
			</div>
			<div className="snacks-box-score-grid snacks-box-score-tab-panel">
				<BoxScoreRows lines={activeLines} title={activeTitle} />
			</div>
		</div>
	);
};

type PlayerRunningAverage = {
	apg: number;
	bpg: number;
	player: Player;
	plusMinus: number;
	ppg: number;
	rpg: number;
};

type StandingsMode = "conference" | "league";

type StandingsLine = {
	conference: "East" | "West";
	gamesBack: number;
	losses: number;
	rank: number;
	team: SimTeam;
	wins: number;
};

const formatRunningAverage = (value: number) => value.toFixed(1);

const formatSignedRunningAverage = (value: number) =>
	value > 0 ? `+${formatRunningAverage(value)}` : formatRunningAverage(value);

const conferenceAssignmentsFor = (standings: SimTeam[]) => {
	return new Map(
		standings.map((team) => [team.id, team.conference] as const),
	);
};

const formatGamesBack = (value: number) => {
	if (value === 0) {
		return "-";
	}
	return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

const standingsSnapshotFor = ({
	conferences,
	regularGames,
	standings,
}: {
	conferences: Map<string, "East" | "West">;
	regularGames: SimGame[];
	standings: SimTeam[];
}): StandingsLine[] => {
	const gamesPlayed = regularGames.length;
	const userWins = regularGames.filter((game) => game.result === "W").length;
	const userLosses = gamesPlayed - userWins;
	const progress = gamesPlayed / 82;

	const rows = standings.map((team) => {
		if (team.id === "user") {
			return {
				conference: conferences.get(team.id) ?? "East",
				losses: userLosses,
				team,
				wins: userWins,
			};
		}

		const projectedWins = team.wins * progress;
		const noise = seededNoise(
			`standings-${team.id}-${gamesPlayed}`,
			clamp(2.5 + progress * 3.5, 1.8, 6),
		);
		const wins = clamp(Math.round(projectedWins + noise), 0, gamesPlayed);

		return {
			conference: conferences.get(team.id) ?? "West",
			losses: gamesPlayed - wins,
			team,
			wins,
		};
	});

	const sortedRows = rows
		.sort(
			(a, b) =>
				b.wins - a.wins ||
				a.losses - b.losses ||
				b.team.power - a.team.power ||
				a.team.name.localeCompare(b.team.name),
		)
		.map((row, index) => ({
			...row,
			gamesBack: 0,
			rank: index + 1,
		}));

	return sortedRows.map((row) => {
		const conferenceLeader =
			sortedRows.find((candidate) => candidate.conference === row.conference) ??
			row;
		const gamesBack = Math.max(
			0,
			(conferenceLeader.wins -
				row.wins +
				(row.losses - conferenceLeader.losses)) /
				2,
		);

		return {
			...row,
			gamesBack,
		};
	});
};

const playerRunningAveragesFor = (
	games: SimGame[],
	team: SimTeam,
): PlayerRunningAverage[] => {
	const totals = new Map(
		team.players.map((player) => [
			player.id,
			{ ast: 0, blk: 0, plusMinus: 0, pts: 0, trb: 0 },
		]),
	);

	for (const game of games) {
		for (const line of boxScoreLinesFor({
			game,
			opponentScore: game.opponentScore,
			score: game.userScore,
			team,
		})) {
			const total = totals.get(line.player.id);
			if (total) {
				total.ast += line.ast;
				total.blk += line.blk;
				total.plusMinus += line.plusMinus;
				total.pts += line.pts;
				total.trb += line.trb;
			}
		}
	}

	const divisor = Math.max(1, games.length);

	return team.players.map((player) => {
		const total = totals.get(player.id)!;

		return {
			apg: total.ast / divisor,
			bpg: total.blk / divisor,
			player,
			plusMinus: total.plusMinus / divisor,
			ppg: total.pts / divisor,
			rpg: total.trb / divisor,
		};
	});
};

const StandingsCard = ({
	regularGames,
	standings,
	userTeam,
}: {
	regularGames: SimGame[];
	standings: SimTeam[];
	userTeam: SimTeam;
}) => {
	const [mode, setMode] = useState<StandingsMode>("conference");
	const conferences = useMemo(
		() => conferenceAssignmentsFor(standings),
		[standings],
	);
	const userConference = conferences.get(userTeam.id) ?? "East";
	const snapshot = useMemo(
		() => standingsSnapshotFor({ conferences, regularGames, standings }),
		[conferences, regularGames, standings],
	);
	const visibleRows = snapshot.filter(
		(line) => mode === "league" || line.conference === userConference,
	);

	return (
		<div className="snacks-standings-card">
			<div className="snacks-running-stats-head">
				<strong>
					{mode === "conference" ? "Conference Standings" : "League Standings"}
				</strong>
				<span>
					{mode === "conference" ? userConference : "All Teams"} /{" "}
					{regularGames.length} GP
				</span>
			</div>
			<div
				className="snacks-box-score-tabs snacks-standings-tabs"
				role="tablist"
			>
				<button
					aria-selected={mode === "conference"}
					className={`snacks-box-score-tab ${
						mode === "conference" ? "snacks-box-score-tab-active" : ""
					}`}
					onClick={() => setMode("conference")}
					role="tab"
					type="button"
				>
					Conference
				</button>
				<button
					aria-selected={mode === "league"}
					className={`snacks-box-score-tab ${
						mode === "league" ? "snacks-box-score-tab-active" : ""
					}`}
					onClick={() => setMode("league")}
					role="tab"
					type="button"
				>
					League
				</button>
			</div>
			<div className="snacks-standings-scroll">
				<div className="snacks-standings-row snacks-standings-row-head">
					<span>#</span>
					<span>Team</span>
					<span>W-L</span>
					<span>GB</span>
				</div>
				{visibleRows.map((line, index) => (
					<div
						className={`snacks-standings-row ${
							line.team.id === userTeam.id ? "snacks-standings-row-user" : ""
						}`}
						key={line.team.id}
					>
						<span>{index + 1}</span>
						<span className="snacks-standings-team">
							<strong>
								{line.team.id === userTeam.id ? "My Team" : line.team.name}
							</strong>
							<small>{line.conference}</small>
						</span>
						<span>
							{line.wins}-{line.losses}
						</span>
						<span>{formatGamesBack(line.gamesBack)}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const PlayerRunningStatsCard = ({
	games,
	team,
}: {
	games: SimGame[];
	team: SimTeam;
}) => {
	const averages = playerRunningAveragesFor(games, team);

	return (
		<div className="snacks-running-stats-card">
			<div className="snacks-running-stats-head">
				<strong>Player Stats</strong>
				<span>{games.length} GP</span>
			</div>
			<div className="snacks-running-stats-row snacks-running-stats-row-head">
				<span>Player</span>
				<span>PPG</span>
				<span>RPG</span>
				<span>APG</span>
				<span>BPG</span>
				<span>+/-</span>
			</div>
			{averages.map((line) => (
				<div className="snacks-running-stats-row" key={line.player.id}>
					<span className="snacks-running-stats-player">
						<strong>{shortName(line.player)}</strong>
						<small>
							{line.player.team} {line.player.pos}
						</small>
					</span>
					<span>{formatRunningAverage(line.ppg)}</span>
					<span>{formatRunningAverage(line.rpg)}</span>
					<span>{formatRunningAverage(line.apg)}</span>
					<span>{formatRunningAverage(line.bpg)}</span>
					<span>{formatSignedRunningAverage(line.plusMinus)}</span>
				</div>
			))}
		</div>
	);
};

const BoxScoreModal = ({
	game,
	onClose,
	userTeam,
}: {
	game: SimGame;
	onClose: () => void;
	userTeam: SimTeam;
}) => {
	const userLines = boxScoreLinesFor({
		game,
		opponentScore: game.opponentScore,
		score: game.userScore,
		team: userTeam,
	});
	const opponentLines = boxScoreLinesFor({
		game,
		opponentScore: game.userScore,
		score: game.opponentScore,
		team: game.opponent,
	});

	return (
		<div
			className={`snacks-modal-backdrop ${
				variant === "teamReveal" ? "snacks-player-modal-team-backdrop" : ""
			}`}
			role="presentation"
			onClick={onClose}
		>
			<div
				aria-modal="true"
				className="snacks-player-modal snacks-box-score-modal"
				onClick={(event) => event.stopPropagation()}
				role="dialog"
			>
				<div className="snacks-player-modal-head">
					<div>
						<div className="snacks-sim-kicker">Game {game.index}</div>
						<div className="snacks-player-modal-name">Box Score</div>
					</div>
					<button
						aria-label="Close box score"
						className="snacks-player-modal-close"
						onClick={onClose}
						type="button"
					>
						x
					</button>
				</div>
				<div className="snacks-box-score-final">
					<div>
						<span>My Team</span>
						<strong>{game.userScore}</strong>
					</div>
					<div>
						<span>Opponent</span>
						<strong>{game.opponentScore}</strong>
					</div>
				</div>
				<div className="snacks-box-score-grid">
					<BoxScoreRows lines={userLines} title="Your Build" />
					<BoxScoreRows lines={opponentLines} title={game.opponent.name} />
				</div>
			</div>
		</div>
	);
};

const SeasonDashboard = ({
	autoStart,
	onContinue,
	onQuit,
	resultsUnlocked,
	sim,
}: {
	autoStart: boolean;
	onContinue: () => void;
	onQuit: () => void;
	resultsUnlocked: boolean;
	sim: SimField;
}) => {
	const segments = useMemo(() => simSegmentsFor(sim), [sim]);
	const revealedCountsForState = () =>
		resultsUnlocked ? segments.map((segment) => segment.games.length) : segments.map(() => 0);
	const [revealedCounts, setRevealedCounts] = useState<number[]>(
		revealedCountsForState,
	);
	const [activeIndex, setActiveIndex] = useState(
		resultsUnlocked ? Math.max(0, segments.length - 1) : 0,
	);
	const [selectedGameId, setSelectedGameId] = useState<string | undefined>();
	const [isSimulating, setIsSimulating] = useState(false);
	const revealTimer = useRef<number | undefined>();
	const touchStartX = useRef<number | undefined>();

	const clearRevealTimer = () => {
		if (revealTimer.current !== undefined) {
			window.clearTimeout(revealTimer.current);
			revealTimer.current = undefined;
		}
	};

	useEffect(() => {
		clearRevealTimer();
		setRevealedCounts(revealedCountsForState());
		setActiveIndex(resultsUnlocked ? Math.max(0, segments.length - 1) : 0);
		setSelectedGameId(undefined);
		setIsSimulating(false);
		return clearRevealTimer;
	}, [resultsUnlocked, segments]);

	const nextSegmentIndex = revealedCounts.findIndex(
		(count, index) => count < (segments[index]?.games.length ?? 0),
	);
	const complete = nextSegmentIndex === -1;
	const activeSegment = segments[activeIndex] ?? segments[0]!;
	const activeRevealedCount = clamp(
		revealedCounts[activeIndex] ?? 0,
		0,
		activeSegment.games.length,
	);
	const activeGames = activeSegment.games.slice(0, activeRevealedCount);
	const activeGameSlots = Array.from({ length: 18 }, (_, i) => activeGames[i]);
	const revealedGames = segments.flatMap((segment, index) =>
		segment.games.slice(0, revealedCounts[index] ?? 0),
	);
	const selectedGame = revealedGames.find((game) => game.id === selectedGameId);
	const regularGames = segments.flatMap((segment, index) =>
		segment.type === "month"
			? segment.games.slice(0, revealedCounts[index] ?? 0)
			: [],
	);
	const regularWins = regularGames.filter((game) => game.result === "W").length;
	const regularLosses = regularGames.length - regularWins;
	const hasStarted = revealedCounts.some((count) => count > 0);
	const revealedSegmentIndexes = revealedCounts
		.map((count, index) => (count > 0 ? index : -1))
		.filter((index) => index >= 0);
	const previousRevealedSegmentIndex = [...revealedSegmentIndexes]
		.reverse()
		.find((index) => index < activeIndex);
	const nextRevealedSegmentIndex = revealedSegmentIndexes.find(
		(index) => index > activeIndex,
	);
	const canGoPrev = previousRevealedSegmentIndex !== undefined;
	const canGoNext = nextRevealedSegmentIndex !== undefined;
	const nextSegment =
		nextSegmentIndex >= 0 ? segments[nextSegmentIndex] : undefined;
	const simButtonLabel = complete
		? resultsUnlocked
			? "See Results"
			: "Get Your Score"
		: isSimulating
			? "Simulation In Progress"
			: hasStarted
				? "Continue sim"
				: `Sim ${nextSegment?.label ?? activeSegment.label}`;
	const simButtonDisabled = isSimulating && !complete;
	const activeDetail =
		activeSegment.type === "month"
			? `${activeGames.length} of ${activeSegment.games.length}`
			: activeSegment.detail;
	const segmentIsViewable = (index: number) =>
		(revealedCounts[index] ?? 0) > 0 || (!hasStarted && index === 0);

	const goToSegment = (index: number) => {
		if (index < 0 || index >= segments.length) {
			return;
		}
		if (!segmentIsViewable(index)) {
			return;
		}
		setActiveIndex(index);
	};

	const toggleSimulation = () => {
		if (complete) {
			onContinue();
			return;
		}

		if (isSimulating) {
			clearRevealTimer();
			setIsSimulating(false);
			return;
		}

		setIsSimulating(true);
	};
	const stopOrQuit = () => {
		if (isSimulating) {
			clearRevealTimer();
			setIsSimulating(false);
			return;
		}
		onQuit();
	};

	const goPrev = () => {
		if (previousRevealedSegmentIndex !== undefined) {
			goToSegment(previousRevealedSegmentIndex);
		}
	};
	const goNext = () => {
		if (nextRevealedSegmentIndex !== undefined) {
			goToSegment(nextRevealedSegmentIndex);
		}
	};

	useEffect(() => {
		if (!autoStart || hasStarted || isSimulating || resultsUnlocked) {
			return;
		}
		setIsSimulating(true);
	}, [autoStart, hasStarted, isSimulating, resultsUnlocked]);

	useEffect(() => {
		clearRevealTimer();
		if (!isSimulating || resultsUnlocked) {
			return;
		}
		if (complete) {
			setIsSimulating(false);
			return;
		}

		const targetIndex = nextSegmentIndex;
		const segment = segments[targetIndex];
		if (!segment) {
			setIsSimulating(false);
			return;
		}
		const nextGameIndex = revealedCounts[targetIndex] ?? 0;
		const game = segment.games[nextGameIndex];
		if (!game) {
			setIsSimulating(false);
			return;
		}

		const segmentStarted = nextGameIndex > 0;
		revealTimer.current = window.setTimeout(
			() => {
				setActiveIndex(targetIndex);
				setRevealedCounts((counts) =>
					counts.map((count, index) =>
						index === targetIndex
							? Math.min(segment.games.length, count + 1)
							: count,
					),
				);
			},
			segmentStarted ? 170 : 360,
		);
	}, [
		complete,
		isSimulating,
		nextSegmentIndex,
		resultsUnlocked,
		revealedCounts,
		segments,
	]);

	return (
		<section className="snacks-season-dashboard">
			<div className="snacks-dashboard-head">
				<div className="snacks-dashboard-title-block">
					<span className="snacks-sim-kicker">Season sim</span>
					<strong>
						{!hasStarted
							? "Ready to sim"
							: complete
								? "Season complete"
								: "Season in progress"}
					</strong>
				</div>
				<div className="snacks-dashboard-record">
					<span>W-L</span>
					<strong>
						{regularWins}-{regularLosses}
					</strong>
				</div>
				<div
					className="snacks-dashboard-actions"
				>
					<button
						className="snacks-flow-back snacks-sim-stop-button"
						onClick={stopOrQuit}
						type="button"
					>
						{isSimulating ? "Stop" : "Quit"}
					</button>
					<button
						className="btn btn-success btn-sm snacks-advance-button snacks-sim-advance-button"
						disabled={simButtonDisabled}
						onClick={toggleSimulation}
						type="button"
					>
						{simButtonLabel}
					</button>
				</div>
			</div>

			<div
				className="snacks-sim-stage"
				onTouchEnd={(event) => {
					const startX = touchStartX.current;
					const endX = event.changedTouches[0]?.clientX;
					touchStartX.current = undefined;
					if (startX === undefined || endX === undefined) {
						return;
					}
					const delta = endX - startX;
					if (Math.abs(delta) < 36) {
						return;
					}
					if (delta > 0) {
						goPrev();
					} else {
						goNext();
					}
				}}
				onTouchStart={(event) => {
					touchStartX.current = event.changedTouches[0]?.clientX;
				}}
			>
				<div className="snacks-sim-tabs">
					{segments.map((segment, index) => {
						const revealed = (revealedCounts[index] ?? 0) > 0;
						return (
							<button
								className={`snacks-sim-tab ${
									index === activeIndex ? "snacks-sim-tab-active" : ""
								}`}
								disabled={!revealed && !segmentIsViewable(index)}
								key={segment.id}
								onClick={() => goToSegment(index)}
								type="button"
							>
								{segment.label}
							</button>
						);
					})}
				</div>

				<article className="snacks-current-segment">
					<div className="snacks-current-segment-head">
						<button
							aria-label="Previous month"
							className="snacks-sim-nav-button"
							disabled={!canGoPrev}
							onClick={goPrev}
							type="button"
						>
							&lt;
						</button>
						<div className="snacks-current-segment-title">
							<strong>{activeSegment.label}</strong>
							<span>{activeDetail}</span>
						</div>
						<button
							aria-label="Next month"
							className="snacks-sim-nav-button"
							disabled={!canGoNext}
							onClick={goNext}
							type="button"
						>
							&gt;
						</button>
					</div>

					<div className="snacks-current-games">
						{activeGameSlots.map((game, i) =>
							game ? (
								<GameScoreChip
									active={game.id === selectedGame?.id}
									game={game}
									key={game.id}
									onSelect={(game) => setSelectedGameId(game.id)}
								/>
							) : (
								<div
									aria-hidden="true"
									className="snacks-game-slot-empty"
									key={`empty-${activeSegment.id}-${i}`}
								/>
							),
						)}
					</div>
				</article>

				{selectedGame ? (
					<InlineBoxScore game={selectedGame} userTeam={sim.userTeam} />
				) : (
					<div className="snacks-inline-box-empty">
						Tap a game to view the box score.
					</div>
				)}
				<StandingsCard
					regularGames={regularGames}
					standings={sim.standings}
					userTeam={sim.userTeam}
				/>
				<PlayerRunningStatsCard games={revealedGames} team={sim.userTeam} />
			</div>

			{complete ? (
				<div className="snacks-sim-finish-card">
					<strong>
						{resultsUnlocked ? "Results unlocked" : "Season sim complete"}
					</strong>
					<span className="snacks-sim-muted">
						{resultsUnlocked
							? `${sim.playoffRound}. ${sim.playoffDetail}`
							: "Continue to see the final dashboard."}
					</span>
				</div>
			) : null}
		</section>
	);
};

type FinishStage = "champ" | "conf" | "finals" | "missed" | "r1" | "r2";

const FINISH_STAGES: { key: FinishStage; label: string }[] = [
	{ key: "missed", label: "Missed" },
	{ key: "r1", label: "R1" },
	{ key: "r2", label: "R2" },
	{ key: "conf", label: "Conf" },
	{ key: "finals", label: "Finals" },
	{ key: "champ", label: "Champ" },
];

const finishStageFor = (sim: SimField): FinishStage => {
	if (sim.playoffRound === "Champion") return "champ";
	if (sim.playoffRound === "Lost Finals") return "finals";
	if (sim.playoffRound === "Lost Conference Finals") return "conf";
	if (
		sim.playoffRound === "Lost Conference Semifinals" ||
		sim.playoffRound === "Lost Semifinals"
	) {
		return "r2";
	}
	if (sim.playoffRound === "Lost First Round") return "r1";
	return "missed";
};

const finishStatusLabelFor = (stage: FinishStage, sim: SimField) => {
	if (stage === "champ") return "Champion";
	if (stage === "finals") return "Lost finals";
	if (stage === "conf") return "Lost conf";
	if (stage === "r2") return "Lost R2";
	if (stage === "r1") return "Lost R1";
	return "Missed playoffs";
};

const finishCaptionFor = (sim: SimField) => {
	const record = `${sim.userTeam.wins}-${sim.userTeam.losses}`;
	if (sim.playoffRound === "Missed Playoffs") {
		return `Finished ${record} and did not qualify.`;
	}
	if (sim.playoffRound === "Champion") {
		return `Finished ${record} and won the championship.`;
	}
	return `Finished ${record}. ${sim.playoffDetail}`;
};

const summaryScore = (value: number) => clamp(Math.round(value), 0, 100);

const summaryInsightMetricFor = ({
	analysis,
	heatMapStats,
	insight,
}: {
	analysis: FiveVFiveSeasonAnalysis;
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	insight: FiveVFiveInsight;
}) => {
	const statByKey = heatMapStatMapFor(heatMapStats);
	if (insight.key.includes("shot_efficiency")) {
		return {
			label: "FG% rank",
			score: summaryScore(statByKey.fgPct.percentile * 100),
		};
	}
	if (
		insight.key.includes("spacing") ||
		insight.key.includes("outside_shooting") ||
		insight.key === "poor_spacing"
	) {
		return {
			label: "Spacing score",
			score: summaryScore(analysis.scores.spacing),
		};
	}
	if (insight.key.includes("rebounding")) {
		return {
			label: "Rebounding score",
			score: summaryScore(analysis.scores.rebounding),
		};
	}
	if (insight.key.includes("defense") || insight.key === "interior_defense") {
		return {
			label: "Defense score",
			score: summaryScore(
				Math.min(
					analysis.scores.perimeterDefense,
					analysis.scores.interiorDefense,
				),
			),
		};
	}
	if (
		insight.key.includes("shot_creation") ||
		insight.key.includes("offense")
	) {
		return {
			label: "Creation score",
			score: summaryScore(analysis.scores.shotCreation),
		};
	}
	if (insight.key.includes("role")) {
		return {
			label: "Role balance",
			score: summaryScore(analysis.scores.roleBalance),
		};
	}
	return { label: "Fit score", score: summaryScore(analysis.scores.fit) };
};

const SeasonSummaryScoreCard = ({
	label,
	score,
	tone = "green",
}: {
	label: string;
	score: number;
	tone?: "amber" | "green";
}) => (
	<div
		className={`snacks-summary-score-card ${
			tone === "amber" ? "snacks-summary-score-card-amber" : ""
		}`}
	>
		<span>{label}</span>
		<strong>{score}</strong>
		<div className="snacks-summary-mini-bar">
			<div style={{ width: `${score}%` }} />
		</div>
	</div>
);

const SeasonSummaryInsightCard = ({
	analysis,
	heatMapStats,
	insight,
	tone,
}: {
	analysis: FiveVFiveSeasonAnalysis;
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	insight: FiveVFiveInsight;
	tone: "amber" | "green";
}) => {
	const metric = summaryInsightMetricFor({ analysis, heatMapStats, insight });
	return (
		<article
			className={`snacks-summary-insight snacks-summary-insight-${tone}`}
		>
			<h3>{insight.title}</h3>
			<p>{insight.detail}</p>
			<div className="snacks-summary-insight-metric">
				<span>{metric.label}</span>
				<strong>
					{metric.score}
					<span>/100</span>
				</strong>
			</div>
			<div className="snacks-summary-bar">
				<div style={{ width: `${metric.score}%` }} />
			</div>
		</article>
	);
};

const driverRoleFor = (player: Player) => {
	const roles = classifyFiveVFiveRoles(player);
	if (roles.includes("primary_creator")) return "Drove shot creation";
	if (roles.includes("shooter")) return "Stretched the floor";
	if (roles.includes("rim_protector")) return "Anchored the defense";
	if (roles.includes("rebounder")) return "Controlled the glass";
	if (roles.includes("perimeter_defender")) return "Set the defensive tone";
	return "Connected the lineup";
};

const SeasonSummary = ({
	analysis,
	heatMapStats,
	sim,
}: {
	analysis: FiveVFiveSeasonAnalysis;
	heatMapStats: ReturnType<typeof teamHeatMapStatsFor>;
	sim: SimField;
}) => {
	const stage = finishStageFor(sim);
	const reachedIndex = FINISH_STAGES.findIndex((item) => item.key === stage);
	const champion = stage === "champ";
	const drivers = analysis.keyPlayerNotes
		.map((note) =>
			sim.userTeam.players.find((player) => player.id === note.playerId),
		)
		.filter((player): player is Player => player !== undefined)
		.slice(0, 2);
	const worked = analysis.worked.slice(0, 2);
	const capped =
		analysis.didntWork[0] ??
		limitingBasketballNeedInsightFor({
			heatMapStats,
			priority: 50,
			scores: analysis.scores,
		});
	const creationScore = summaryScore(analysis.scores.shotCreation);
	const spacingScore = summaryScore(analysis.scores.spacing);
	const fitScore = summaryScore(analysis.scores.fit);

	return (
		<section className="snacks-season-summary">
			<div className="snacks-summary-eyebrow">Final Result</div>
			<article
				className={`snacks-summary-result-card ${
					champion ? "snacks-summary-result-card-champ" : ""
				}`}
			>
				<div className="snacks-summary-result-top">
					<div>
						<span>Regular season</span>
						<strong className="snacks-summary-record">
							{sim.userTeam.wins}
							<span>&ndash;</span>
							{sim.userTeam.losses}
						</strong>
					</div>
					<div className="snacks-summary-result-status">
						<strong>{finishStatusLabelFor(stage, sim)}</strong>
						<span>League rank #{sim.userTeam.seed}</span>
					</div>
				</div>
				<div className="snacks-summary-ladder">
					<div className="snacks-summary-ladder-track" />
					<div className="snacks-summary-ladder-steps">
						{FINISH_STAGES.map((item, index) => {
							const reached = index <= reachedIndex;
							const current = index === reachedIndex;
							return (
								<div
									className={`snacks-summary-ladder-step ${
										reached ? "snacks-summary-ladder-step-reached" : ""
									} ${
										current ? "snacks-summary-ladder-step-current" : ""
									} ${champion ? "snacks-summary-ladder-step-champ" : ""}`}
									key={item.key}
								>
									<span />
									<small>{item.label}</small>
								</div>
							);
						})}
					</div>
				</div>
				<p className="snacks-summary-caption">
					{finishCaptionFor(sim)} Team ratings:{" "}
					<strong>OFF {Math.round(sim.userTeam.result.offense)}</strong> ·{" "}
					<strong>DEF {Math.round(sim.userTeam.result.defense)}</strong> ·{" "}
					<strong>Fit {formatFit(sim.userTeam.result.fit)}</strong>.
				</p>
			</article>

			<div className="snacks-summary-eyebrow">Season Analysis</div>
			<h2 className="snacks-summary-headline">{analysis.headline}</h2>
			<div className="snacks-summary-score-grid">
				<SeasonSummaryScoreCard label="Creation" score={creationScore} />
				<SeasonSummaryScoreCard label="Spacing" score={spacingScore} />
				<SeasonSummaryScoreCard label="Fit" score={fitScore} tone="amber" />
			</div>
			<p className="snacks-summary-lead">{analysis.summary}</p>

			<div className="snacks-summary-section-label snacks-summary-section-label-worked">
				What Worked
			</div>
			<div className="snacks-summary-insight-stack">
				{worked.map((item) => (
					<SeasonSummaryInsightCard
						analysis={analysis}
						heatMapStats={heatMapStats}
						insight={item}
						key={item.key}
						tone="green"
					/>
				))}
			</div>

			<div className="snacks-summary-section-label snacks-summary-section-label-capped">
				What Capped It
			</div>
			<SeasonSummaryInsightCard
				analysis={analysis}
				heatMapStats={heatMapStats}
				insight={capped}
				tone="amber"
			/>

			{drivers.length > 0 ? (
				<>
					<div className="snacks-summary-section-label">Who Drove It</div>
					<div className="snacks-summary-drivers">
						{drivers.map((player) => (
							<div className="snacks-summary-driver" key={player.id}>
								<div>
									<strong>{shortName(player)}</strong>
									<span>{driverRoleFor(player)}</span>
								</div>
								<div className="snacks-summary-driver-stats">
									<span>
										<small>OFF</small>
										<strong>{player.off}</strong>
									</span>
									<span>
										<small>DEF</small>
										<strong>{player.def}</strong>
									</span>
								</div>
							</div>
						))}
					</div>
				</>
			) : null}

			<article className="snacks-summary-next-build">
				<span>Next build &rarr;</span>
				<p>{analysis.recommendation}</p>
			</article>
		</section>
	);
};

const H2HWaitingPanel = () => (
	<section className="snacks-season-dashboard snacks-h2h-waiting-panel">
		<div className="snacks-dashboard-head">
			<div className="snacks-dashboard-title-block">
				<span className="snacks-sim-kicker">Head-to-Head</span>
				<strong>Waiting for opponent</strong>
			</div>
		</div>
		<div className="snacks-waiting-card">
			<span className="snacks-waiting-dot" />
			<div>
				<strong>Simulation locked</strong>
				<div className="snacks-sim-muted">
					Your season will unlock after the second player submits from this same
					board.
				</div>
			</div>
		</div>
	</section>
);

const SimResults = ({
	autoStart,
	copied,
	hostHandle,
	newChallengeHref,
	onCopy,
	playerHandle,
	seasonBlockedReason,
	showSeasonDashboard,
	sim,
}: {
	autoStart: boolean;
	copied: boolean;
	hostHandle?: string;
	newChallengeHref: string;
	onCopy: () => Promise<void>;
	playerHandle: string;
	seasonBlockedReason?: "h2h-opponent";
	showSeasonDashboard: boolean;
	sim: SimField;
}) => {
	const activeTeam = sim.userTeam;
	const activePlayerLines = playerSimLines(activeTeam.players, activeTeam);
	const handleLabel = playerHandleLabel(playerHandle);
	const normalizedHostHandle = normalizePlayerHandle(hostHandle ?? "");
	const showHostLeaderboardRow =
		normalizedHostHandle.length === 4 &&
		normalizedHostHandle !== normalizePlayerHandle(playerHandle);
	const heatMapStats = teamHeatMapStatsFor(
		activeTeam.players,
		sim.statBenchmark,
	);
	const seasonAnalysis = buildFiveVFiveSeasonAnalysis({ heatMapStats, sim });
	const [dashboardUnlocked, setDashboardUnlocked] =
		useState(!showSeasonDashboard);
	const [viewingSummary, setViewingSummary] = useState(!showSeasonDashboard);
	const dashboardRef = useRef<HTMLDivElement>(null);
	const seasonBlocked = seasonBlockedReason !== undefined;
	const showFinalDashboard =
		!showSeasonDashboard || (dashboardUnlocked && viewingSummary && !seasonBlocked);

	const unlockDashboard = () => {
		setDashboardUnlocked(true);
		setViewingSummary(true);
		window.requestAnimationFrame(() => {
			dashboardRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});
	};
	const goBack = () => {
		if (showFinalDashboard && showSeasonDashboard) {
			setViewingSummary(false);
			return;
		}
		window.location.href = "/";
	};

	return (
		<section
			className={`snacks-sim-card ${
				showFinalDashboard ? "snacks-summary-shell" : ""
			}`}
		>
			{showSeasonDashboard && showFinalDashboard ? (
				<button
					className="snacks-flow-back snacks-summary-back"
					onClick={goBack}
					type="button"
				>
					Back
				</button>
			) : null}

			{showFinalDashboard ? (
				<div ref={dashboardRef}>
					<SeasonSummary
						analysis={seasonAnalysis}
						heatMapStats={heatMapStats}
						sim={sim}
					/>
				</div>
			) : null}

			{seasonBlocked ? <H2HWaitingPanel /> : null}

			{showSeasonDashboard && !showFinalDashboard && !seasonBlocked ? (
				<SeasonDashboard
					autoStart={autoStart}
					onContinue={unlockDashboard}
					onQuit={() => {
						window.location.href = "/";
					}}
					resultsUnlocked={dashboardUnlocked}
					sim={sim}
				/>
			) : null}

			<div
				className="snacks-sim-grid"
				style={{ display: showFinalDashboard ? undefined : "none" }}
			>
				<div>
					<div className="snacks-team-heat-map">
						<div className="snacks-team-heat-map-head">
							<strong>Your Team Stats</strong>
							<span className="snacks-sim-kicker">League rank</span>
						</div>
						<div className="snacks-team-heat-map-grid">
							{heatMapStats.map((stat) => (
								<div
									className="snacks-team-heat-cell"
									key={stat.key}
									style={heatMapStyleFor(stat.percentile)}
									title={`${stat.label} ranks ${heatMapRankLabel(stat.percentile)} in a 30-team league projection`}
								>
									<span>{stat.label}</span>
									<strong>{formatTeamBoxPct(stat.value)}</strong>
									<small>{heatMapRankLabel(stat.percentile)}</small>
								</div>
							))}
						</div>
					</div>
					<div className="snacks-team-ratings">
						<div className="snacks-team-rating">
							<span>Off</span>
							<strong>{Math.round(activeTeam.result.offense)}</strong>
						</div>
						<div className="snacks-team-rating">
							<span>Def</span>
							<strong>{Math.round(activeTeam.result.defense)}</strong>
						</div>
						<div className="snacks-team-rating">
							<span>Fit</span>
							<strong>{formatFit(activeTeam.result.fit)}</strong>
						</div>
					</div>
					<h3 className="snacks-section-title">Player Stats</h3>
					{activePlayerLines.map((line) => (
						<div className="snacks-player-line" key={line.player.id}>
							<div className="snacks-player-line-name">
								<strong>{line.player.name}</strong>
								<span>
									${line.player.price} / {line.player.team} {line.player.pos}
								</span>
							</div>
							<div className="snacks-stat-cell">
								<span>PTS</span>
								<strong>{line.pts}</strong>
							</div>
							<div className="snacks-stat-cell">
								<span>REB</span>
								<strong>{line.trb}</strong>
							</div>
							<div className="snacks-stat-cell">
								<span>AST</span>
								<strong>{line.ast}</strong>
							</div>
						</div>
					))}
				</div>

				<div>
					<h3 className="snacks-section-title">Leaderboard</h3>
					<div className="snacks-leaderboard-row snacks-leaderboard-user snacks-leaderboard-active">
						<span className="snacks-leaderboard-rank">1</span>
						<strong className="snacks-leaderboard-name">{handleLabel}</strong>
						<span>Record</span>
						<span>
							{activeTeam.wins}-{activeTeam.losses}
						</span>
					</div>
					{showHostLeaderboardRow ? (
						<div className="snacks-leaderboard-row">
							<span className="snacks-leaderboard-rank">...</span>
							<strong className="snacks-leaderboard-name">
								{playerHandleLabel(normalizedHostHandle)}
							</strong>
							<span>Challenger</span>
							<span>Waiting</span>
						</div>
					) : null}
					<div className="snacks-waiting-card">
						<span className="snacks-waiting-dot" />
						<div>
							<strong>Waiting for opponents</strong>
							<div className="snacks-sim-muted">
								Opponent rows will appear after they submit this same challenge.
							</div>
						</div>
					</div>
				</div>
			</div>

			<div
				className={`snacks-copy-row ${
					showSeasonDashboard ? "snacks-copy-row-single" : ""
				}`}
				style={{ display: showFinalDashboard ? undefined : "none" }}
			>
				{showSeasonDashboard ? null : (
					<div className="snacks-sim-muted">
						Send this result back to the challenger. Everyone uses the same
						league year, tiers, roster size, and ${CAP} cap.
					</div>
				)}
				<div
					className={`snacks-copy-actions ${
						showSeasonDashboard ? "snacks-copy-actions-single" : ""
					}`}
				>
					<a
						className={`btn ${
							showSeasonDashboard
								? "btn-success snacks-advance-button snacks-new-challenge-button"
								: "btn-outline-secondary"
						}`}
						href={newChallengeHref}
					>
						Start new challenge
					</a>
					{showSeasonDashboard ? null : (
						<button className="btn btn-success" onClick={onCopy}>
							{copied ? "Shared result" : "Share result"}
						</button>
					)}
				</div>
			</div>
		</section>
	);
};

const InviteChallengeDialog = ({
	challengeSeed,
	copiedInvite,
	leagueYear,
	onClose,
	onCopyInvite,
}: {
	challengeSeed: number;
	copiedInvite: boolean;
	leagueYear: number;
	onClose: () => void;
	onCopyInvite: () => Promise<void>;
}) => (
	<div
		className="snacks-modal-backdrop snacks-top-drawer-backdrop"
		role="presentation"
		onClick={onClose}
	>
		<div
			aria-modal="true"
			className="snacks-player-modal snacks-top-drawer snacks-invite-modal"
			onClick={(event) => event.stopPropagation()}
			role="dialog"
		>
			<div className="snacks-player-modal-head">
				<div>
					<div className="snacks-sim-kicker">
						Challenge #{challengeSeed} / {leagueYearLabel(leagueYear)}
					</div>
					<div className="snacks-player-modal-name">Invite someone to play</div>
				</div>
				<button
					aria-label="Close invite dialog"
					className="snacks-player-modal-close"
					onClick={onClose}
					type="button"
				>
					x
				</button>
			</div>
			<p className="snacks-sim-subtitle" style={{ margin: 0 }}>
				Send this board before making picks so both players use the same tiers
				and league year.
			</p>
			<div className="snacks-invite-actions">
				<button
					aria-label="Copy invite"
					className="btn btn-outline-secondary snacks-copy-invite-button"
					onClick={onCopyInvite}
					type="button"
				>
					<svg aria-hidden="true" height="16" viewBox="0 0 24 24" width="16">
						<path
							d="M8 8h10v12H8z M6 16H4V4h10v2"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
						/>
					</svg>
					{copiedInvite ? "Copied" : "Copy Invite"}
				</button>
				<button className="btn btn-success" onClick={onClose} type="button">
					Start picking
				</button>
			</div>
		</div>
	</div>
);

const SubmitHandleDialog = ({
	handle,
	onChange,
	onClose,
	resultPath,
}: {
	handle: string;
	onChange: (handle: string) => void;
	onClose: () => void;
	resultPath: string;
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const complete = handle.length === 4;

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	return (
		<div
			className="snacks-modal-backdrop snacks-top-drawer-backdrop"
			role="presentation"
			onClick={onClose}
		>
			<div
				aria-modal="true"
				className="snacks-player-modal snacks-top-drawer snacks-handle-modal"
				onClick={(event) => event.stopPropagation()}
				role="dialog"
			>
				<div className="snacks-player-modal-head">
					<div>
						<div className="snacks-sim-kicker">Leaderboard handle</div>
						<div className="snacks-player-modal-name">
							Enter your 4-char handle
						</div>
					</div>
					<button
						aria-label="Close handle dialog"
						className="snacks-player-modal-close"
						onClick={onClose}
						type="button"
					>
						x
					</button>
				</div>
				<p className="snacks-sim-subtitle" style={{ margin: 0 }}>
					This is the player handle that appears on the leaderboard. Use A-Z and
					0-9.
				</p>
				<div
					className="snacks-handle-cells"
					onClick={() => inputRef.current?.focus()}
				>
					{Array.from({ length: 4 }, (_, i) => {
						const filled = i < handle.length;
						const active = i === handle.length && handle.length < 4;
						return (
							<div
								className={`snacks-handle-cell ${
									filled ? "snacks-handle-cell-filled" : ""
								} ${active ? "snacks-handle-cell-active" : ""}`}
								key={i}
							>
								{handle[i] ?? ""}
								{active ? <span className="snacks-handle-caret" /> : null}
							</div>
						);
					})}
					<input
						autoComplete="one-time-code"
						autoCapitalize="characters"
						autoCorrect="off"
						className="snacks-handle-input"
						inputMode="text"
						onChange={(event) =>
							onChange(normalizePlayerHandle(event.target.value))
						}
						onKeyDown={(event) => {
							if (event.key === "Enter" && complete) {
								window.location.href = resultPath;
							}
						}}
						ref={inputRef}
						spellCheck={false}
						value={handle}
					/>
				</div>
				<div className="snacks-invite-actions">
					<button className="btn btn-outline-secondary" onClick={onClose}>
						Cancel
					</button>
					<a
						aria-disabled={!complete}
						className={`btn btn-success ${complete ? "" : "disabled"}`}
						href={complete ? resultPath : undefined}
					>
						Submit
					</a>
				</div>
			</div>
		</div>
	);
};

const PlayerCardModal = ({
	actions,
	onClose,
	onNext,
	onPrevious,
	player,
	playerCount,
	playerIndex,
	variant = "default",
}: {
	actions?: ReactNode;
	onClose: () => void;
	onNext?: () => void;
	onPrevious?: () => void;
	player: Player;
	playerCount?: number;
	playerIndex?: number;
	variant?: "default" | "teamReveal";
}) => {
	const canNavigate =
		onPrevious !== undefined &&
		onNext !== undefined &&
		playerCount !== undefined &&
		playerCount > 1;
	const touchStartRef = useRef<{ x: number; y: number } | undefined>(undefined);

	useEffect(() => {
		if (!canNavigate) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				onPrevious?.();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				onNext?.();
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [canNavigate, onNext, onPrevious]);

	const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		if (!canNavigate || event.touches.length !== 1) {
			return;
		}
		const touch = event.touches[0];
		touchStartRef.current = { x: touch.clientX, y: touch.clientY };
	};

	const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
		const start = touchStartRef.current;
		touchStartRef.current = undefined;
		if (
			!canNavigate ||
			start === undefined ||
			event.changedTouches.length !== 1
		) {
			return;
		}
		const touch = event.changedTouches[0];
		const deltaX = touch.clientX - start.x;
		const deltaY = touch.clientY - start.y;
		if (Math.abs(deltaX) < 44 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) {
			return;
		}
		if (deltaX < 0) {
			onNext?.();
		} else {
			onPrevious?.();
		}
	};
	const teamRevealVitals =
		variant === "teamReveal" ? playerVitalsFor(player) : undefined;
	const teamRevealSummary =
		variant === "teamReveal" ? teamRevealCardSummary(player) : undefined;
	const teamRevealTier =
		variant === "teamReveal" ? teamRevealTierFor(player) : undefined;
	const skillBarVariant = variant === "teamReveal" ? "teamReveal" : "default";

	return (
		<div
			className="snacks-modal-backdrop"
			role="presentation"
			onClick={onClose}
		>
			<div
				aria-modal="true"
				className={`snacks-player-modal ${
					variant === "teamReveal" ? "snacks-player-modal-team" : ""
				}`}
				onClick={(event) => event.stopPropagation()}
				onTouchEnd={onTouchEnd}
				onTouchStart={onTouchStart}
				role="dialog"
			>
				<div className="snacks-player-modal-head">
					<div>
						<div className="snacks-sim-kicker">
							<span>${player.price}</span> / {player.team} /{" "}
							{teamRevealTier ?? player.pos}
						</div>
						<div className="snacks-player-modal-name">{player.name}</div>
					</div>
					<button
						aria-label="Close player card"
						className="snacks-player-modal-close"
						onClick={onClose}
						type="button"
					>
						x
					</button>
				</div>
				{variant === "teamReveal" ? (
					<div className="snacks-player-modal-vitals">
						<div>
							<span>Position</span>
							<strong>{positionLabelFor(player.pos)}</strong>
						</div>
						<div>
							<span>Tier</span>
							<strong>{teamRevealTier}</strong>
						</div>
						<div>
							<span>Team</span>
							<strong>{player.team}</strong>
						</div>
					</div>
				) : null}
				{variant === "teamReveal" ? null : (
					<div className="snacks-player-modal-badges">
						<span>Badges</span>
						<div>
							{playerBadges(player).map((badge) => (
								<strong key={badge.code} title={badge.label}>
									{badge.code}
								</strong>
							))}
						</div>
					</div>
				)}
				{canNavigate && variant !== "teamReveal" ? (
					<div className="snacks-player-modal-nav">
						<button
							aria-label="Previous player"
							className="snacks-player-modal-nav-button"
							onClick={onPrevious}
							type="button"
						>
							{"<"}
						</button>
						<div className="snacks-player-modal-nav-count">
							{(playerIndex ?? 0) + 1} / {playerCount}
						</div>
						<button
							aria-label="Next player"
							className="snacks-player-modal-nav-button"
							onClick={onNext}
							type="button"
						>
							{">"}
						</button>
					</div>
				) : null}
				<div className="snacks-player-modal-stats">
					{(variant === "teamReveal"
						? [
								["PTS", player.pts],
								["REB", player.trb],
								["AST", player.ast],
								["OFF", teamRevealSummary?.offense ?? player.off],
								["DEF", teamRevealSummary?.defense ?? player.def],
								["OVR", teamRevealSummary?.overall ?? player.off],
							]
						: [
								["PTS", player.pts],
								["REB", player.trb],
								["AST", player.ast],
								["OFF", player.off],
								["DEF", player.def],
								["USG", player.usage],
							]
					).map(([label, value]) => (
						<div className="snacks-player-modal-stat" key={label}>
							<span>{label}</span>
							<strong>{value}</strong>
						</div>
					))}
				</div>
				<div
					className={
						variant === "teamReveal" ? "snacks-player-modal-skill-bars" : ""
					}
					style={
						variant === "teamReveal" ? undefined : { display: "grid", gap: 10 }
					}
				>
					{(variant === "teamReveal"
						? TEAM_REVEAL_CARD_SKILLS
						: [
								{ key: "off" as const, label: "Offense" },
								{ key: "def" as const, label: "Defense" },
								{ key: "space" as const, label: "Spacing" },
								{ key: "create" as const, label: "Creation" },
								{ key: "usage" as const, label: "Usage" },
								{ key: "reb" as const, label: "Rebounding" },
							]
					).map(({ key, label }) => (
						<StatBar
							key={key}
							label={label}
							value={player[key]}
							variant={skillBarVariant}
						/>
					))}
				</div>
				{actions ? (
					<div className="snacks-player-modal-actions">{actions}</div>
				) : null}
			</div>
		</div>
	);
};

const Gallery = () => {
	useTitleBar({ title: "Summer League Snacks", hideNewWindow: true });

	return (
		<div className="container" style={{ maxWidth: 1040 }}>
			<SnacksStyles />
			<div style={{ margin: "20px 0 22px" }}>
				<div
					className="text-uppercase text-secondary fw-bold"
					style={{ fontSize: 12, letterSpacing: 0 }}
				>
					Summer League Snacks
				</div>
				<h1 style={{ fontSize: 42, fontWeight: 900, letterSpacing: 0 }}>
					Quick NBA games built for group chats
				</h1>
			</div>

			<div
				style={{
					display: "grid",
					gap: 14,
					gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
				}}
			>
				<a
					href="/fantasy"
					style={{
						background: "#16181d",
						border: "1px solid rgba(255,255,255,0.12)",
						borderRadius: 8,
						color: "#f8f9fa",
						display: "grid",
						gap: 14,
						padding: 18,
						textDecoration: "none",
					}}
				>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							gap: 12,
						}}
					>
						<div>
							<div className="text-secondary fw-bold" style={{ fontSize: 12 }}>
								#1 by popularity
							</div>
							<h2 style={{ fontSize: 24, fontWeight: 900, margin: "4px 0 0" }}>
								Cap Lineup
							</h2>
						</div>
						<div className="badge text-bg-success align-self-start">
							Playable
						</div>
					</div>
					<p className="text-secondary" style={{ margin: 0 }}>
						Build the best 5-man roster for $30, then get a shareable projection
						your friends can argue with.
					</p>
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						<span className="badge text-bg-dark">$30 cap</span>
						<span className="badge text-bg-dark">5 players</span>
						<span className="badge text-bg-dark">2025-2026 default</span>
					</div>
				</a>
			</div>
		</div>
	);
};

const CapChallenge = ({
	gameName = "5 Player Draw",
}: {
	gameName?: string;
}) => {
	useTitleBar({ title: gameName, hideNewWindow: true });
	const challengeSeed = initialChallengeSeed();
	const [syncedChallengeSeed, setSyncedChallengeSeed] = useState(challengeSeed);
	const [leagueYear, setLeagueYear] = useState(initialLeagueYear);
	const [selectedIds, setSelectedIds] = useState<string[]>(
		selectedIdsFromSearch,
	);
	const [copiedInvite, setCopiedInvite] = useState(false);
	const [playerHandle, setPlayerHandle] = useState(cachedPlayerHandle);
	const [showInviteDialog, setShowInviteDialog] = useState(false);
	const [showHandleDialog, setShowHandleDialog] = useState(false);
	const [viewingPlayerId, setViewingPlayerId] = useState<string | undefined>();
	const visiblePlayersByTier = useMemo(
		() => playersByTierForSeed(challengeSeed),
		[challengeSeed],
	);
	const boardPlayers = useMemo(
		() => visiblePlayersByTier.flatMap((tier) => tier.players),
		[visiblePlayersByTier],
	);

	useEffect(() => {
		if (syncedChallengeSeed === challengeSeed) {
			return;
		}
		setSyncedChallengeSeed(challengeSeed);
		setLeagueYear(initialLeagueYear());
		setSelectedIds(selectedIdsFromSearch());
		setCopiedInvite(false);
		setShowInviteDialog(false);
		setShowHandleDialog(false);
		setViewingPlayerId(undefined);
	}, [challengeSeed, syncedChallengeSeed]);

	const selected = useMemo(
		() => playersForIds(selectedIds, defaultPlayerById),
		[selectedIds],
	);
	const spent = selected.reduce((sum, p) => sum + p.price, 0);
	const complete = selected.length === ROSTER_SIZE && spent <= CAP;
	const viewingPlayerIndex = boardPlayers.findIndex(
		(p) => p.id === viewingPlayerId,
	);
	const viewingPlayer =
		viewingPlayerIndex >= 0 ? boardPlayers[viewingPlayerIndex] : undefined;
	const isSimGame = fantasyBasePath() === "/fantasy-sim";
	const lockedPlayerIds = selectedIdsFromSearch();
	const viewingLockedBuild =
		complete &&
		lockedPlayerIds.length === ROSTER_SIZE &&
		selectedIds.length === lockedPlayerIds.length &&
		selectedIds.every((id, i) => id === lockedPlayerIds[i]);
	const normalizedPlayerHandle = normalizePlayerHandle(playerHandle);
	const challengeUrl = challengeUrlFor({
		leagueYear,
		playerIds: selectedIds,
		seed: challengeSeed,
	});
	const resultPath = resultPathFor({
		autoStart: isSimGame,
		leagueYear,
		playerHandle: normalizedPlayerHandle,
		playerIds: selectedIds,
		seed: challengeSeed,
	});

	useEffect(() => {
		cachePlayerHandle(normalizedPlayerHandle);
	}, [normalizedPlayerHandle]);

	const changeLeagueYear = (year: number) => {
		setLeagueYear(year);
		setCopiedInvite(false);
		setViewingPlayerId(undefined);
		window.history.replaceState(
			null,
			"",
			challengePathFor({
				leagueYear: year,
				playerIds: selectedIds,
				seed: challengeSeed,
			}),
		);
	};

	const toggle = (p: Player) => {
		if (selectedIds.includes(p.id)) {
			setSelectedIds((ids) => ids.filter((id) => id !== p.id));
			return;
		}
		if (selectedIds.length >= ROSTER_SIZE || spent + p.price > CAP) {
			return;
		}
		setSelectedIds((ids) => [...ids, p.id]);
	};

	const boardText = challengeBoardTextFor(challengeSeed);
	const challengeInviteText = `${gameName} Challenge #${challengeSeed}\nLeague year: ${leagueYearLabel(leagueYear)}\nSame board, same tiers:\n${boardText}\n\nPick your 5, submit, then send your result back.`;

	const copyChallengeInvite = async () => {
		await shareOrCopy({
			text: challengeInviteText,
			title: `${gameName} Challenge #${challengeSeed}`,
			url: challengeUrl,
		});
		setCopiedInvite(true);
	};

	const submitBuild = () => {
		if (!complete) {
			return;
		}
		if (normalizedPlayerHandle.length === 4) {
			cachePlayerHandle(normalizedPlayerHandle);
			window.location.href = resultPath;
			return;
		}
		setShowHandleDialog(true);
	};

	const viewBoardPlayerByOffset = (offset: number) => {
		if (boardPlayers.length === 0) {
			return;
		}
		const currentIndex = viewingPlayerIndex >= 0 ? viewingPlayerIndex : 0;
		const nextIndex =
			(currentIndex + offset + boardPlayers.length) % boardPlayers.length;
		setViewingPlayerId(boardPlayers[nextIndex]!.id);
	};
	const viewingPlayerPicked = viewingPlayer
		? selectedIds.includes(viewingPlayer.id)
		: false;
	const viewingPlayerUnavailable =
		viewingPlayer !== undefined &&
		!viewingPlayerPicked &&
		(selectedIds.length >= ROSTER_SIZE || spent + viewingPlayer.price > CAP);
	const confirmViewingPlayer = () => {
		if (!viewingPlayer || viewingPlayerUnavailable) {
			return;
		}
		toggle(viewingPlayer);
		setViewingPlayerId(undefined);
	};

	return (
		<div className="container snacks-page" style={{ maxWidth: 1180 }}>
			<SnacksStyles />
			<div className="snacks-header snacks-reveal-header">
				<div>
					<a className="snacks-back-link" href="/">
						All games
					</a>
					<div className="snacks-kicker">
						League year {leagueYearLabel(leagueYear)}
					</div>
					<h1 className="snacks-title">{gameName}</h1>
					<LeagueYearSelect
						leagueYear={leagueYear}
						onChange={changeLeagueYear}
					/>
				</div>
				<div className="snacks-cap-panel">
					<div className="snacks-cap-line">
						<div>
							<div className="snacks-cap-spent">${spent}</div>
							<div className="text-secondary">of ${CAP}</div>
						</div>
						<div className="snacks-cap-meta">
							{selected.length} / {ROSTER_SIZE}
							<br />
							players
						</div>
					</div>
					<div className="snacks-cap-track">
						<div
							className="snacks-cap-fill"
							style={{ width: `${clamp((spent / CAP) * 100, 0, 100)}%` }}
						/>
					</div>
				</div>
			</div>

			<div className="snacks-game-flow">
				<div className="snacks-board-scroll">
					<div className="snacks-player-grid snacks-board">
						{visiblePlayersByTier.map((tier) => (
							<div className="snacks-tier-row" key={tier.price}>
								<div className="snacks-tier-label">
									<strong>${tier.price}</strong>
									<span>{tier.price >= 8 ? "stars" : "value"}</span>
								</div>
								{tier.players.map((p) => {
									const picked = selectedIds.includes(p.id);
									const badges = playerBadges(p);
									const unavailable =
										!picked &&
										(selectedIds.length >= ROSTER_SIZE ||
											spent + p.price > CAP);
									return (
										<button
											aria-label={`View ${shortName(p)}, ${p.name}, $${p.price}`}
											aria-pressed={picked}
											className={`snacks-player-card snacks-player-card-price-${p.price} ${
												picked ? "snacks-player-card-picked" : ""
											} ${unavailable ? "snacks-player-card-unavailable" : ""}`}
											key={p.id}
											onClick={() => setViewingPlayerId(p.id)}
											title={`${p.name} | ${p.team} ${p.pos} | MPG ${p.mpg} | OFF ${p.off} | DEF ${p.def} | P/R/A ${p.pts}/${p.trb}/${p.ast}`}
											type="button"
										>
											<div className="snacks-player-card-top">
												<div className="snacks-player-name">
													<span className="snacks-player-name-default">
														{shortName(p)}
													</span>
													<span className="snacks-player-name-phone">
														{phoneName(p)}
													</span>
												</div>
												<div className="snacks-player-cost">${p.price}</div>
											</div>
											<div className="snacks-player-meta">
												{p.team} {p.pos}
											</div>
											<div
												aria-label={`${p.name} badges: ${badges
													.map((badge) => badge.label)
													.join(", ")}`}
												className="snacks-player-badges"
											>
												{badges.map((badge) => (
													<span
														className="snacks-player-badge"
														key={badge.code}
														title={badge.label}
													>
														{badge.code}
													</span>
												))}
											</div>
											<div className="snacks-player-stats">
												<span className="snacks-player-stat">
													<span>OFF</span>
													<strong>{p.off}</strong>
												</span>
												<span className="snacks-player-stat">
													<span>DEF</span>
													<strong>{p.def}</strong>
												</span>
												<span className="snacks-player-stat snacks-player-stat-pts">
													<span>PTS</span>
													<strong>{p.pts}</strong>
												</span>
											</div>
										</button>
									);
								})}
							</div>
						))}
					</div>
				</div>

				<div className="snacks-submit-row">
					<button
						className={`btn btn-success snacks-advance-button ${
							complete ? "" : "disabled"
						}`}
						disabled={!complete}
						onClick={submitBuild}
						type="button"
					>
						{viewingLockedBuild && isSimGame
							? "Start Simulation"
							: viewingLockedBuild
								? "View Results"
								: "Submit"}
					</button>
				</div>
			</div>
			{showInviteDialog ? (
				<InviteChallengeDialog
					challengeSeed={challengeSeed}
					copiedInvite={copiedInvite}
					leagueYear={leagueYear}
					onClose={() => setShowInviteDialog(false)}
					onCopyInvite={copyChallengeInvite}
				/>
			) : undefined}
			{showHandleDialog ? (
				<SubmitHandleDialog
					handle={normalizedPlayerHandle}
					onChange={setPlayerHandle}
					onClose={() => setShowHandleDialog(false)}
					resultPath={resultPath}
				/>
			) : undefined}
			{viewingPlayer ? (
				<PlayerCardModal
					actions={
						<>
							<button
								className="btn btn-outline-secondary"
								onClick={() => setViewingPlayerId(undefined)}
								type="button"
							>
								Back
							</button>
							<button
								className={`btn ${
									viewingPlayerPicked
										? "btn-outline-secondary"
										: "btn-success snacks-advance-button"
								} ${viewingPlayerUnavailable ? "disabled" : ""}`}
								disabled={viewingPlayerUnavailable}
								onClick={confirmViewingPlayer}
								type="button"
							>
								{viewingPlayerPicked
									? "Remove player"
									: viewingPlayerUnavailable
										? selectedIds.length >= ROSTER_SIZE
											? "Roster full"
											: "Over cap"
										: "Confirm"}
							</button>
						</>
					}
					onClose={() => setViewingPlayerId(undefined)}
					onNext={() => viewBoardPlayerByOffset(1)}
					onPrevious={() => viewBoardPlayerByOffset(-1)}
					player={viewingPlayer}
					playerCount={boardPlayers.length}
					playerIndex={viewingPlayerIndex}
				/>
			) : undefined}
		</div>
	);
};

const SalaryCapLedger = ({
	showItems = true,
	selected,
}: {
	selected: Player[];
	showItems?: boolean;
}) => {
	const spent = selected.reduce((sum, player) => sum + player.price, 0);
	const remaining = CAP - spent;

	return (
		<div className="snacks-reveal-cap-ledger" aria-label="Salary cap ledger">
			<div className="snacks-reveal-cap-ledger-head">
				<div>
					<span>Salary Cap</span>
					<strong>
						${spent} <span>/ ${CAP}</span>
					</strong>
				</div>
				<small>
					{remaining >= 0
						? `$${remaining} left`
						: `$${Math.abs(remaining)} over`}
				</small>
			</div>
			<div className="snacks-cap-track">
				<div
					className="snacks-cap-fill"
					style={{ width: `${clamp((spent / CAP) * 100, 0, 100)}%` }}
				/>
			</div>
			{showItems ? (
				<div className="snacks-reveal-cap-ledger-items">
					{Array.from({ length: ROSTER_SIZE }, (_, i) => {
						const player = selected[i];
						return (
							<div
								className={`snacks-reveal-cap-ledger-item ${
									player ? "snacks-reveal-cap-ledger-item-filled" : ""
								}`}
								key={i}
							>
								<strong>{player ? shortName(player) : `Pick ${i + 1}`}</strong>
								<span>{player ? `$${player.price}` : "$0"}</span>
							</div>
						);
					})}
				</div>
			) : null}
		</div>
	);
};

const TeamRevealModePicker = ({
	leagueYear,
	onStart,
}: {
	leagueYear: number;
	onStart: (mode: TeamRevealMode) => Promise<void> | void;
}) => {
	const [selectedMode, setSelectedMode] = useState<TeamRevealMode>("solo");

	return (
		<div className="snacks-team-mode-screen">
			<div className="snacks-team-mode-header">
				<a className="snacks-back-link" href="/">
					All games
				</a>
				<div className="snacks-kicker">Team Reveal</div>
				<h1 className="snacks-title">Choose your mode</h1>
				<p>How do you want to play this season?</p>
			</div>

			<div className="snacks-team-mode-options">
				{TEAM_REVEAL_MODES.map((mode) => {
					const selected = mode.id === selectedMode;
					return (
						<button
							aria-pressed={selected}
							className={`snacks-team-mode-card ${
								selected ? "snacks-team-mode-card-selected" : ""
							}`}
							key={mode.id}
							onClick={() => setSelectedMode(mode.id)}
							type="button"
						>
							<span className="snacks-team-mode-icon">{mode.icon}</span>
							<span className="snacks-team-mode-copy">
								<strong>{mode.title}</strong>
								<small>{mode.meta}</small>
								<span>{mode.description}</span>
							</span>
							<span className="snacks-team-mode-radio" />
						</button>
					);
				})}
			</div>

			<button
				className="btn btn-success snacks-advance-button snacks-team-mode-start"
				onClick={() => onStart(selectedMode)}
				type="button"
			>
				Start build
			</button>

			<div className="snacks-team-mode-note">
				League year {leagueYearLabel(leagueYear)}
			</div>
		</div>
	);
};

const TeamRevealCopiedPopup = ({
	mode,
	onClose,
}: {
	mode: TeamRevealMode;
	onClose: () => void;
}) => (
	<div
		className="snacks-modal-backdrop snacks-top-drawer-backdrop"
		onClick={onClose}
		role="presentation"
	>
		<div
			aria-modal="true"
			className="snacks-player-modal snacks-top-drawer snacks-link-copied-modal"
			onClick={(event) => event.stopPropagation()}
			role="dialog"
		>
			<div className="snacks-player-modal-head">
				<div>
					<div className="snacks-sim-kicker">
						{mode === "h2h" ? "Head-to-Head" : "Multiplayer"}
					</div>
					<div className="snacks-player-modal-name">
						Link has been copied to your clipboard.
					</div>
				</div>
				<button
					aria-label="Close copied link dialog"
					className="snacks-player-modal-close"
					onClick={onClose}
					type="button"
				>
					x
				</button>
			</div>
			<p className="snacks-sim-subtitle" style={{ margin: 0 }}>
				Send it before you build so everyone starts from this same Team Reveal
				board.
			</p>
			<button className="btn btn-success" onClick={onClose} type="button">
				Continue
			</button>
		</div>
	</div>
);

type TeamRevealBoardSortKey =
	| "ast"
	| "blk"
	| "defense"
	| "dunk"
	| "offense"
	| "pass"
	| "perd"
	| "ppg"
	| "reb"
	| "tpt"
	| "two"
	| "value";

const TEAM_REVEAL_BOARD_COLUMNS: {
	key: Exclude<TeamRevealBoardSortKey, "value">;
	label: string;
	value: (player: Player) => number;
}[] = [
	{ key: "offense", label: "OFF", value: teamRevealOffenseComposite },
	{ key: "defense", label: "DEF", value: teamRevealDefenseComposite },
	{ key: "ppg", label: "PPG", value: (player) => player.pts },
	{ key: "reb", label: "REB", value: (player) => player.trb },
	{ key: "ast", label: "AST", value: (player) => player.ast },
	{ key: "pass", label: "Pass", value: (player) => player.create },
	{ key: "two", label: "2PT", value: (player) => player.off },
	{ key: "dunk", label: "Dunk", value: (player) => player.usage },
	{ key: "tpt", label: "3PT", value: (player) => player.space },
	{ key: "perd", label: "Per D", value: (player) => player.def },
	{ key: "blk", label: "Blk", value: (player) => player.reb },
];

const teamRevealSortValue = (player: Player, key: TeamRevealBoardSortKey) => {
	if (key === "value") {
		return teamRevealValueScore(player);
	}
	return (
		TEAM_REVEAL_BOARD_COLUMNS.find((column) => column.key === key)?.value(
			player,
		) ?? 0
	);
};

const teamRevealCellText = (key: TeamRevealBoardSortKey, value: number) =>
	key === "offense" || key === "defense" || Number.isInteger(value)
		? String(Math.round(value))
		: value.toFixed(1);

const TeamRevealAvailableBoard = ({
	onOpenPlayer,
	players,
	selectedIds,
	sortKey,
	team,
	onSort,
}: {
	onOpenPlayer: (player: Player) => void;
	onSort: (key: TeamRevealBoardSortKey) => void;
	players: Player[];
	selectedIds: string[];
	sortKey: TeamRevealBoardSortKey;
	team: string;
}) => {
	const availablePlayers = players
		.filter((player) => !selectedIds.includes(player.id))
		.sort(
			(a, b) =>
				teamRevealSortValue(b, sortKey) - teamRevealSortValue(a, sortKey) ||
				teamRevealValueScore(b) - teamRevealValueScore(a) ||
				a.name.localeCompare(b.name),
		);
	const valueHeaderActive = sortKey === "value";

	return (
		<div className="snacks-team-board">
			<div className="snacks-team-board-prompt">
				<span>Tap a player to open their card.</span>
				<strong>Swipe -&gt;</strong>
			</div>
			<div className="snacks-team-board-shell">
				<div className="snacks-team-board-scroll">
					<div className="snacks-team-board-grid">
						<div className="snacks-team-board-row snacks-team-board-head">
							<div className="snacks-team-board-player-head">Player</div>
							{TEAM_REVEAL_BOARD_COLUMNS.map((column) => {
								const active = sortKey === column.key;
								const sortColumn = () => onSort(column.key);
								return (
									<button
										className={`snacks-team-board-head-cell ${
											active ? "snacks-team-board-head-active" : ""
										}`}
										key={column.key}
										onClick={sortColumn}
										onPointerDown={sortColumn}
										type="button"
									>
										{column.label}
										{active ? " ▾" : ""}
									</button>
								);
							})}
							<button
								className={`snacks-team-board-value-head ${
									valueHeaderActive ? "snacks-team-board-head-active" : ""
								}`}
								onClick={() => onSort("value")}
								onPointerDown={() => onSort("value")}
								type="button"
							>
								Value{valueHeaderActive ? " ▾" : ""}
							</button>
						</div>
						{availablePlayers.map((player) => {
							const tier = teamRevealTierFor(player);
							const tierColor = TEAM_REVEAL_TIER_COLORS[tier];
							const valueScore = teamRevealValueScore(player);
							const valueLabel = teamRevealValueLabel(valueScore);
							const valueColor = teamRevealValueColor(valueScore);
							return (
								<button
									aria-label={`View ${player.name}, ${team} ${tier}, $${player.price}`}
									className="snacks-team-board-row snacks-team-board-body-row"
									key={player.id}
									onClick={() => onOpenPlayer(player)}
									style={{ "--tier-color": tierColor } as CSSProperties}
									title={`${player.name} | ${team} ${tier} | ${teamRevealRatingTitle(player)}`}
									type="button"
								>
									<div className="snacks-team-board-player-cell">
										<strong>{lastName(player)}</strong>
										<span>
											{firstName(player)}
											<i> · </i>
											<b style={{ color: tierColor }}>{tier}</b>
										</span>
									</div>
									{TEAM_REVEAL_BOARD_COLUMNS.map((column) => {
										const value = column.value(player);
										const active = sortKey === column.key;
										const ratingCell = [
											"offense",
											"defense",
											"pass",
											"two",
											"dunk",
											"tpt",
											"perd",
											"blk",
										].includes(column.key);
										return (
											<div
												className={`snacks-team-board-cell ${
													active ? "snacks-team-board-cell-active" : ""
												} ${
													ratingCell && value >= 90
														? "snacks-team-board-cell-elite"
														: ratingCell && value >= 75
															? "snacks-team-board-cell-good"
															: ""
												}`}
												key={column.key}
											>
												{teamRevealCellText(column.key, value)}
											</div>
										);
									})}
									<div className="snacks-team-board-value-cell">
										<strong>${player.price}</strong>
										<span style={{ color: valueColor }}>{valueLabel}</span>
									</div>
								</button>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

const RevealChallenge = ({
	enforceCap = false,
	gameName = "5 Player - Reveal",
	groupsForSeed = tierRevealGroupsForSeed,
	hideGroupUntilRevealed = false,
	prompt = "Pick your starting 5",
	showStartOver = true,
	teamRevealDesign = false,
}: {
	enforceCap?: boolean;
	gameName?: string;
	groupsForSeed?: (seed: number) => RevealGroup[];
	hideGroupUntilRevealed?: boolean;
	prompt?: string;
	showStartOver?: boolean;
	teamRevealDesign?: boolean;
}) => {
	useTitleBar({ title: gameName, hideNewWindow: true });
	const challengeSeed = initialChallengeSeed();
	const [syncedChallengeSeed, setSyncedChallengeSeed] = useState(challengeSeed);
	const [leagueYear, setLeagueYear] = useState(initialLeagueYear);
	const [teamRevealMode, setTeamRevealMode] = useState<
		TeamRevealMode | undefined
	>(() => teamRevealModeFromSearch());
	const teamRevealRole = teamRevealRoleFromSearch();
	const teamRevealHostHandle = teamRevealDesign
		? teamRevealHostHandleFromSearch()
		: "";
	const [selectedIds, setSelectedIds] = useState<string[]>(
		selectedIdsFromSearch,
	);
	const [playerHandle, setPlayerHandle] = useState(cachedPlayerHandle);
	const [copiedTeamRevealMode, setCopiedTeamRevealMode] = useState<
		TeamRevealMode | undefined
	>();
	const [showHandleDialog, setShowHandleDialog] = useState(false);
	const [revealedTierIndex, setRevealedTierIndex] = useState<
		number | undefined
	>();
	const [viewingPlayerId, setViewingPlayerId] = useState<string | undefined>();
	const [viewingListKey, setViewingListKey] = useState<"active" | "selected">(
		"active",
	);
	const [teamRevealSortKey, setTeamRevealSortKey] =
		useState<TeamRevealBoardSortKey>("value");
	const revealGroups = useMemo(
		() => groupsForSeed(challengeSeed),
		[challengeSeed, groupsForSeed],
	);

	useEffect(() => {
		if (syncedChallengeSeed === challengeSeed) {
			return;
		}
		setSyncedChallengeSeed(challengeSeed);
		setLeagueYear(initialLeagueYear());
		setTeamRevealMode(teamRevealModeFromSearch());
		setCopiedTeamRevealMode(undefined);
		setSelectedIds(selectedIdsFromSearch());
		setViewingPlayerId(undefined);
		setViewingListKey("active");
		setRevealedTierIndex(undefined);
		setShowHandleDialog(false);
	}, [challengeSeed, syncedChallengeSeed]);

	const selected = useMemo(
		() =>
			playersForIds(
				selectedIds,
				teamRevealDesign ? teamRevealPlayerById : defaultPlayerById,
			),
		[selectedIds],
	);
	const spent = selected.reduce((sum, player) => sum + player.price, 0);
	const overCap = spent > CAP;
	const complete = selected.length === ROSTER_SIZE && (!enforceCap || !overCap);
	const activeGroupIndex = selected.length;
	const activeGroup = revealGroups[activeGroupIndex];
	const groupRevealed = !complete && revealedTierIndex === activeGroupIndex;
	const activeGroupPlayers = activeGroup?.players ?? [];
	const modalPlayers =
		viewingListKey === "selected"
			? selected
			: activeGroupPlayers.filter((player) => !selectedIds.includes(player.id));
	const minimumFutureCost = revealGroups
		.slice(activeGroupIndex + 1)
		.reduce(
			(sum, group) =>
				sum + Math.min(...group.players.map((player) => player.price), CAP),
			0,
		);
	const viewingPlayerIndex = modalPlayers.findIndex(
		(p) => p.id === viewingPlayerId,
	);
	const viewingPlayer =
		viewingPlayerIndex >= 0 ? modalPlayers[viewingPlayerIndex] : undefined;
	const wouldBreakCapPath = (player: Player) =>
		enforceCap && spent + player.price + minimumFutureCost > CAP;
	const viewingPlayerUnavailable =
		viewingListKey === "active" &&
		viewingPlayer !== undefined &&
		wouldBreakCapPath(viewingPlayer);
	const normalizedPlayerHandle = normalizePlayerHandle(playerHandle);
	const teamRevealHostHandleForLinks = teamRevealDesign
		? teamRevealHostHandle ||
			(teamRevealRole === "opponent" ? "" : normalizedPlayerHandle)
		: undefined;
	const resultPath = resultPathFor({
		autoStart: true,
		hostHandle: teamRevealHostHandleForLinks,
		leagueYear,
		mode: teamRevealDesign ? teamRevealMode : undefined,
		playerHandle: normalizedPlayerHandle,
		playerIds: selectedIds,
		role: teamRevealDesign ? teamRevealRole : undefined,
		seed: challengeSeed,
	});

	useEffect(() => {
		cachePlayerHandle(normalizedPlayerHandle);
	}, [normalizedPlayerHandle]);

	const changeLeagueYear = (year: number) => {
		setLeagueYear(year);
		setSelectedIds([]);
		setViewingPlayerId(undefined);
		setViewingListKey("active");
		setRevealedTierIndex(undefined);
		window.history.replaceState(
			null,
			"",
			challengePathFor({
				hostHandle: teamRevealHostHandleForLinks,
				leagueYear: year,
				mode: teamRevealDesign ? teamRevealMode : undefined,
				role: teamRevealDesign ? teamRevealRole : undefined,
				seed: challengeSeed,
			}),
		);
	};

	const startTeamRevealMode = async (mode: TeamRevealMode) => {
		setTeamRevealMode(mode);
		if (mode !== "solo") {
			const inviteUrl = challengeUrlFor({
				hostHandle: normalizedPlayerHandle,
				leagueYear,
				mode,
				role: mode === "h2h" ? "opponent" : undefined,
				seed: challengeSeed,
			});
			void navigator.clipboard?.writeText(inviteUrl).catch(() => {
				// Clipboard can be blocked in some webviews; still show the reminder.
			});
			setCopiedTeamRevealMode(mode);
		}
		window.history.replaceState(
			null,
			"",
			challengePathFor({
				hostHandle: mode === "solo" ? undefined : normalizedPlayerHandle,
				leagueYear,
				mode,
				seed: challengeSeed,
			}),
		);
	};

	const revealTier = () => {
		if (complete) {
			return;
		}
		setViewingPlayerId(undefined);
		setViewingListKey("active");
		setRevealedTierIndex(activeGroupIndex);
	};

	const confirmPick = (player: Player) => {
		if (wouldBreakCapPath(player)) {
			return;
		}
		const nextSelectedIds = [...selectedIds, player.id];
		setSelectedIds(nextSelectedIds);
		setViewingPlayerId(undefined);
		setViewingListKey("active");
		setRevealedTierIndex(undefined);
		window.history.replaceState(
			null,
			"",
			challengePathFor({
				hostHandle: teamRevealHostHandleForLinks,
				leagueYear,
				mode: teamRevealDesign ? teamRevealMode : undefined,
				playerIds: nextSelectedIds,
				role: teamRevealDesign ? teamRevealRole : undefined,
				seed: challengeSeed,
			}),
		);
	};

	const submitBuild = () => {
		if (!complete) {
			return;
		}
		if (normalizedPlayerHandle.length === 4) {
			cachePlayerHandle(normalizedPlayerHandle);
			window.location.href = resultPath;
			return;
		}
		setShowHandleDialog(true);
	};

	const startOver = () => {
		setSelectedIds([]);
		setViewingPlayerId(undefined);
		setViewingListKey("active");
		setRevealedTierIndex(undefined);
		window.history.replaceState(
			null,
			"",
			challengePathFor({
				hostHandle: teamRevealHostHandleForLinks,
				leagueYear,
				mode: teamRevealDesign ? teamRevealMode : undefined,
				role: teamRevealDesign ? teamRevealRole : undefined,
				seed: challengeSeed,
			}),
		);
	};
	const viewPlayerByOffset = (offset: number) => {
		if (modalPlayers.length === 0) {
			return;
		}
		const currentIndex = viewingPlayerIndex >= 0 ? viewingPlayerIndex : 0;
		const nextIndex =
			(currentIndex + offset + modalPlayers.length) % modalPlayers.length;
		setViewingPlayerId(modalPlayers[nextIndex]!.id);
	};

	const actionLabel = complete
		? "Submit"
		: groupRevealed
			? "Choose a player"
			: hideGroupUntilRevealed
				? "Reveal team"
				: (activeGroup?.revealLabel ?? "Reveal");

	const actionDisabled =
		groupRevealed || (selected.length >= ROSTER_SIZE && !complete);
	const action = complete ? submitBuild : revealTier;
	const viewingSelectedPlayer = viewingListKey === "selected";
	const activeGroupLabel =
		hideGroupUntilRevealed && !groupRevealed
			? "Next team"
			: (activeGroup?.label ?? "Complete");
	const showRevealTierPill =
		!teamRevealDesign || groupRevealed || complete || !hideGroupUntilRevealed;
	const slotLabelFor = (group: RevealGroup, index: number) => {
		if (teamRevealDesign && index === activeGroupIndex && !complete) {
			return group.slotLabel;
		}
		if (!hideGroupUntilRevealed) {
			return group.slotLabel;
		}
		if (groupRevealed && index === activeGroupIndex) {
			return group.slotLabel;
		}
		return `${index + 1}`;
	};

	if (teamRevealDesign && selectedIds.length === 0 && !teamRevealMode) {
		return (
			<div className="container snacks-page snacks-team-reveal-page">
				<SnacksStyles />
				<TeamRevealModePicker
					leagueYear={leagueYear}
					onStart={startTeamRevealMode}
				/>
			</div>
		);
	}

	return (
		<div
			className={`container snacks-page ${
				teamRevealDesign ? "snacks-team-reveal-page" : ""
			}`}
			style={{ maxWidth: teamRevealDesign ? undefined : 980 }}
		>
			<SnacksStyles />
			<div className="snacks-header">
				<div>
					<a className="snacks-back-link" href="/">
						All games
					</a>
					<div className="snacks-kicker">
						League year {leagueYearLabel(leagueYear)}
					</div>
					<h1 className="snacks-title">{gameName}</h1>
					{teamRevealDesign ? null : (
						<LeagueYearSelect
							leagueYear={leagueYear}
							onChange={changeLeagueYear}
						/>
					)}
				</div>
			</div>

			<div className="snacks-game-flow">
				<section
					className={`snacks-reveal-stage ${
						teamRevealDesign ? "snacks-team-reveal-shell" : ""
					}`}
				>
					<div
						className={`snacks-reveal-card-shell ${
							teamRevealDesign ? "snacks-team-reveal-card" : ""
						}`}
					>
						<div className="snacks-reveal-head">
							<div>
								<span>{prompt}</span>
								<strong>
									{complete
										? "Lineup locked"
										: groupRevealed
											? "Choose one"
											: "Ready to reveal"}
								</strong>
							</div>
							{showRevealTierPill ? (
								<div className="snacks-reveal-tier-pill">
									{complete ? "Roster locked" : activeGroupLabel}
								</div>
							) : null}
						</div>

						<div className="snacks-reveal-slots" aria-label="Selected players">
							{revealGroups.map((group, i) => {
								const player = selected[i];
								const activeEmptySlot =
									!player && teamRevealDesign && i === activeGroupIndex && !complete;
								const slotLabel = slotLabelFor(group, i);
								const slotContent = (
									<>
										<div className="snacks-reveal-slot-mark">
											{player ? playerInitials(player) : slotLabel}
										</div>
										<div className="snacks-reveal-slot-label">
											{player
												? teamRevealDesign
													? player.pos
													: player.pos
												: activeEmptySlot
													? ""
												: teamRevealDesign
													? groupRevealed &&
														i === activeGroupIndex &&
														activeGroup
														? activeGroup.slotLabel
														: "PICK"
													: slotLabel}
										</div>
										{teamRevealDesign ? (
											<div className="snacks-reveal-slot-price">
												{activeEmptySlot ? "" : `$${player ? player.price : 0}`}
											</div>
										) : null}
									</>
								);

								return (
									<button
										className={`snacks-reveal-slot ${
											player ? "snacks-reveal-slot-filled" : ""
										} ${
											activeEmptySlot ? "snacks-reveal-slot-active" : ""
										}`}
										disabled={!player || !teamRevealDesign}
										key={group.id}
										onClick={() => {
											if (!player || !teamRevealDesign) {
												return;
											}
											setViewingListKey("selected");
											setViewingPlayerId(player.id);
										}}
										type="button"
									>
										{slotContent}
									</button>
								);
							})}
						</div>

						{enforceCap ? (
							<SalaryCapLedger
								selected={selected}
								showItems={!teamRevealDesign}
							/>
						) : null}
					</div>

					<div className="snacks-reveal-board">
						{groupRevealed && activeGroup ? (
							<TeamRevealAvailableBoard
								onOpenPlayer={(player) => {
									setViewingListKey("active");
									setViewingPlayerId(player.id);
								}}
								onSort={setTeamRevealSortKey}
								players={activeGroup.players}
								selectedIds={selectedIds}
								sortKey={teamRevealSortKey}
								team={activeGroup.slotLabel}
							/>
						) : (
							<div className="snacks-reveal-empty">
								{complete
									? "Your five are set. Submit to start the season simulation."
									: "Tap reveal to flip over the next group of players."}
							</div>
						)}
					</div>

					<div className="snacks-reveal-actions">
						<button
							className={`btn btn-success snacks-advance-button ${
								actionDisabled ? "disabled" : ""
							}`}
							disabled={actionDisabled}
							onClick={action}
							type="button"
						>
							{actionLabel}
						</button>
					</div>
					{showStartOver && selected.length > 0 ? (
						<div className="snacks-reveal-actions">
							<button
								className="btn btn-outline-secondary btn-sm"
								onClick={startOver}
								type="button"
							>
								Start over
							</button>
						</div>
					) : null}
				</section>
			</div>
			{showHandleDialog ? (
				<SubmitHandleDialog
					handle={normalizedPlayerHandle}
					onChange={setPlayerHandle}
					onClose={() => setShowHandleDialog(false)}
					resultPath={resultPath}
				/>
			) : undefined}
			{copiedTeamRevealMode ? (
				<TeamRevealCopiedPopup
					mode={copiedTeamRevealMode}
					onClose={() => setCopiedTeamRevealMode(undefined)}
				/>
			) : undefined}
			{viewingPlayer ? (
				<PlayerCardModal
					actions={
						<>
							<button
								className="btn btn-outline-secondary"
								onClick={() => setViewingPlayerId(undefined)}
								type="button"
							>
								Back
							</button>
							<button
								className={`btn btn-success snacks-advance-button ${
									viewingPlayerUnavailable || viewingSelectedPlayer
										? "disabled"
										: ""
								}`}
								disabled={viewingPlayerUnavailable || viewingSelectedPlayer}
								onClick={() => confirmPick(viewingPlayer)}
								type="button"
							>
								{viewingSelectedPlayer
									? "On your team"
									: viewingPlayerUnavailable
										? "Over cap"
										: teamRevealDesign
											? `Add to team · ${viewingPlayer.price === 0 ? "Free" : `$${viewingPlayer.price}`}`
											: "Confirm"}
							</button>
						</>
					}
					onClose={() => setViewingPlayerId(undefined)}
					onNext={() => viewPlayerByOffset(1)}
					onPrevious={() => viewPlayerByOffset(-1)}
					player={viewingPlayer}
					playerCount={modalPlayers.length}
					playerIndex={viewingPlayerIndex}
					variant={teamRevealDesign ? "teamReveal" : "default"}
				/>
			) : undefined}
		</div>
	);
};

const CapChallengeResults = ({
	enforceCap = true,
	gameName = "5 Player Draw",
	showSeasonDashboard = false,
}: {
	enforceCap?: boolean;
	gameName?: string;
	showSeasonDashboard?: boolean;
}) => {
	useTitleBar({ title: `${gameName} Results`, hideNewWindow: true });

	useEffect(() => {
		if (!showSeasonDashboard) {
			return;
		}

		const previousPaddingTop = document.body.style.paddingTop;
		document.body.style.paddingTop = "0px";

		return () => {
			document.body.style.paddingTop = previousPaddingTop;
		};
	}, [showSeasonDashboard]);

	const challengeSeed = initialChallengeSeed();
	const leagueYear = initialLeagueYear();
	const playerIds = resultPlayerIdsFromSearch();
	const playerHandle = resultPlayerHandleFromSearch();
	const playerKey = playerIds.join(",");
	const teamRevealMode = window.location.pathname.startsWith("/team-reveal")
		? teamRevealModeFromSearch()
		: undefined;
	const teamRevealRole = window.location.pathname.startsWith("/team-reveal")
		? teamRevealRoleFromSearch()
		: undefined;
	const teamRevealHostHandle = window.location.pathname.startsWith("/team-reveal")
		? teamRevealHostHandleFromSearch()
		: "";
	const h2hSimReady =
		teamRevealMode !== "h2h" ||
		teamRevealRole === "opponent" ||
		teamRevealReadyFromSearch();
	const autoStart = showSeasonDashboard && shouldAutoStartSim() && h2hSimReady;
	const selected = useMemo(
		() =>
			playersForIds(
				playerIds,
				window.location.pathname.startsWith("/team-reveal")
					? teamRevealPlayerById
					: defaultPlayerById,
			),
		[playerKey],
	);
	const buildIsValid =
		selected.length === ROSTER_SIZE &&
		(!enforceCap || selected.reduce((sum, p) => sum + p.price, 0) <= CAP);
	const opponentTeams = useMemo(
		() =>
			window.location.pathname.startsWith("/team-reveal") && buildIsValid
				? selected.map((player) => player.team)
				: undefined,
		[buildIsValid, selected],
	);
	const sim = useMemo(
		() =>
			buildIsValid
				? simulateField(selected, challengeSeed, {
						opponentTeams,
						reservedOpponent: teamRevealMode === "h2h",
					})
				: undefined,
		[buildIsValid, selected, challengeSeed, opponentTeams, teamRevealMode],
	);
	const activeTeam = sim?.userTeam;
	const [copied, setCopied] = useState(false);
	const [copiedChallenge, setCopiedChallenge] = useState(false);
	const hideResultsIntro = gameName === "Team Reveal";

	const challengeHref = challengePathFor({
		hostHandle: teamRevealHostHandle,
		leagueYear,
		mode: teamRevealMode,
		playerIds,
		role: teamRevealRole,
		seed: challengeSeed,
	});
	const challengeShareUrl = challengeUrlFor({
		hostHandle: teamRevealHostHandle || playerHandle,
		leagueYear,
		mode: teamRevealMode,
		role: teamRevealMode === "h2h" ? "opponent" : teamRevealRole,
		seed: challengeSeed,
	});
	const activeResultUrl = resultUrlFor({
		hostHandle: teamRevealHostHandle,
		leagueYear,
		mode: teamRevealMode,
		playerHandle,
		playerIds,
		role: teamRevealRole,
		seed: challengeSeed,
	});
	const newChallengeHref = challengePathFor({
		hostHandle: teamRevealHostHandle,
		leagueYear,
		mode: teamRevealMode,
		role: teamRevealRole,
		seed: challengeSeed + 1,
	});
	const shareText =
		activeTeam && sim
			? `${playerHandleLabel(playerHandle)} submitted ${gameName} Challenge #${challengeSeed}: projected ${activeTeam.wins}-${activeTeam.losses}, ${sim.playoffRound.toLowerCase()}. Roster: ${activeTeam.players.map((p) => p.name).join(", ")}`
			: "";
	const challengeShareText = `${gameName} Challenge #${challengeSeed}\nLeague year: ${leagueYearLabel(leagueYear)}\nSame board, same tiers:\n${challengeBoardTextFor(challengeSeed)}\n\nPick your 5, submit, then send your result back.`;

	useEffect(() => {
		cachePlayerHandle(playerHandle);
	}, [playerHandle]);

	if (!sim || !activeTeam) {
		return (
			<div className="container snacks-page" style={{ maxWidth: 920 }}>
				<SnacksStyles />
				<div className="snacks-header">
					<div>
						<a className="snacks-back-link" href="/">
							All games
						</a>
						<div className="snacks-kicker">{gameName}</div>
						<h1 className="snacks-title">Results Not Found</h1>
					</div>
				</div>
				<section className="snacks-sim-card">
					<h2 className="snacks-sim-title">No locked build found</h2>
					<p className="snacks-sim-subtitle">
						This results page needs five valid players
						{enforceCap ? ` under the $${CAP} cap` : ""}.
					</p>
					<a
						className="btn btn-success"
						href={challengePathFor({
							leagueYear,
							seed: challengeSeed,
						})}
					>
						Build a team
					</a>
				</section>
			</div>
		);
	}

	return (
		<div
			className={`container snacks-page snacks-results-page ${
				showSeasonDashboard ? "snacks-results-page-sim" : ""
			}`}
			style={{ maxWidth: 1180 }}
		>
			<SnacksStyles />
			{hideResultsIntro ? null : (
				<>
					<div className="snacks-header">
						<div>
							<a
								className="snacks-back-link"
								href={challengePathFor({
									leagueYear,
									mode: teamRevealMode,
									playerIds,
									role: teamRevealRole,
									seed: challengeSeed,
								})}
							>
								Back to board
							</a>
							<div className="snacks-kicker">
								Challenge #{challengeSeed} / {leagueYearLabel(leagueYear)}
							</div>
							<h1 className="snacks-title">{gameName} Results</h1>
						</div>
					</div>

					<div className="snacks-results-share">
						<div className="snacks-results-share-actions">
							<button
								className="btn btn-outline-success snacks-share-link-button"
								onClick={async () => {
									await shareOrCopy({
										text: challengeShareText,
										title: `${gameName} Challenge #${challengeSeed}`,
										url: challengeShareUrl,
									});
									setCopiedChallenge(true);
								}}
								type="button"
							>
								{copiedChallenge ? "Shared" : "Share Link"}
							</button>
							<a className="btn btn-outline-secondary" href={challengeHref}>
								Open board
							</a>
						</div>
					</div>
				</>
			)}

			<div className="snacks-game-flow">
				<SimResults
					autoStart={autoStart}
					copied={copied}
					hostHandle={teamRevealHostHandle}
					newChallengeHref={newChallengeHref}
					onCopy={async () => {
						await shareOrCopy({
							text: shareText,
							title: `${gameName} Result #${challengeSeed}`,
							url: activeResultUrl,
						});
						setCopied(true);
					}}
					playerHandle={playerHandle}
					seasonBlockedReason={
						showSeasonDashboard && teamRevealMode === "h2h" && !h2hSimReady
							? "h2h-opponent"
							: undefined
					}
					showSeasonDashboard={showSeasonDashboard}
					sim={sim}
				/>
			</div>
		</div>
	);
};

const Snacks = () => {
	if (window.location.pathname === "/team-reveal/results") {
		return <CapChallengeResults gameName="Team Reveal" showSeasonDashboard />;
	}

	if (window.location.pathname === "/fantasy-reveal/results") {
		return (
			<CapChallengeResults gameName="5 Player - Reveal" showSeasonDashboard />
		);
	}

	if (window.location.pathname === "/fantasy-sim/results") {
		return (
			<CapChallengeResults gameName="5 Player Draw - Sim" showSeasonDashboard />
		);
	}

	if (
		window.location.pathname === "/fantasy/results" ||
		window.location.pathname === "/snacks/cap-challenge/results"
	) {
		return <CapChallengeResults />;
	}

	if (window.location.pathname === "/fantasy-sim") {
		return <CapChallenge gameName="5 Player Draw - Sim" />;
	}

	if (window.location.pathname === "/fantasy-reveal") {
		return <RevealChallenge />;
	}

	if (window.location.pathname === "/team-reveal") {
		return (
			<RevealChallenge
				enforceCap
				gameName="Team Reveal"
				groupsForSeed={teamRevealGroupsForSeed}
				hideGroupUntilRevealed
				prompt="Pick one player from each team"
				showStartOver={false}
				teamRevealDesign
			/>
		);
	}

	if (
		window.location.pathname === "/fantasy" ||
		window.location.pathname === "/snacks/cap-challenge"
	) {
		return <CapChallenge />;
	}

	return <Gallery />;
};

export default Snacks;
